import { Link, createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { MedicalDisclaimer } from "@/components/MedicalDisclaimer";
import portraitScrubsAsset from "@/assets/dr-kukrer-scrubs.webp.asset.json";

const AUTHOR_IMAGE = `https://www.sadikkukrer.com${portraitScrubsAsset.url}`;

interface FAQ {
  q: string;
  a: string;
}

interface Post {
  slug: string;
  title: string;
  description: string;
  metaTitle?: string;
  metaDescription?: string;
  date: string;
  updated?: string;
  readingTime: string;
  keyword: string;
  trSlug?: string;
  tldr?: string[];
  faqs?: FAQ[];
}

const SITE_UPDATED = "2026-09-01";

const POSTS: Record<string, Post> = {
  "early-pregnancy-symptoms": {
    slug: "early-pregnancy-symptoms",
    title: "Early Pregnancy Symptoms: 15 Signs from the First Week",
    description:
      "Missed period, nausea, breast tenderness, fatigue and more. When early pregnancy symptoms typically appear and when to take a pregnancy test.",
    date: "2026-07-07",
    updated: SITE_UPDATED,
    readingTime: "8 min read",
    keyword: "early pregnancy symptoms",
    trSlug: "hamilelik-belirtileri",
    tldr: [
      "A missed period is the most reliable early sign; a delay of more than 5–7 days warrants a pregnancy test.",
      "Most symptoms begin 6–12 days after conception, around the time of implantation.",
      "Home urine tests are approximately 99% accurate one day after the expected period.",
    ],
    faqs: [
      {
        q: "When do early pregnancy symptoms start?",
        a: "Most symptoms appear 6–12 days after conception, as the embryo implants into the uterine wall. Some women feel changes very early, while others notice nothing until the missed period. Both patterns are normal.",
      },
      {
        q: "Which symptom is the most reliable?",
        a: "In women with regular cycles, a missed period is the most reliable indicator. A delay of 5–7 days beyond the expected date is enough reason to perform a home urine pregnancy test.",
      },
      {
        q: "The test is negative but my symptoms continue. What should I do?",
        a: "Tests performed too early can produce false negatives. Repeat the test after 3–4 days with your first morning urine. If the result is still unclear, a quantitative beta-hCG blood test provides a definitive answer.",
      },
    ],
  },
  "how-to-calculate-pregnancy-week": {
    slug: "how-to-calculate-pregnancy-week",
    title: "How to Calculate Pregnancy Weeks: A Step-by-Step Guide from LMP to Due Date",
    metaTitle: "How to Calculate Pregnancy Weeks: LMP & Due Date Guide",
    description:
      "How to calculate pregnancy weeks and the estimated due date (EDD) from the last menstrual period. Naegele's rule, ultrasound dating, IVF differences, and the trimesters.",
    date: "2026-07-14",
    updated: SITE_UPDATED,
    readingTime: "9 min read",
    keyword: "pregnancy week calculation",
    trSlug: "gebelik-haftasi-hesaplama",
    tldr: [
      "Pregnancy weeks are counted from the first day of the last menstrual period (LMP), not from conception.",
      "The estimated due date is calculated with Naegele's rule: LMP + 7 days − 3 months + 1 year.",
      "A first-trimester ultrasound (crown–rump length) redates the pregnancy when it differs from the LMP by more than 5 days.",
    ],
    faqs: [
      {
        q: "How is pregnancy age calculated?",
        a: "It is counted from the first day of the last menstrual period (LMP). For example, if 8 complete weeks have passed since the LMP, you are 8 weeks pregnant. This is called gestational age and is 2 weeks longer than fetal age (time since conception).",
      },
      {
        q: "How is the due date determined?",
        a: "Naegele's rule is used: add 7 days to the LMP, subtract 3 months, and add 1 year. The result is the estimated due date at 40 weeks (280 days) of gestation.",
      },
      {
        q: "Is the calculation different for IVF pregnancies?",
        a: "Yes. When the embryo transfer date is known, dating is based directly on that date rather than the LMP. IVF pregnancies therefore have much more precise gestational ages.",
      },
    ],
  },
  "when-to-take-pregnancy-test": {
    slug: "when-to-take-pregnancy-test",
    title: "When and How to Take a Pregnancy Test: Urine and Blood Test Guide",
    metaTitle: "When to Take a Pregnancy Test: Urine & Blood Test Guide",
    description:
      "When does a pregnancy test turn positive? Urine hCG tests versus beta-hCG blood tests, optimal timing, causes of false results, and next steps after a positive.",
    date: "2026-07-21",
    updated: SITE_UPDATED,
    readingTime: "8 min read",
    keyword: "when to take a pregnancy test",
    trSlug: "gebelik-testi-ne-zaman-nasil-yapilir",
    tldr: [
      "Urine tests are most reliable one day after the expected period, using the first morning urine.",
      "A quantitative beta-hCG blood test can turn positive 8–10 days after conception — 2–3 days earlier than urine tests.",
      "Testing too early, dilute urine, or low hCG levels are the most common causes of false negatives.",
    ],
    faqs: [
      {
        q: "When does a pregnancy test become positive?",
        a: "A quantitative beta-hCG blood test may become positive 8–10 days after conception. The safest time for a home urine test is one day after the expected period date, using the first morning urine.",
      },
      {
        q: "Is a urine test or blood test more reliable?",
        a: "The blood beta-hCG test turns positive earlier and gives a numeric value, which provides information about gestational age and pregnancy progression. Urine tests are convenient and around 99% accurate when timed correctly.",
      },
      {
        q: "What causes a false negative result?",
        a: "The most common causes are testing too early before hCG has risen enough, dilute urine from high fluid intake earlier in the day, and — rarely — conditions such as ectopic pregnancy where hCG remains low.",
      },
    ],
  },
  "bleeding-in-pregnancy": {
    slug: "bleeding-in-pregnancy",
    title: "Bleeding in Pregnancy: Causes, and When It Is an Emergency",
    description:
      "Implantation bleeding, threatened miscarriage, ectopic pregnancy, placenta praevia and abruption. Which bleeding is urgent and which can wait for a scheduled review.",
    date: "2026-07-28",
    updated: SITE_UPDATED,
    readingTime: "9 min read",
    keyword: "bleeding in pregnancy",
    trSlug: "gebelikte-kanama",
    tldr: [
      "Bleeding occurs in roughly 20–30% of pregnancies; the majority go on to have a healthy outcome.",
      "Severe pain, heavy bright-red bleeding, fainting, or shoulder-tip pain require emergency assessment.",
      "In Rh-negative mothers every bleeding episode must be assessed for anti-D (Rh) immunoglobulin.",
    ],
    faqs: [
      {
        q: "Does bleeding in pregnancy always mean a miscarriage?",
        a: "No. A significant proportion of first-trimester bleeding is caused by benign conditions such as implantation or cervical sensitivity. Every episode should still be evaluated by a clinician.",
      },
      {
        q: "Which bleeding is a medical emergency?",
        a: "Heavy bright-red bleeding, severe one-sided pain, fainting, shoulder-tip pain, passage of tissue, or fever with a foul-smelling discharge all warrant immediate hospital assessment.",
      },
      {
        q: "How does bleeding from an ectopic pregnancy present?",
        a: "It usually begins as dark-brown spotting with one-sided pelvic pain. Serum beta-hCG rises more slowly than expected. Urgent evaluation is essential.",
      },
    ],
  },
  "nutrition-in-pregnancy": {
    slug: "nutrition-in-pregnancy",
    title: "Nutrition in Pregnancy: What to Include and What to Avoid",
    description:
      "A practical guide to nutrition in pregnancy: calorie needs, folic acid and iron, foods to avoid, trimester-based recommendations, and frequently asked questions.",
    metaDescription:
      "Practical nutrition guide in pregnancy: calorie needs, folic acid and iron, foods to avoid, and trimester-based recommendations.",
    date: "2026-08-04",
    updated: SITE_UPDATED,
    readingTime: "10 min read",
    keyword: "nutrition in pregnancy",
    trSlug: "gebelikte-beslenme",
    tldr: [
      "No additional calories are required in the first trimester; add roughly +340 kcal/day in the second and +450 kcal/day in the third.",
      "Folic acid should be started at least one month before conception at 400–800 mcg per day.",
      "Raw fish, raw or undercooked meat, unpasteurised dairy, and all alcohol should be avoided completely.",
    ],
    faqs: [
      {
        q: "How much caffeine is safe during pregnancy?",
        a: "Less than 200 mg per day is generally considered safe — roughly one cup of filter coffee or two small cups of tea. Higher intake has been associated with miscarriage and low birth weight.",
      },
      {
        q: "Which vitamins are essential?",
        a: "Folic acid (400–800 mcg), vitamin D, iodine and, when required, iron. Calcium and omega-3 (DHA) are usually met through diet; your doctor tailors the plan based on blood tests.",
      },
      {
        q: "How much weight should I gain?",
        a: "Recommended ranges: 11.5–16 kg for normal BMI, 7–11.5 kg for overweight, 5–9 kg for obese, and 12.5–18 kg for underweight women.",
      },
    ],
  },
  "ovarian-cysts": {
    slug: "ovarian-cysts",
    title: "Ovarian Cysts: Symptoms, Treatment, and When Surgery Is Needed",
    metaTitle: "Ovarian Cysts: Symptoms, Treatment & Surgery Guide",
    description:
      "Functional cysts, endometrioma, dermoid cysts and PCOS: types of ovarian cysts, symptoms, follow-up criteria, treatment options, and surgical indications.",
    metaDescription:
      "Functional cysts, endometrioma, dermoid, and PCOS: symptoms, follow-up criteria, treatment options, and surgical indications.",
    date: "2026-08-11",
    updated: SITE_UPDATED,
    readingTime: "10 min read",
    keyword: "ovarian cyst",
    trSlug: "yumurtalik-kisti",
    tldr: [
      "Most functional cysts resolve spontaneously within 2–3 menstrual cycles.",
      "Cysts larger than 5 cm, those with complex features, rapid growth, or pain require surgical evaluation.",
      "Sudden severe pelvic pain may indicate ovarian torsion — an emergency requiring immediate intervention.",
    ],
    faqs: [
      {
        q: "Are ovarian cysts dangerous?",
        a: "Most cysts are benign and resolve on their own. However, size, internal architecture, and symptoms must be assessed because of the risks of torsion, rupture, or, rarely, malignant transformation.",
      },
      {
        q: "When is surgery required?",
        a: "Persistent cysts larger than 5–6 cm, complex or solid components, endometrioma, dermoid cysts, torsion or rupture, and suspicious imaging findings are the main surgical indications.",
      },
      {
        q: "Can cyst surgery be performed laparoscopically?",
        a: "Yes. The great majority of benign cysts can be removed laparoscopically (keyhole surgery). Recovery is faster, scars are minimal, and ovarian tissue is preserved.",
      },
    ],
  },
  "vaginal-discharge": {
    slug: "vaginal-discharge",
    title: "Vaginal Discharge: Normal or Infection?",
    description:
      "When is vaginal discharge normal and when does it point to an infection? Causes by colour, odour and consistency: yeast, bacterial vaginosis, trichomoniasis and treatment.",
    metaDescription:
      "When is vaginal discharge normal vs an infection? Causes by colour, odour, consistency — yeast, bacterial vaginosis, trichomoniasis.",
    date: "2026-08-18",
    updated: SITE_UPDATED,
    readingTime: "9 min read",
    keyword: "vaginal discharge",
    trSlug: "vajinal-akinti",
    tldr: [
      "Clear or milky-white, odourless discharge is normal and varies in amount across the menstrual cycle.",
      "Itching with cheese-like discharge suggests a yeast infection; a grey, fishy-smelling discharge points to bacterial vaginosis.",
      "Frothy yellow-green discharge may indicate trichomoniasis; partner treatment is mandatory.",
    ],
    faqs: [
      {
        q: "When is vaginal discharge normal?",
        a: "Clear or milky-white, odourless discharge without itching or burning is physiological. It becomes more slippery and abundant around ovulation and thicker before menstruation.",
      },
      {
        q: "What are the symptoms of a yeast infection?",
        a: "A thick, white, cottage-cheese-like discharge — usually odourless — with itching, burning, and redness. Discomfort during intercourse and urination is common.",
      },
      {
        q: "Can it be treated with natural remedies alone?",
        a: "Mild physiological discharge needs no treatment beyond appropriate hygiene. Infections (yeast, BV, trichomoniasis) must be treated with the appropriate medications prescribed by a physician.",
      },
    ],
  },
  endometriosis: {
    slug: "endometriosis",
    title: "Endometriosis: Symptoms, Diagnosis and Treatment Options",
    description:
      "What is endometriosis and why does it occur? Severe menstrual pain, painful intercourse, infertility, diagnostic methods, and medical, hormonal and laparoscopic surgical treatments.",
    metaDescription:
      "What is endometriosis? Severe period pain, painful intercourse, infertility, diagnosis, plus medical, hormonal and laparoscopic care.",
    date: "2026-08-25",
    updated: SITE_UPDATED,
    readingTime: "10 min read",
    keyword: "endometriosis",
    trSlug: "endometriozis",
    tldr: [
      "Endometriosis is a chronic condition in which tissue similar to the uterine lining grows outside the uterus.",
      "The most common symptoms are severe menstrual pain, painful intercourse, and infertility; it affects roughly 1 in 10 women of reproductive age.",
      "Definitive diagnosis is by laparoscopy; treatment includes pain management, hormonal therapy, and laparoscopic surgery.",
    ],
    faqs: [
      {
        q: "What is endometriosis?",
        a: "An oestrogen-dependent, chronic disease in which tissue histologically similar to the uterine lining (endometrium) implants outside the uterus — on the ovaries, fallopian tubes, bowel, or peritoneum.",
      },
      {
        q: "Does endometriosis cause infertility?",
        a: "About 30–50% of women with endometriosis experience difficulty conceiving. Reduced ovarian reserve, tubal adhesions, and pelvic inflammation impair fertility; nonetheless, pregnancy is achievable in the majority of cases with appropriate treatment.",
      },
      {
        q: "How is endometriosis definitively diagnosed?",
        a: "Clinical assessment and ultrasound raise strong suspicion — endometrioma ('chocolate cyst') is usually visible on ultrasound. However, the definitive diagnosis is made by direct visualisation and biopsy during laparoscopy.",
      },
      {
        q: "Can it be treated without medication or naturally?",
        a: "In mild cases pain relievers, heat application, and lifestyle changes may ease symptoms. In most cases, however, hormonal therapy or surgery is required to halt disease progression.",
      },
    ],
  },
  "uterine-fibroids": {
    slug: "uterine-fibroids",
    title: "Uterine Fibroids (Myoma): Symptoms, Diagnosis and Treatment",
    description:
      "What are uterine fibroids and why do they occur? Heavy menstrual bleeding, pelvic pain, urinary symptoms, infertility, diagnostic pathway, and medical, hysteroscopic and laparoscopic surgical treatment options.",
    metaDescription:
      "What are uterine fibroids? Heavy bleeding, pelvic pain, infertility; diagnosis, medical, hysteroscopic and laparoscopic surgical care.",
    date: "2026-09-01",
    updated: SITE_UPDATED,
    readingTime: "10 min read",
    keyword: "uterine fibroids",
    trSlug: "miyom",
    tldr: [
      "Uterine fibroids (myomas) are benign tumours arising from the muscular layer of the uterus; they affect roughly 70% of women of reproductive age.",
      "The most common symptoms are heavy and prolonged menstrual bleeding, pelvic pain, urinary frequency, and — in some cases — infertility; many fibroids remain asymptomatic.",
      "Treatment is individualised according to fibroid size, location, symptoms, and pregnancy plans: options include surveillance, medical therapy, and hysteroscopic or laparoscopic myomectomy.",
    ],
    faqs: [
      {
        q: "What are uterine fibroids?",
        a: "Benign smooth-muscle tumours arising from the myometrium (uterine muscle layer). They are sensitive to oestrogen and progesterone. Malignant transformation is exceedingly rare (below 0.1%).",
      },
      {
        q: "Do fibroids cause infertility?",
        a: "Submucosal fibroids that distort the uterine cavity, and large intramural fibroids, can impair fertility. Small, favourably located fibroids generally do not affect the ability to conceive.",
      },
      {
        q: "Does every fibroid require surgery?",
        a: "No. Asymptomatic, small fibroids are monitored at regular intervals. Surgery is considered when fibroids cause significant symptoms, grow rapidly, interfere with fertility, or appear suspicious on imaging.",
      },
      {
        q: "Can fibroids recur after removal?",
        a: "After myomectomy, the risk of new fibroid development within five years is approximately 15–30%. After menopause, declining oestrogen levels typically cause existing fibroids to shrink.",
      },
    ],
  },
  "hpv-vaccine": {
    slug: "hpv-vaccine",
    title: "The HPV Vaccine: Who Needs It, When and Why? An Evidence-Based Guide",
    description:
      "What is HPV and which cancers does it cause? Vaccine types (Gardasil 9), age ranges, dosing schedule, safety profile, side effects, and vaccination for men.",
    metaTitle: "HPV Vaccine: Age, Dosing and Safety",
    metaDescription:
      "Who should receive the HPV vaccine? Gardasil 9 dosing, age limits, vaccination for men, safety profile and side effects.",
    date: "2026-09-08",
    updated: SITE_UPDATED,
    readingTime: "10 min read",
    keyword: "HPV vaccine",
    trSlug: "hpv-asisi",
    tldr: [
      "The HPV vaccine prevents the majority of cervical, vaginal, vulvar, anal, penile and oropharyngeal cancers, as well as genital warts.",
      "The most effective age window is 9–14 years, when only 2 doses are required. From 15 years onwards and in immunocompromised individuals, 3 doses are given.",
      "Vaccination is recommended for both girls and boys and may be offered up to age 45 after individual assessment. It is not recommended during pregnancy but is safe during breastfeeding.",
    ],
    faqs: [
      {
        q: "Who should receive the HPV vaccine?",
        a: "Ideally, girls and boys aged 9–14 before sexual activity begins. Routine vaccination is recommended up to age 26, and can be considered up to age 45 based on individual risk factors.",
      },
      {
        q: "How many doses are required?",
        a: "Between 9 and 14 years, a 2-dose schedule (0 and 6–12 months) is sufficient. From age 15 onwards, or in immunocompromised individuals, 3 doses are given (0, 2 and 6 months).",
      },
      {
        q: "Is the vaccine recommended for men as well?",
        a: "Yes. It protects men against anal, penile and oropharyngeal (head and neck) cancers and genital warts, and reduces transmission to sexual partners.",
      },
      {
        q: "Is the HPV vaccine safe?",
        a: "The World Health Organization, FDA and EMA all confirm that the vaccine has an excellent safety profile. The most common side effects are mild injection-site pain, redness and low-grade fever. Serious adverse events are very rare.",
      },
      {
        q: "Does the vaccine still help someone already exposed to HPV?",
        a: "Yes. Gardasil 9 protects against nine HPV types; even if you have been exposed to one type, the vaccine still protects against the remaining types. It is preventive, not therapeutic.",
      },
    ],
  },
  pcos: {
    slug: "pcos",
    title: "PCOS (Polycystic Ovary Syndrome): Symptoms, Diagnosis and Modern Treatment",
    metaTitle: "PCOS: Symptoms, Rotterdam Diagnosis and Treatment",
    description:
      "Irregular periods, hirsutism, acne and infertility: how PCOS is diagnosed using the Rotterdam criteria, the difference between PCOS and PCOM, insulin resistance and current treatment options.",
    metaDescription:
      "Polycystic Ovary Syndrome (PCOS) explained: symptoms, Rotterdam criteria, PCOS versus PCOM, insulin resistance and evidence-based treatment options.",
    date: "2026-09-15",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "polycystic ovary syndrome PCOS",
    trSlug: "pcos",
    tldr: [
      "PCOS is diagnosed when at least two of the three Rotterdam criteria are met: ovulatory dysfunction, clinical or biochemical hyperandrogenism, and polycystic ovarian morphology (PCOM) or elevated AMH.",
      "PCOM (a multi-follicular appearance on ultrasound) is only a finding, not a diagnosis. The 2023 international guideline also allows anti-Müllerian hormone (AMH) to replace ultrasound for adults.",
      "Treatment is individualised to the patient's goal — fertility, cycle regulation, control of skin symptoms and long-term metabolic risk — with lifestyle modification and insulin sensitisation as the foundation.",
    ],
    faqs: [
      {
        q: "Are PCOS and PCOM the same thing?",
        a: "No. PCOM (Polycystic Ovarian Morphology) refers only to the appearance of multiple small follicles on ultrasound — it is a finding. PCOS is a clinical syndrome that also requires ovulatory dysfunction and/or hyperandrogenism. Around 20–30% of healthy women can show PCOM without having PCOS.",
      },
      {
        q: "How is PCOS diagnosed?",
        a: "According to the Rotterdam criteria, at least two of the following must be present: (1) ovulatory dysfunction (irregular or absent periods), (2) clinical or biochemical hyperandrogenism (hirsutism, acne, elevated testosterone), and (3) PCOM on ultrasound or elevated AMH. Thyroid disorders, hyperprolactinaemia and adrenal disease must be excluded first.",
      },
      {
        q: "What is the role of insulin resistance in PCOS?",
        a: "Approximately 70% of women with PCOS have insulin resistance, which drives androgen production and worsens hirsutism, acne and weight gain. Addressing insulin resistance through weight loss, exercise and, when needed, metformin is a cornerstone of treatment.",
      },
      {
        q: "Can a woman with PCOS get pregnant?",
        a: "Yes. Although PCOS is a leading cause of anovulatory infertility, it is highly treatable. Losing even 5–10% of body weight can restore ovulation. When required, letrozole (first-line) or clomiphene citrate can induce ovulation, followed by gonadotropins or IVF in resistant cases.",
      },
      {
        q: "What are the long-term health risks of untreated PCOS?",
        a: "Untreated PCOS increases the lifetime risk of type 2 diabetes, cardiovascular disease, obstructive sleep apnoea, non-alcoholic fatty liver disease, endometrial cancer and mood disorders. PCOS is not only a reproductive concern — it is a metabolic condition requiring lifelong follow-up.",
      },
    ],
  },
  hysteroscopy: {
    slug: "hysteroscopy",
    title: "Hysteroscopy: Incision-Free Diagnosis and Treatment for Intrauterine Conditions",
    metaTitle: "Hysteroscopy: Indications, Procedure and Recovery",
    description:
      "Hysteroscopy allows direct, real-time visualisation of the uterine cavity through a thin, illuminated camera. Indications, diagnostic and operative techniques, the procedure itself, recovery and risks.",
    metaDescription:
      "What is hysteroscopy and how is it performed? Diagnostic versus operative hysteroscopy, polyp and fibroid resection, indications, recovery and potential risks.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "10 min read",
    keyword: "hysteroscopy",
    trSlug: "histeroskopi",
    tldr: [
      "Hysteroscopy is the direct visualisation of the uterine cavity using a thin, illuminated camera; it can be both diagnostic and therapeutic in the same session.",
      "Office (diagnostic) hysteroscopy is usually completed in 5–15 minutes with no anaesthesia or light sedation; operative hysteroscopy is performed under general or spinal anaesthesia in a theatre setting.",
      "It is today the gold standard for abnormal uterine bleeding, endometrial polyps, submucosal fibroids, uterine septum, intrauterine adhesions and selected infertility investigations.",
    ],
    faqs: [
      {
        q: "Is hysteroscopy painful?",
        a: "Diagnostic (office) hysteroscopy is generally well tolerated; brief cramping similar to menstrual pain may be felt. Operative hysteroscopy is performed under anaesthesia, so no pain is felt during the procedure.",
      },
      {
        q: "How long does the procedure take?",
        a: "Diagnostic hysteroscopy takes 5–15 minutes. Operative hysteroscopy lasts 20–60 minutes depending on the pathology being treated.",
      },
      {
        q: "When can I return to work and normal life?",
        a: "Same day after diagnostic hysteroscopy, and typically within 1–2 days after operative hysteroscopy. Strenuous exercise, sexual intercourse, tampons and swimming/hot tubs should be avoided for 1–2 weeks.",
      },
      {
        q: "Does hysteroscopy affect or delay pregnancy?",
        a: "On the contrary, in many cases it improves the chance of conception. Hysteroscopic removal of endometrial polyps, submucosal fibroids, a uterine septum or intrauterine adhesions improves pregnancy rates. Attempts to conceive can generally begin after 1–2 menstrual cycles.",
      },
      {
        q: "Are hysteroscopy and D&C the same?",
        a: "No. Dilation and curettage (D&C) is a blind scraping of the uterine cavity and can miss focal lesions such as polyps or fibroids. During hysteroscopy the cavity is seen directly and pathology is removed under vision, which makes hysteroscopy both more accurate and less traumatic.",
      },
      {
        q: "When can hysteroscopy not be performed?",
        a: "Active pelvic infection, active heavy bleeding, pregnancy and known cervical cancer are absolute contraindications. These conditions are treated first, and the procedure is performed only if still required.",
      },
    ],
  },
  menopause: {
    slug: "menopause",
    trSlug: "menopoz",
    title: "Menopause and Hormone Therapy: Symptoms, Diagnosis and Treatment Options",
    metaTitle: "Menopause Symptoms and Hormone Replacement Therapy (HRT)",
    description:
      "What is the menopause and when does it begin? Hot flushes, sleep disturbance, bone loss and hormone replacement therapy (HRT): who is suitable, and what are the risks?",
    metaDescription:
      "Menopause symptoms, diagnosis and hormone replacement therapy (HRT): suitability, risks, and non-hormonal alternatives.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "menopause",
    tldr: [
      "The menopause is diagnosed retrospectively, after 12 consecutive months without a period; the average age is 48–51.",
      "Hot flushes, night sweats, disturbed sleep, vaginal dryness and mood changes are the most common complaints, while bone loss progresses silently.",
      "Hormone replacement therapy (HRT) has a favourable benefit-to-risk balance when started under the age of 60 and within 10 years of the menopause.",
    ],
    faqs: [
      {
        q: "At what age does the menopause begin?",
        a: "Natural menopause usually occurs between 45 and 55, with an average age of 48–51. Cessation of periods before the age of 40 is termed premature ovarian insufficiency and should always be investigated.",
      },
      {
        q: "Are hormone tests required to diagnose the menopause?",
        a: "In a woman over 45 with typical symptoms the diagnosis is clinical and routine hormone testing is unnecessary. FSH and oestradiol measurement is more useful under the age of 45, after hysterectomy, or where hormonal contraception is being used.",
      },
      {
        q: "Does HRT cause breast cancer?",
        a: "Oestrogen-only therapy (used after hysterectomy) has not been shown to increase breast cancer risk significantly. Combined oestrogen–progestogen therapy carries a small but real increase in risk with use beyond five years. Treatment is therefore individualised and reviewed annually.",
      },
      {
        q: "Who should not take HRT?",
        a: "Active or previous breast cancer, other oestrogen-sensitive cancers, unexplained vaginal bleeding, active liver disease, previous deep vein thrombosis or pulmonary embolism, and a history of stroke or myocardial infarction are the principal contraindications.",
      },
      {
        q: "I would prefer not to take hormones — are there alternatives?",
        a: "Yes. SSRIs/SNRIs, gabapentin and the newer non-hormonal NK3 receptor antagonists are effective for hot flushes. Vaginal moisturisers, lubricants and very low-dose local oestrogen address vaginal dryness. Exercise, weight management, smoking cessation and calcium with vitamin D are recommended for all patients.",
      },
      {
        q: "How is bone loss monitored after the menopause?",
        a: "DEXA bone density scanning is recommended for all women over 65, and earlier in those with risk factors. Calcium, vitamin D, walking and resistance exercise form the basis of prevention.",
      },
    ],
  },
  infertility: {
    slug: "infertility",
    trSlug: "kisirlik-infertilite",
    title: "Infertility: Causes, Investigations and Treatment Options",
    metaTitle: "Infertility: Causes, Tests and Treatment Options",
    description:
      "No pregnancy after a year of trying? Female and male causes of infertility, the first-line investigations, and when ovulation induction, IUI or IVF is required.",
    metaDescription:
      "Causes of infertility, investigations in women and men, ovulation induction, intrauterine insemination (IUI) and IVF.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "infertility",
    tldr: [
      "Assessment is indicated after 12 months of regular unprotected intercourse under the age of 35, and after 6 months at 35 or older.",
      "Approximately one third of cases are female in origin and one third male; the remainder are combined or unexplained.",
      "First-line investigations are straightforward: semen analysis, confirmation of ovulation and assessment of tubal patency.",
    ],
    faqs: [
      {
        q: "When should infertility be investigated?",
        a: "After 12 months of regular unprotected intercourse in couples where the woman is under 35, and after 6 months at 35 or older. Investigation begins immediately where there is irregular menstruation, previous pelvic infection, endometriosis, or a known male factor.",
      },
      {
        q: "What are the most common causes?",
        a: "In women, ovulatory disorders (most often PCOS), tubal blockage, endometriosis, fibroids and intrauterine adhesions. In men, abnormalities of sperm count, motility or morphology, and varicocele. In roughly 10–15% of couples all tests are normal, which is termed unexplained infertility.",
      },
      {
        q: "Which tests are performed?",
        a: "In women, a day 2–4 hormone profile, AMH for ovarian reserve, pelvic ultrasound, and hysterosalpingography (HSG) or HyCoSy for tubal patency. In men, semen analysis after 2–5 days of abstinence. Hysteroscopy or laparoscopy is added where indicated.",
      },
      {
        q: "Does every couple need IVF?",
        a: "No. Many women with an ovulatory disorder conceive with ovulation induction alone. Intrauterine insemination (IUI) is offered in mild male factor and unexplained infertility. IVF is reserved for bilateral tubal blockage, severe male factor, advanced maternal age, or failure of earlier treatment.",
      },
      {
        q: "How does age affect the chance of pregnancy?",
        a: "Egg number and quality decline gradually after 32 and markedly after 37. For this reason, assessment and treatment should not be delayed in women over 35, as timing directly affects the chance of conception.",
      },
      {
        q: "Do lifestyle changes make a difference?",
        a: "Yes. Smoking cessation, a body mass index between 19 and 29, reduced alcohol intake, folic acid supplementation and adequate sleep improve both natural conception and treatment success. In men, smoking and heat exposure impair sperm quality.",
      },
    ],
  },
  "prenatal-screening-tests": {
    slug: "prenatal-screening-tests",
    trSlug: "gebelikte-tarama-testleri",
    title: "Prenatal Screening Tests: Combined Test, NIPT and the Anomaly Scan",
    metaTitle: "Prenatal Screening Tests: Combined Test, NIPT and Anomaly Scan",
    description:
      "When are the combined test, quadruple test, NIPT, nuchal translucency measurement and the anomaly scan performed? What a high-risk result means, and which tests give a definitive diagnosis.",
    metaDescription:
      "A schedule of prenatal screening tests: combined test, NIPT, quadruple test, nuchal translucency, anomaly scan and glucose tolerance testing.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "prenatal screening tests",
    tldr: [
      "Screening tests do not diagnose; they calculate a probability. A high-risk result does not mean the baby is affected.",
      "The combined test and nuchal translucency are performed at 11–14 weeks, the quadruple test at 16–18 weeks, NIPT from 10 weeks, and the anomaly scan at 20–22 weeks.",
      "A definitive diagnosis requires chorionic villus sampling (CVS) or amniocentesis, which are offered after a high-risk screening result.",
    ],
    faqs: [
      {
        q: "Are prenatal screening tests compulsory?",
        a: "No. Screening is optional. It is nevertheless offered to all pregnant women so that chromosomal conditions — principally Down syndrome — and certain structural anomalies can be identified early. The decision rests with the patient after appropriate counselling.",
      },
      {
        q: "When is the combined test performed and what does it measure?",
        a: "Between 11 weeks 0 days and 13 weeks 6 days. Maternal serum PAPP-A and free beta-hCG are measured, and nuchal translucency (NT) together with crown-rump length are assessed on ultrasound. Combined with maternal age, these give an individual risk for trisomies 21, 18 and 13, with a detection rate of approximately 90%.",
      },
      {
        q: "What is NIPT and who should consider it?",
        a: "NIPT analyses placenta-derived cell-free DNA in the maternal circulation and can be performed from 10 weeks. Detection of trisomy 21 exceeds 99% with a very low false-positive rate. It is particularly recommended in advanced maternal age, an increased or intermediate risk on the combined test, or a previous affected pregnancy, and may be offered to any woman who requests it. NIPT remains a screening, not a diagnostic, test.",
      },
      {
        q: "My result is 'high risk' — is my baby affected?",
        a: "Not necessarily. A high-risk result means only that the calculated probability exceeds the chosen threshold. A risk of 1 in 100, for example, means there is a 99% probability that the baby is unaffected. Further assessment with NIPT, or diagnostic testing by CVS or amniocentesis, is then discussed.",
      },
      {
        q: "Is amniocentesis risky?",
        a: "In experienced hands, the procedure-related pregnancy loss rate is currently reported as approximately 1 in 300 to 1 in 1000. It is performed under ultrasound guidance between 16 and 20 weeks and provides a definitive chromosomal result.",
      },
      {
        q: "When is the anomaly scan performed?",
        a: "Between 20 and 22 weeks. The brain, heart, spine, kidneys, stomach, face, hands and feet are examined systematically, along with the placenta, amniotic fluid volume and cervical length. It complements rather than replaces chromosomal screening.",
      },
      {
        q: "Is the glucose tolerance test necessary?",
        a: "Screening for gestational diabetes between 24 and 28 weeks is recommended for all pregnancies. In higher-risk women — obesity, previous gestational diabetes, or a family history of diabetes — testing may also be performed in the first trimester.",
      },
    ],
  },
  "birth-control-methods": {
    slug: "birth-control-methods",
    trSlug: "dogum-kontrol-yontemleri",
    title: "Birth Control Methods: Choosing the Right Option",
    metaTitle: "Birth Control Methods: Pill, IUD, Implant and Permanent Options",
    description:
      "The combined pill, hormonal and copper IUDs, the implant, injections, condoms and permanent methods: effectiveness, side effects, suitability and return to fertility.",
    metaDescription:
      "A comparison of contraceptive methods: pill, hormonal and copper IUD, implant, injection and condoms — effectiveness rates, side effects and how to choose.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "birth control methods",
    tldr: [
      "The most effective methods are those that do not depend on the user: the IUD, the contraceptive implant and permanent methods, all with annual failure rates below 1%.",
      "The combined pill is highly effective when taken correctly, but missed pills bring the real-world pregnancy rate to about 7% per year.",
      "No hormonal method protects against sexually transmitted infections; condoms remain necessary for that purpose.",
      "Fertility returns promptly after removal of an IUD or implant; only the three-monthly injection may delay conception by several months.",
    ],
    faqs: [
      {
        q: "Which contraceptive method is the most effective?",
        a: "Long-acting reversible methods that do not depend on daily use are the most effective: the hormonal IUD, the copper IUD and the contraceptive implant all have annual pregnancy rates below 1%. The combined pill exceeds 99% effectiveness with perfect use, but real-world effectiveness falls to about 93% because of missed pills.",
      },
      {
        q: "Is IUD insertion painful, and who is it suitable for?",
        a: "Insertion is an outpatient procedure lasting a few minutes; cramping similar to period pain is common. It is suitable for women of any age, whether or not they have given birth. The copper IUD provides 5–10 years of protection and the hormonal IUD 5–8 years; the latter also markedly reduces menstrual bleeding.",
      },
      {
        q: "Does the pill cause weight gain or infertility?",
        a: "Modern low-dose pills have not been shown to cause sustained weight gain, although mild fluid retention may occur in the first months. The pill does not cause infertility; ovulation usually resumes within one to three cycles after stopping.",
      },
      {
        q: "Which methods are safe while breastfeeding?",
        a: "Oestrogen-free methods are preferred: the progestogen-only pill, the implant, the three-monthly injection and the IUD may all be used safely. An IUD is usually fitted four to six weeks after delivery.",
      },
      {
        q: "How quickly does emergency contraception work?",
        a: "The sooner it is taken, the more effective it is. Levonorgestrel is licensed for use within 72 hours and ulipristal acetate within 120 hours. Insertion of a copper IUD within five days is the single most effective option. Emergency contraception is not a regular method.",
      },
      {
        q: "When should hormonal methods be avoided?",
        a: "Migraine with aura, uncontrolled hypertension, previous venous thrombosis, active breast cancer, severe liver disease and smoking over the age of 35 are contraindications to oestrogen-containing methods. Progestogen-only or non-hormonal options are advised in these situations.",
      },
    ],
  },
  "vaginal-birth-or-c-section": {
    slug: "vaginal-birth-or-c-section",
    trSlug: "normal-dogum-mu-sezaryen-mi",
    title: "Vaginal Birth or Caesarean Section: How the Decision Is Made",
    metaTitle: "Vaginal Birth or Caesarean Section: Risks, Benefits and Recovery",
    description:
      "The benefits and risks of vaginal birth and caesarean section, recovery times, absolute indications for surgery, vaginal birth after caesarean (VBAC) and preparing a birth plan.",
    metaDescription:
      "Vaginal birth or caesarean? Medical indications, recovery time, risks, epidural analgesia and vaginal birth after caesarean (VBAC) explained.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "vaginal birth or caesarean section",
    tldr: [
      "In the absence of a medical contraindication, vaginal birth offers a shorter recovery and fewer surgical risks for the mother, and generally better respiratory adaptation for the baby.",
      "Caesarean section is life-saving in defined situations: placenta praevia, malpresentation, fetal distress, obstructed labour and a previous classical uterine incision.",
      "Each caesarean increases the risks of the next pregnancy, including adhesions and abnormal placentation, which matters most for women planning a large family.",
      "In suitable candidates with one previous lower-segment caesarean, vaginal birth after caesarean (VBAC) succeeds in 60–80% of cases.",
    ],
    faqs: [
      {
        q: "When is a caesarean section medically necessary?",
        a: "Placenta praevia, placental abruption, cord prolapse, transverse lie, certain breech presentations, a previous classical (vertical) uterine incision, obstructed labour, cephalopelvic disproportion and fetal distress are the principal indications. Caesarean is also preferred in active genital herpes and untreated HIV infection.",
      },
      {
        q: "What are the advantages of vaginal birth?",
        a: "Hospital stay is usually one to two days and full recovery one to two weeks. Rates of infection, haemorrhage and thrombosis are lower, and breastfeeding is generally easier to establish. Passage through the birth canal helps clear fetal lung fluid and supports the development of the infant's microbiome.",
      },
      {
        q: "How long does recovery after a caesarean take?",
        a: "Hospital stay is typically two to three days and wound healing two to four weeks. Heavy lifting and driving should be avoided for four to six weeks. Early mobilisation within the first 24 hours is encouraged because it reduces the risk of thrombosis.",
      },
      {
        q: "Is vaginal birth after caesarean (VBAC) possible?",
        a: "Yes. VBAC may be offered to women with a single lower-segment (transverse) incision, a cephalic presentation and no additional risk factors; success rates are 60–80%. The risk of uterine rupture is approximately 1 in 200, so birth should take place in a unit with immediate access to emergency caesarean section.",
      },
      {
        q: "Does an epidural prolong labour or harm the baby?",
        a: "Epidural analgesia is the most effective form of pain relief in labour. It may modestly prolong the second stage but has not been shown to increase the caesarean rate. The amount of drug reaching the baby is very small and no harmful effect has been demonstrated.",
      },
      {
        q: "Is caesarean section on request a reasonable choice?",
        a: "Without a medical indication, caesarean section remains major surgery carrying risks of haemorrhage, infection, bladder or bowel injury, thrombosis and abnormal placentation in future pregnancies. The decision should be made with the clinician after discussing expectations and concerns; counselling for fear of childbirth is often effective.",
      },
    ],
  },
  "gestational-diabetes": {
    slug: "gestational-diabetes",
    trSlug: "gestasyonel-diyabet",
    title: "Gestational Diabetes: Screening, Diet and Treatment",
    metaTitle: "Gestational Diabetes: Glucose Tolerance Test, Diet and Insulin",
    description:
      "What gestational diabetes is and who develops it: glucose tolerance test thresholds, target blood glucose values, dietary management, insulin therapy and postnatal follow-up.",
    metaDescription:
      "A guide to gestational diabetes: the 24–28 week glucose tolerance test, diagnostic thresholds, diet, exercise, insulin and postnatal diabetes risk.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "gestational diabetes",
    tldr: [
      "Gestational diabetes arises from pregnancy-related insulin resistance and affects roughly 7–14% of pregnancies.",
      "All women are offered an oral glucose tolerance test (OGTT) at 24–28 weeks; those at higher risk are tested in the first trimester as well.",
      "About 70–85% of women reach target glucose levels with diet and exercise alone; insulin is added when they do not.",
      "A repeat OGTT is required 6–12 weeks after delivery, because the long-term risk of type 2 diabetes is substantially increased.",
    ],
    faqs: [
      {
        q: "When and how is the glucose tolerance test performed?",
        a: "Screening is offered between 24 and 28 weeks. In the one-step approach, a fasting sample is taken after eight hours without food, 75 g of glucose is given, and glucose is measured at one and two hours. In the two-step approach, a non-fasting 50 g screening test is performed first, followed by a 100 g confirmatory test if the result is raised.",
      },
      {
        q: "Which values confirm the diagnosis?",
        a: "On the 75 g OGTT, a single value at or above fasting 92 mg/dL, one hour 180 mg/dL, or two hours 153 mg/dL is sufficient for diagnosis. On the 50 g screening test, a one-hour value above 140 mg/dL requires confirmatory testing.",
      },
      {
        q: "Does gestational diabetes harm the baby?",
        a: "When untreated, it increases the risks of excessive fetal growth (macrosomia), birth trauma, preterm birth, neonatal hypoglycaemia and jaundice, as well as pre-eclampsia and caesarean birth for the mother. Most of these risks return close to background levels once glucose is well controlled.",
      },
      {
        q: "What are the target blood glucose values?",
        a: "In general, fasting below 95 mg/dL, one hour after meals below 140 mg/dL, and two hours after meals below 120 mg/dL. Levels are usually checked four times a day: fasting and after each main meal.",
      },
      {
        q: "What dietary changes are recommended?",
        a: "Carbohydrate should be distributed across three main meals and two to three snacks. Sugary drinks, refined flour and confectionery are removed; wholegrains, pulses, vegetables and adequate protein are preferred. Pairing carbohydrate with protein or healthy fat slows the rise in glucose, and a 15–20 minute walk after each meal lowers postprandial values appreciably.",
      },
      {
        q: "Is insulin harmful to the baby?",
        a: "No. Insulin does not cross the placenta and is the safest treatment in pregnancy. It is started when targets are not met within one to two weeks of dietary measures, and is usually discontinued immediately after delivery.",
      },
      {
        q: "Will my glucose return to normal after birth?",
        a: "In most women it does. A repeat 75 g OGTT is nevertheless required 6–12 weeks after delivery, with screening every one to three years thereafter. Breastfeeding, weight management and regular exercise reduce the long-term risk of type 2 diabetes.",
      },
    ],
  },
  "gynaecological-laparoscopy": {
    slug: "gynaecological-laparoscopy",
    trSlug: "laparoskopi-nedir",
    title: "Gynaecological Laparoscopy (Keyhole Surgery): Indications and Recovery",
    metaTitle: "Gynaecological Laparoscopy: Keyhole Surgery Explained",
    description:
      "How laparoscopic keyhole surgery is performed and which gynaecological conditions it treats: preparation, operating times, risks, recovery and its advantages over open surgery.",
    metaDescription:
      "A guide to gynaecological laparoscopy: how keyhole surgery is performed, which conditions it treats, its risks and how long recovery takes.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "11 min read",
    keyword: "gynaecological laparoscopy",
    tldr: [
      "Laparoscopy is keyhole surgery performed through a camera placed at the umbilicus and several 5–10 mm incisions.",
      "It is used for ovarian cysts, endometriosis, fibroids, ectopic pregnancy, fertility assessment and hysterectomy, among other indications.",
      "Compared with open surgery it causes less pain and blood loss, requires a shorter hospital stay and allows a markedly faster return to work.",
      "The rate of serious complications is below 1%; the principal risks are vascular, bowel and bladder injury, which are uncommon in experienced hands.",
    ],
    faqs: [
      {
        q: "What is laparoscopy (keyhole surgery)?",
        a: "Laparoscopy is an operation performed through several 5–10 mm incisions in the abdominal wall, using a camera and fine instruments. The abdominal cavity is inflated with carbon dioxide so that the organs can be visualised in magnified high definition, and the procedure is carried out under monitor guidance.",
      },
      {
        q: "Which conditions are treated laparoscopically?",
        a: "Ovarian cysts, endometriosis and endometriomas, fibroids, ectopic pregnancy, ovarian torsion, investigation of unexplained infertility and chronic pelvic pain, assessment of tubal patency, hysterectomy, and selected gynaecological cancer operations.",
      },
      {
        q: "How long does the operation take?",
        a: "This depends on the procedure. Diagnostic laparoscopy takes 20–40 minutes, removal of an ovarian cyst 45–60 minutes, and myomectomy, advanced endometriosis surgery or laparoscopic hysterectomy between 1.5 and 3 hours.",
      },
      {
        q: "How long does recovery take?",
        a: "Most patients are discharged the same day or the following morning. Incisional discomfort and gas-related shoulder pain are common for the first two to three days and respond to simple analgesia. Return to desk work usually takes 5–10 days, and return to heavy work or sport three to four weeks; after major procedures this may extend to four to six weeks.",
      },
      {
        q: "What are the risks?",
        a: "Serious complications occur in fewer than 1% of cases. Rarely there may be injury to blood vessels, bowel, bladder or ureter, bleeding, infection, port-site hernia or anaesthetic complications. Extensive adhesions, a very high body mass index or previous multiple operations may require conversion to open surgery.",
      },
      {
        q: "Will the scars be visible?",
        a: "The incisions measure only 5–10 mm, so scars are small. The umbilical incision becomes almost invisible over time; the remaining scars stay pink for a few months and fade considerably within a year.",
      },
      {
        q: "Does laparoscopy affect fertility?",
        a: "When correctly indicated, laparoscopy generally preserves and may improve fertility. Excision of endometriotic deposits, division of adhesions and careful cyst removal with preservation of healthy ovarian tissue can enhance the chance of conception. Excessive removal of ovarian tissue may reduce ovarian reserve, which is why surgical experience matters.",
      },
    ],
  },
  "laparoscopic-hysterectomy": {
    slug: "laparoscopic-hysterectomy",
    trSlug: "laparoskopik-histerektomi",
    title: "Laparoscopic Hysterectomy: The Procedure, Risks and Recovery",
    metaTitle: "Laparoscopic Hysterectomy: Procedure, Ovaries and Recovery",
    description:
      "When keyhole removal of the uterus is indicated: surgical techniques, whether the ovaries are preserved, operating time, risks, the recovery timeline and life after surgery.",
    metaDescription:
      "A guide to laparoscopic hysterectomy: indications, ovarian preservation, operating time, recovery timeline and the effect on sexual function.",
    date: "2026-09-22",
    updated: SITE_UPDATED,
    readingTime: "12 min read",
    keyword: "laparoscopic hysterectomy",
    tldr: [
      "Laparoscopic hysterectomy removes the uterus through small incisions under camera guidance, with less pain and faster recovery than open surgery.",
      "The commonest indications are fibroids, heavy menstrual bleeding unresponsive to treatment, adenomyosis, advanced endometriosis, uterine prolapse and certain pre-cancerous lesions.",
      "Removing the uterus does not mean removing the ovaries; in pre-menopausal women the ovaries are preserved wherever possible.",
      "Discharge is usually within one to two days and full recovery takes four to six weeks; menstruation ceases and pregnancy is no longer possible.",
    ],
    faqs: [
      {
        q: "What is a laparoscopic hysterectomy?",
        a: "It is removal of the uterus through several 5–10 mm abdominal incisions using a camera and fine instruments. The uterus is usually delivered vaginally and the vaginal vault is closed laparoscopically, avoiding the large abdominal incision required in open surgery.",
      },
      {
        q: "When is hysterectomy indicated?",
        a: "Heavy menstrual bleeding that has not responded to medical treatment or intrauterine procedures, large or multiple fibroids, adenomyosis, advanced endometriosis, uterine prolapse, recurrent abnormal bleeding, and certain pre-cancerous or malignant diagnoses. Where fertility is to be preserved, uterus-sparing options are considered first.",
      },
      {
        q: "Are the ovaries removed as well, and will I go through the menopause?",
        a: "When the ovaries are preserved, hormone production continues and surgical menopause does not occur; only menstruation ceases. In pre-menopausal women the ovaries are retained unless there is a medical reason to remove them. If they are removed, menopausal symptoms begin abruptly and hormone therapy may be discussed.",
      },
      {
        q: "How long does surgery take and how long is the hospital stay?",
        a: "Surgery usually takes 1.5 to 3 hours. Most women are discharged within one to two days, and same-day discharge is possible in selected cases.",
      },
      {
        q: "What does recovery involve?",
        a: "Pain is controlled with simple analgesia in the first week and short daily walks are encouraged. Normal daily activity resumes within two to four weeks, and heavy work and sport within four to six weeks. Intercourse, tampons and heavy lifting should be avoided for six weeks while the vaginal vault heals; light spotting during this period is normal.",
      },
      {
        q: "Will sexual function be affected?",
        a: "Studies show that once recovery is complete, sexual function is unchanged in most women and often improves because pain and bleeding have resolved. No hormonal change is expected when the ovaries are preserved.",
      },
      {
        q: "How does the laparoscopic route compare with open surgery?",
        a: "It offers less blood loss, less postoperative pain, a lower wound infection rate, a shorter hospital stay and a considerably faster return to work. Open surgery may still be more appropriate for a very large uterus, extensive adhesions or some cancer operations.",
      },
      {
        q: "Do I still need cervical screening afterwards?",
        a: "If the cervix was removed and there is no history of abnormal smears or cervical cancer, routine screening is no longer required. If the cervix was preserved (subtotal hysterectomy), or there is a history of high-risk HPV or abnormal cytology, screening continues at the intervals your clinician advises.",
      },
    ],
  },
  preeclampsia: {
    slug: "preeclampsia",
    trSlug: "preeklampsi",
    title: "Pre-eclampsia: Symptoms, Risk Factors and Prevention",
    metaTitle: "Pre-eclampsia: Warning Signs, Risk Factors and Aspirin",
    description:
      "High blood pressure in pregnancy and pre-eclampsia: who is at risk, which symptoms are urgent, who should take low-dose aspirin, diagnosis, monitoring and timing of birth.",
    metaDescription:
      "A guide to pre-eclampsia: symptoms, risk factors, prevention with low-dose aspirin, diagnostic criteria, monitoring and timing of delivery.",
    date: "2026-10-06",
    readingTime: "10 min read",
    keyword: "pre-eclampsia",
    tldr: [
      "Pre-eclampsia is new-onset high blood pressure after 20 weeks of pregnancy accompanied by proteinuria or signs of organ involvement; it affects roughly 2–8% of pregnancies.",
      "Severe headache, visual disturbance, upper abdominal pain, sudden swelling of the face or hands and breathlessness require urgent assessment.",
      "In high-risk women, low-dose aspirin started between 12 and 16 weeks substantially reduces the risk of pre-eclampsia.",
      "The definitive treatment is delivery of the placenta; timing depends on gestational age and the condition of mother and baby.",
    ],
    faqs: [
      {
        q: "What is pre-eclampsia?",
        a: "It is blood pressure of 140/90 mmHg or above arising for the first time after 20 weeks of pregnancy, together with protein in the urine or evidence of kidney, liver, clotting, neurological or placental dysfunction. It is thought to originate from abnormal development of the placenta.",
      },
      {
        q: "What are the symptoms?",
        a: "Mild disease may cause no symptoms and is detected by routine blood pressure checks. Warning symptoms include a persistent severe headache, blurred vision or flashing lights, pain below the ribs or in the upper abdomen, nausea and vomiting, sudden swelling of the face and hands, breathlessness and reduced urine output.",
      },
      {
        q: "Who is at risk?",
        a: "Risk factors include pre-eclampsia in a previous pregnancy, chronic hypertension, pre-existing diabetes, kidney disease, lupus or antiphospholipid syndrome, multiple pregnancy, first pregnancy, age over 35, obesity, IVF conception and a family history of pre-eclampsia.",
      },
      {
        q: "Does aspirin prevent pre-eclampsia?",
        a: "In high-risk women, low-dose aspirin started between 12 and 16 weeks and continued until 36 weeks has been shown to reduce the risk of pre-eclampsia, particularly severe early-onset disease. Aspirin should only be taken on medical advice and at the recommended dose.",
      },
      {
        q: "How is pre-eclampsia treated?",
        a: "The definitive treatment is delivery of the baby and placenta. Birth is generally recommended from 37 weeks. Earlier in pregnancy, management involves close monitoring, antihypertensive medication, magnesium sulphate to prevent seizures and corticosteroids to mature the baby's lungs; severe features may require delivery regardless of gestation.",
      },
      {
        q: "How does pre-eclampsia affect the baby?",
        a: "Reduced placental blood flow can slow fetal growth, reduce amniotic fluid and necessitate preterm birth. Placental abruption occurs rarely. Fetal growth and wellbeing are therefore monitored with ultrasound and Doppler studies.",
      },
      {
        q: "Does pre-eclampsia resolve after birth?",
        a: "Signs usually settle over the days and weeks after delivery, but pre-eclampsia can also develop or worsen within the first six weeks postpartum. Women who have had pre-eclampsia carry a higher long-term risk of hypertension and cardiovascular disease, so regular blood pressure and health checks are advised.",
      },
    ],
  },
  "cervical-screening-smear-hpv": {
    slug: "cervical-screening-smear-hpv",
    trSlug: "smear-testi-hpv-taramasi",
    title: "Cervical Screening: Smear Test and HPV Testing Explained",
    metaTitle: "Cervical Screening: Smear Test, HPV Testing and Results",
    description:
      "Cervical cancer screening: when to start, how often to test, what ASCUS, LSIL, HSIL and HPV-positive results mean, colposcopy and treatment.",
    metaDescription:
      "A guide to the smear test and HPV testing: screening ages, preparation, the meaning of abnormal and HPV-positive results, colposcopy and follow-up.",
    date: "2026-10-06",
    readingTime: "10 min read",
    keyword: "cervical screening",
    tldr: [
      "Almost all cervical cancers are caused by persistent high-risk HPV infection; regular screening detects pre-cancerous change years in advance.",
      "Türkiye's national programme offers HPV testing with cytology every five years between 30 and 65; international guidelines describe screening from age 21–25.",
      "A positive HPV result does not mean cancer; most infections clear spontaneously within one to two years.",
      "HPV 16/18 positivity or abnormal cells lead to colposcopy, a magnified examination of the cervix.",
    ],
    faqs: [
      {
        q: "What is a smear test?",
        a: "A sample of cells is taken from the cervix with a soft brush and examined under the microscope to detect pre-cancerous change. HPV testing can be performed on the same sample. The procedure takes a few minutes and is usually painless.",
      },
      {
        q: "When and how often should I be screened?",
        a: "Türkiye's Ministry of Health programme offers HPV testing with cytology every five years to women aged 30–65, free of charge at cancer screening centres (KETEM) and family practices. International guidelines recommend starting with cytology at 21–25 and moving to HPV-based screening every five years from 25–30. Immunosuppressed women need more frequent screening.",
      },
      {
        q: "How should I prepare for the test?",
        a: "Book it outside your period. Avoiding intercourse, douching, vaginal medication and lubricants for 48 hours beforehand improves the reliability of the result.",
      },
      {
        q: "My HPV test was positive. Do I have cancer?",
        a: "No. HPV is extremely common and most sexually active people encounter it at some point. The great majority of infections are cleared by the immune system within one to two years. What matters is long-term persistence of high-risk types, so follow-up is tailored to the HPV type and cytology result.",
      },
      {
        q: "What do ASCUS, LSIL and HSIL mean?",
        a: "ASCUS means atypical cells of undetermined significance, LSIL low-grade squamous intraepithelial change and HSIL high-grade pre-cancerous change. ASCUS and LSIL are usually managed by surveillance or colposcopy according to HPV status; HSIL warrants colposcopy and treatment where indicated.",
      },
      {
        q: "What is colposcopy, and is it painful?",
        a: "It is examination of the cervix with a magnifying instrument. Acetic acid is applied to highlight abnormal areas, from which small biopsies may be taken. It is performed like a smear test and takes 10–15 minutes; a brief cramp may be felt during biopsy.",
      },
      {
        q: "Do I still need screening if I have had the HPV vaccine?",
        a: "Yes. The vaccine provides strong protection against the HPV types that most often cause cancer, but not against all of them. Vaccinated women should continue age-appropriate screening.",
      },
    ],
  },
  "abnormal-uterine-bleeding": {
    slug: "abnormal-uterine-bleeding",
    trSlug: "anormal-rahim-kanamasi",
    title: "Abnormal Uterine Bleeding and Irregular Periods: Causes and Treatment",
    metaTitle: "Abnormal Uterine Bleeding: Causes, Diagnosis and Treatment",
    description:
      "Intermenstrual bleeding, heavy periods and irregular cycles: the limits of a normal period, PALM-COEIN causes, investigations, and medical and hysteroscopic treatment.",
    metaDescription:
      "A guide to abnormal uterine bleeding: normal menstrual limits, polyps, fibroids, hormonal causes, postmenopausal bleeding, diagnosis and treatment.",
    date: "2026-10-06",
    readingTime: "10 min read",
    keyword: "abnormal uterine bleeding",
    tldr: [
      "A normal cycle lasts 24–38 days with bleeding for fewer than eight days; bleeding outside these limits, between periods or in excessive amounts is abnormal uterine bleeding.",
      "Causes are classified as structural (polyp, adenomyosis, fibroid, malignancy or hyperplasia) and non-structural (coagulopathy, ovulatory dysfunction, medication).",
      "Assessment includes a pregnancy test, ultrasound, blood tests and, where indicated, endometrial biopsy or hysteroscopy.",
      "Any bleeding after the menopause must be investigated.",
    ],
    faqs: [
      {
        q: "What counts as abnormal bleeding?",
        a: "Cycles shorter than 24 or longer than 38 days, bleeding lasting more than eight days, variation of more than seven to nine days between cycles, bleeding between periods or after intercourse, bleeding that requires hourly pad changes or produces large clots, and any bleeding after the menopause.",
      },
      {
        q: "What are the commonest causes?",
        a: "The FIGO PALM-COEIN classification lists structural causes (polyps, adenomyosis, fibroids, and malignancy or hyperplasia) and non-structural causes (coagulopathy, ovulatory dysfunction such as PCOS, thyroid disease, raised prolactin or stress, endometrial causes, iatrogenic causes such as medication or an intrauterine device, and those not yet classified).",
      },
      {
        q: "Which investigations are needed?",
        a: "Pregnancy is excluded first. Examination, transvaginal ultrasound, a full blood count and iron studies follow, with thyroid, prolactin and clotting tests where indicated. Endometrial biopsy is advised for women over 45, those with risk factors and those who do not respond to treatment; hysteroscopy is recommended when a polyp or submucous fibroid is suspected.",
      },
      {
        q: "Can heavy periods cause anaemia?",
        a: "Yes. Prolonged heavy bleeding is one of the commonest causes of iron-deficiency anaemia, leading to fatigue, palpitations, hair loss and poor concentration. Iron replacement is added to treatment.",
      },
      {
        q: "What treatments are available?",
        a: "Treatment depends on the cause. Non-hormonal options include tranexamic acid and non-steroidal anti-inflammatory drugs; hormonal options include the combined pill, progestogens and the levonorgestrel intrauterine system. Polyps and submucous fibroids are removed hysteroscopically. Endometrial ablation or hysterectomy may be considered when other treatments fail.",
      },
      {
        q: "Is postmenopausal bleeding dangerous?",
        a: "Most postmenopausal bleeding has a benign cause such as endometrial atrophy or a polyp, but because it is the principal symptom of endometrial cancer, every episode should be assessed with ultrasound and, where indicated, biopsy.",
      },
      {
        q: "When should I seek urgent care?",
        a: "Bleeding that soaks a pad every hour or more often, dizziness or feeling faint, bleeding when pregnancy is possible and severe pelvic pain all require urgent assessment.",
      },
    ],
  },
  "ectopic-pregnancy": {
    slug: "ectopic-pregnancy",
    trSlug: "dis-gebelik",
    title: "Ectopic Pregnancy: Symptoms, Diagnosis and Treatment",
    metaTitle: "Ectopic Pregnancy: Warning Signs, Diagnosis and Treatment",
    description:
      "What an ectopic pregnancy is and which symptoms are urgent; diagnosis with beta-hCG and ultrasound, methotrexate, laparoscopic surgery and future fertility.",
    metaDescription:
      "A guide to ectopic pregnancy: risk factors, emergency symptoms, beta-hCG and ultrasound diagnosis, medical and laparoscopic treatment, and future pregnancy.",
    date: "2026-10-14",
    readingTime: "9 min read",
    keyword: "ectopic pregnancy",
    tldr: [
      "An ectopic pregnancy implants outside the uterine cavity, most often in a fallopian tube; it occurs in about 1–2% of pregnancies.",
      "Pelvic pain and vaginal bleeding after a missed period are the commonest symptoms; severe pain, faintness or shoulder-tip pain are emergencies.",
      "Diagnosis relies on serial serum beta-hCG measurements and transvaginal ultrasound.",
      "Suitable cases are treated with methotrexate and others with laparoscopic surgery; most women go on to have a healthy pregnancy.",
    ],
    faqs: [
      {
        q: "What is an ectopic pregnancy?",
        a: "It is implantation of a fertilised egg outside the lining of the uterus, in the great majority of cases in a fallopian tube. Less commonly it occurs in the ovary, cervix, a caesarean scar or the abdominal cavity. Such pregnancies cannot develop normally and, if untreated, the tube may rupture and cause internal bleeding.",
      },
      {
        q: "What are the symptoms?",
        a: "One-sided or generalised pelvic pain with spotting or light bleeding after a missed period or a positive test are the commonest symptoms. Severe abdominal pain, dizziness, fainting, shoulder-tip pain and a sensation of rectal pressure suggest internal bleeding and require emergency care.",
      },
      {
        q: "Who is at risk?",
        a: "Risk is increased by a previous ectopic pregnancy, tubal surgery or sterilisation, previous pelvic infection (particularly chlamydia), endometriosis, IVF, smoking and conceiving with an intrauterine device in place. However, about half of women with an ectopic pregnancy have no identifiable risk factor.",
      },
      {
        q: "How is it diagnosed?",
        a: "Serum beta-hCG is measured and repeated after 48 hours. On transvaginal ultrasound, the absence of an intrauterine gestational sac, an adnexal mass or free fluid in the pelvis supports the diagnosis. Several measurements may be needed; in the meantime the pregnancy is managed as a 'pregnancy of unknown location' with close follow-up.",
      },
      {
        q: "Who is suitable for methotrexate?",
        a: "Women who are haemodynamically stable, show no signs of internal bleeding, have relatively low beta-hCG and a small mass on ultrasound may be treated with a methotrexate injection, preserving the tube. Beta-hCG is monitored until it is negative; alcohol, folic acid-containing supplements and intercourse are avoided during this period.",
      },
      {
        q: "When is surgery needed?",
        a: "Laparoscopic surgery is performed for internal bleeding, suspected rupture, high beta-hCG, a visible fetal heartbeat or failure of medical treatment. Depending on the tube, either the pregnancy tissue is removed (salpingotomy) or the tube is removed (salpingectomy).",
      },
      {
        q: "Can I get pregnant again after an ectopic pregnancy?",
        a: "Yes. Most women who lose one tube conceive naturally through the other. Because the risk of recurrence is somewhat higher, an early ultrasound should confirm the location of any future pregnancy as soon as the test is positive.",
      },
    ],
  },
  "miscarriage-recurrent-pregnancy-loss": {
    slug: "miscarriage-recurrent-pregnancy-loss",
    trSlug: "dusuk-tekrarlayan-gebelik-kaybi",
    title: "Miscarriage and Recurrent Pregnancy Loss: Causes, Investigations and Treatment",
    metaTitle: "Miscarriage and Recurrent Pregnancy Loss: Causes and Tests",
    description:
      "Why early pregnancy loss happens and how it presents; expectant, medical and surgical management; investigations after recurrent miscarriage and the next pregnancy.",
    metaDescription:
      "A guide to miscarriage: symptoms, causes, management options, investigations and treatment for recurrent pregnancy loss, and advice for the next pregnancy.",
    date: "2026-10-14",
    readingTime: "10 min read",
    keyword: "miscarriage",
    tldr: [
      "About 10–15% of recognised pregnancies end in miscarriage in the first 12 weeks; the commonest cause is a chance chromosomal abnormality in the embryo.",
      "Most early miscarriages are not caused by anything the mother did, such as working, intercourse, exercise or ordinary stress.",
      "Management options are expectant, medical or surgical (vacuum aspiration).",
      "Investigation is recommended after two or more losses; with appropriate care most couples go on to have a healthy baby.",
    ],
    faqs: [
      {
        q: "How common is miscarriage?",
        a: "About 10–15% of recognised pregnancies miscarry in the first 12 weeks. The risk rises with maternal age and increases markedly after 35.",
      },
      {
        q: "What causes miscarriage?",
        a: "About half of early miscarriages result from chance chromosomal abnormalities in the embryo and are not expected to recur. Other causes include advanced maternal age, poorly controlled diabetes and thyroid disease, antiphospholipid syndrome, structural uterine problems (septum, polyp, submucous fibroid), smoking, heavy alcohol use and certain infections.",
      },
      {
        q: "What are the symptoms?",
        a: "Vaginal bleeding, cramping pain similar to or stronger than period pain, passage of tissue and a sudden reduction in pregnancy symptoms such as nausea or breast tenderness. Some miscarriages cause no symptoms and are detected on a routine scan (missed miscarriage).",
      },
      {
        q: "Is surgery always needed after a miscarriage?",
        a: "No. In suitable cases the pregnancy tissue may pass naturally, or the process may be hastened with medication such as misoprostol. Vacuum aspiration is offered for heavy bleeding, signs of infection, incomplete medical management or patient preference. The choice is made together with your clinician.",
      },
      {
        q: "What is recurrent pregnancy loss?",
        a: "The European Society of Human Reproduction and Embryology (ESHRE) defines recurrent pregnancy loss as two or more pregnancy losses. Investigation is recommended in this situation.",
      },
      {
        q: "Which investigations are performed?",
        a: "Antiphospholipid antibodies, thyroid function and antibodies, glucose and, where relevant, prolactin are checked. The uterus is assessed with 3D ultrasound, hysteroscopy or MRI. Chromosome analysis of the couple and genetic testing of pregnancy tissue are recommended in selected cases. No cause is found in about half of couples.",
      },
      {
        q: "When can I try again?",
        a: "You may try again once bleeding has stopped and you feel physically and emotionally ready; many women prefer to wait for one period. Continuing folic acid is advised. Even after unexplained recurrent loss, the chance of a live birth in the next pregnancy is high.",
      },
    ],
  },
  "postpartum-recovery": {
    slug: "postpartum-recovery",
    trSlug: "lohusalik-dogum-sonrasi",
    title: "The Postpartum Period: Recovery, Warning Signs and Check-ups",
    metaTitle: "Postpartum Recovery: What Is Normal and Warning Signs",
    description:
      "The first six weeks after birth: lochia, perineal and caesarean wound care, breastfeeding, postnatal depression, emergency symptoms, check-ups and contraception.",
    metaDescription:
      "A guide to the postpartum period: lochia, stitches, caesarean recovery, breastfeeding, postnatal depression, warning signs and postnatal check-ups.",
    date: "2026-10-14",
    readingTime: "10 min read",
    keyword: "postpartum recovery",
    tldr: [
      "The postpartum period lasts about six weeks after birth, during which the uterus returns to its pre-pregnancy size.",
      "Postpartum bleeding (lochia) is red and heavy in the first days, then lightens and usually stops within four to six weeks.",
      "Heavy bleeding, fever, severe headache, chest pain, breathlessness or a swollen leg require urgent care.",
      "The 'baby blues' are common and settle within two weeks; longer-lasting or severe symptoms suggest postnatal depression, which is treatable.",
    ],
    faqs: [
      {
        q: "How long does postpartum bleeding last?",
        a: "Lochia is red and may be heavier than a period for the first three to four days, then turns pinkish-brown and later yellowish-white, usually stopping within four to six weeks. An increase during breastfeeding is normal. Bleeding that soaks a pad every hour, large clots or offensive discharge require urgent assessment.",
      },
      {
        q: "How should I care for perineal stitches?",
        a: "Wash the area with warm water from front to back after using the toilet, pat dry and change pads frequently. Cold packs reduce swelling in the first days. Stitches usually dissolve and heal within two to three weeks. Increasing pain, redness, discharge or fever may indicate infection.",
      },
      {
        q: "What is recovery like after a caesarean section?",
        a: "Getting up on the first day is encouraged. The wound usually heals within one to two weeks; you may shower and should keep it dry. Avoid lifting anything heavier than your baby for six weeks. Seek advice for redness, discharge, wound separation or fever.",
      },
      {
        q: "What is the difference between the baby blues and postnatal depression?",
        a: "Most mothers experience tearfulness, mood swings and anxiety in the first days; these 'baby blues' usually resolve within two weeks. Low mood lasting more than two weeks, loss of enjoyment, disturbed sleep and appetite, feelings of worthlessness or thoughts of harming yourself or your baby suggest postnatal depression. It is treatable, and help should be sought promptly.",
      },
      {
        q: "Which symptoms are urgent?",
        a: "Heavy bleeding, fever above 38°C, severe or persistent headache, visual disturbance, chest pain, breathlessness, a painful swollen leg, a red, hard and painful area in the breast with fever, burning on passing urine and severe abdominal pain all require urgent assessment. Pre-eclampsia can also develop after birth.",
      },
      {
        q: "When are postnatal check-ups done?",
        a: "Blood pressure, bleeding, wound healing and breastfeeding are reviewed in the first weeks, with a comprehensive examination at around six weeks. This covers the uterus and any stitches, mood, the pelvic floor, contraception and follow-up of conditions found in pregnancy such as diabetes or hypertension.",
      },
      {
        q: "When can I become pregnant again?",
        a: "Ovulation can return as early as three to four weeks after birth in women who are not breastfeeding. Breastfeeding alone is not reliable contraception. An interval of at least 18 months between births is recommended for a healthy next pregnancy; choose a method compatible with breastfeeding together with your clinician.",
      },
    ],
  },
};

// Legacy Turkish slugs that Google still has indexed under /en/blog/*.
// Redirect to the current English slug with a single 301 (no chain).
const LEGACY_TR_SLUG_TO_EN: Record<string, string> = {
  // Auto-derived from POSTS[].trSlug
  ...Object.fromEntries(
    Object.values(POSTS)
      .filter((p): p is Post & { trSlug: string } => Boolean(p.trSlug))
      .map((p) => [p.trSlug, p.slug]),
  ),
  // Manual aliases for older slug variants indexed by search engines
  "histeroskopi-nedir": "hysteroscopy",
  "gebelik-testi-ne-zaman-nasil-yapilir": "when-to-take-pregnancy-test",
};

export const Route = createFileRoute("/en/blog/$slug")({
  beforeLoad: ({ params }) => {
    const mapped = LEGACY_TR_SLUG_TO_EN[params.slug];
    if (mapped && mapped !== params.slug) {
      throw redirect({ href: `/en/blog/${mapped}`, statusCode: 301 });
    }
  },
  loader: ({ params }) => {
    const post = POSTS[params.slug];
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found | Op. Dr. Sadık Kükrer, MD" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `https://www.sadikkukrer.com/en/blog/${post.slug}`;
    const modified = post.updated && post.updated > post.date ? post.updated : post.date;
    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          image: [AUTHOR_IMAGE],
          datePublished: post.date,
          dateModified: modified,
          inLanguage: "en-US",
          url,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".article-tldr", "h1", "h2"],
          },
          author: {
            "@type": "Physician",
            name: "Op. Dr. Sadık Kükrer, MD",
            medicalSpecialty: ["Gynecology", "Obstetrics"],
            url: "https://www.sadikkukrer.com/en",
            worksFor: {
              "@type": "Hospital",
              name: "Health Sciences University Adana City Training and Research Hospital",
            },
          },
          publisher: {
            "@type": "Physician",
            name: "Op. Dr. Sadık Kükrer, MD",
            url: "https://www.sadikkukrer.com/en",
          },
          about: { "@type": "MedicalCondition", name: post.keyword },
          keywords: `${post.keyword}, Adana obgyn, Op. Dr. Sadık Kükrer`,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.sadikkukrer.com/en",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.sadikkukrer.com/en/blog",
            },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        }),
      },
    ];
    if (post.faqs && post.faqs.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }
    const links: Array<{ rel: string; href: string; hrefLang?: string }> = [
      { rel: "canonical", href: url },
      { rel: "alternate", hrefLang: "en", href: url },
    ];
    if (post.trSlug) {
      links.push({
        rel: "alternate",
        hrefLang: "tr",
        href: `https://www.sadikkukrer.com/blog/${post.trSlug}`,
      });
    }
    const metaTitle = post.metaTitle ?? post.title;
    const metaDescription = post.metaDescription ?? post.description;
    return {
      meta: [
        { title: metaTitle },
        { name: "description", content: metaDescription },
        { name: "keywords", content: `${post.keyword}, Adana obgyn, Op. Dr. Sadık Kükrer` },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { property: "article:author", content: "Op. Dr. Sadık Kükrer, MD" },
        { property: "article:published_time", content: post.date },
        { property: "article:modified_time", content: modified },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: metaDescription },
      ],
      links,
      scripts,
    };
  },
  notFoundComponent: PostNotFound,
  component: BlogPost,
});

function PostNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center md:px-8">
      <h1 className="font-display text-3xl font-semibold">Article not found</h1>
      <p className="mt-3 text-[color:var(--muted-foreground)]">
        The article you are looking for could not be found or may have been moved.
      </p>
      <Link
        to="/en/blog"
        className="mt-6 inline-flex rounded-full bg-[color:var(--primary)] px-6 py-2.5 text-sm font-medium text-[color:var(--primary-foreground)]"
      >
        Back to blog
      </Link>
    </div>
  );
}

function BlogPost() {
  const { post } = Route.useLoaderData();
  const slug = post.slug;

  if (slug === "early-pregnancy-symptoms") {
    return <EarlyPregnancySymptomsPost post={post} />;
  }
  if (slug === "how-to-calculate-pregnancy-week") {
    return <PregnancyWeekCalculationPost post={post} />;
  }
  if (slug === "when-to-take-pregnancy-test") {
    return <PregnancyTestPost post={post} />;
  }
  if (slug === "bleeding-in-pregnancy") {
    return <BleedingInPregnancyPost post={post} />;
  }
  if (slug === "nutrition-in-pregnancy") {
    return <NutritionInPregnancyPost post={post} />;
  }
  if (slug === "ovarian-cysts") {
    return <OvarianCystsPost post={post} />;
  }
  if (slug === "vaginal-discharge") {
    return <VaginalDischargePost post={post} />;
  }
  if (slug === "endometriosis") {
    return <EndometriosisPost post={post} />;
  }
  if (slug === "uterine-fibroids") {
    return <UterineFibroidsPost post={post} />;
  }
  if (slug === "hpv-vaccine") {
    return <HpvVaccinePost post={post} />;
  }
  if (slug === "pcos") {
    return <PcosPost post={post} />;
  }
  if (slug === "hysteroscopy") {
    return <HysteroscopyPost post={post} />;
  }
  if (slug === "menopause") {
    return <MenopausePost post={post} />;
  }
  if (slug === "infertility") {
    return <InfertilityPost post={post} />;
  }
  if (slug === "prenatal-screening-tests") {
    return <PrenatalScreeningPost post={post} />;
  }
  if (slug === "birth-control-methods") {
    return <BirthControlPost post={post} />;
  }
  if (slug === "vaginal-birth-or-c-section") {
    return <BirthModePost post={post} />;
  }
  if (slug === "gestational-diabetes") {
    return <GestationalDiabetesPost post={post} />;
  }
  if (slug === "gynaecological-laparoscopy") {
    return <LaparoscopyPost post={post} />;
  }
  if (slug === "laparoscopic-hysterectomy") {
    return <LaparoscopicHysterectomyPost post={post} />;
  }

  if (slug === "preeclampsia") {
    return <PreeclampsiaPost post={post} />;
  }
  if (slug === "cervical-screening-smear-hpv") {
    return <CervicalScreeningPost post={post} />;
  }
  if (slug === "abnormal-uterine-bleeding") {
    return <AbnormalBleedingPost post={post} />;
  }
  if (slug === "ectopic-pregnancy") {
    return <EctopicPregnancyPost post={post} />;
  }
  if (slug === "miscarriage-recurrent-pregnancy-loss") {
    return <MiscarriagePost post={post} />;
  }
  if (slug === "postpartum-recovery") {
    return <PostpartumPost post={post} />;
  }
  return null;
}

function ArticleShell({ post, children }: { post: Post; children: React.ReactNode }) {
  return (
    <article className="mx-auto max-w-3xl px-5 py-16 md:px-8 md:py-24">
      <nav className="mb-8 flex items-center justify-between text-xs text-[color:var(--muted-foreground)]">
        <Link to="/en/blog" className="hover:text-[color:var(--primary-text)]">
          ← All articles
        </Link>
        {post.trSlug && (
          <Link
            to="/blog/$slug"
            params={{ slug: post.trSlug }}
            className="rounded-full border border-[color:var(--border)] px-3 py-1 uppercase tracking-[0.2em] hover:text-[color:var(--gold-light)]"
            aria-label="Türkçe oku — Türkçe versiyonu"
          >
            Türkçe oku →
          </Link>
        )}
      </nav>

      <header className="mb-10">
        <div className="flex flex-wrap items-center gap-3 text-xs text-[color:var(--muted-foreground)]">
          <time dateTime={post.date}>
            Published:{" "}
            {new Date(post.date).toLocaleDateString("en-US", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </time>
          {post.updated && post.updated > post.date && (
            <>
              <span>·</span>
              <time dateTime={post.updated}>
                Last updated:{" "}
                {new Date(post.updated).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </time>
            </>
          )}
          <span>·</span>
          <span>{post.readingTime}</span>
        </div>
        <h1 className="mt-4 font-display text-3xl font-semibold leading-tight md:text-5xl">
          {post.title}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-[color:var(--muted-foreground)]">
          {post.description}
        </p>
        <div className="mt-6 flex items-center gap-3 border-t border-[color:var(--border)] pt-5 text-sm">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[color:var(--primary)] font-display font-semibold text-[color:var(--primary-foreground)]">
            SK
          </div>
          <div>
            <p className="font-medium">Op. Dr. Sadık Kükrer, MD</p>
            <p className="text-xs text-[color:var(--muted-foreground)]">
              Obstetrician &amp; Gynecologist · Adana, Türkiye
            </p>
          </div>
        </div>
      </header>

      {post.tldr && post.tldr.length > 0 && (
        <aside
          className="article-tldr mb-10 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-6"
          aria-label="Key takeaways"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[color:var(--rose)]">
            Key takeaways
          </p>
          <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-[color:var(--foreground)]">
            {post.tldr.map((item, i) => (
              <li key={i} className="flex gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--primary)]" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      <div className="prose-content space-y-6 text-[15.5px] leading-[1.85] text-[color:var(--foreground)]">
        {children}
      </div>

      {post.faqs && post.faqs.length > 0 && (
        <section className="mt-14" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="font-display text-2xl font-semibold">
            Frequently asked questions
          </h2>
          <div className="mt-6 divide-y divide-[color:var(--border)] rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)]">
            {post.faqs.map((faq, i) => (
              <details key={i} className="group p-5 open:bg-[color:var(--darkest)]">
                <summary className="cursor-pointer list-none font-medium text-[color:var(--foreground)] marker:hidden">
                  <span className="inline-flex w-full items-start justify-between gap-4">
                    <span>{faq.q}</span>
                    <span className="mt-1 shrink-0 text-[color:var(--primary-text)] transition-transform group-open:rotate-45">
                      +
                    </span>
                  </span>
                </summary>
                <p className="mt-3 text-[15px] leading-relaxed text-[color:var(--muted-foreground)]">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      <MedicalDisclaimer lang="en" />

      <div className="mt-10 rounded-2xl bg-[color:var(--primary)] p-8 text-center text-[color:var(--primary-foreground)]">
        <p className="font-display text-2xl font-semibold">
          Would you like to book a consultation?
        </p>
        <p className="mt-2 opacity-90">
          For pregnancy monitoring, women's health, and gynecologic surgery in Adana, get in touch
          with Op. Dr. Sadık Kükrer.
        </p>
        <Link
          to="/en"
          hash="iletisim"
          className="mt-5 inline-flex rounded-full bg-[color:var(--darkest)] px-6 py-2.5 text-sm font-medium text-[color:var(--foreground)]"
        >
          Contact details
        </Link>
      </div>
    </article>
  );
}

/* ---------------- Post 1: Early Pregnancy Symptoms ---------------- */
function EarlyPregnancySymptomsPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Pregnancy is a period of rapid hormonal and physical change in the body. Some of these
        changes can be noticed even before a pregnancy test turns positive, and sometimes even
        before the expected period date. In this article I summarise the{" "}
        <strong>early pregnancy symptoms</strong> most commonly asked about in my clinic, when they
        typically appear, and when a pregnancy test should be performed.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        When do pregnancy symptoms start?
      </h2>
      <p>
        Most symptoms begin around 6–12 days after fertilisation, when the embryo implants into the
        uterine lining. Some women perceive these changes very early, while others notice nothing
        until their period is late. Both patterns are entirely normal.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        15 common early pregnancy symptoms
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Missed period</h3>
      <p>
        This is the most reliable early sign. In a woman with regular cycles, a delay of more than
        5–7 days is enough reason to take a pregnancy test.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Implantation bleeding</h3>
      <p>
        A light pink or brown spotting that may appear 1–2 days before the expected period. It
        usually lasts one to two days and is often mistaken for a light period.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Breast tenderness and fullness</h3>
      <p>
        Rising oestrogen and progesterone increase blood flow to breast tissue. Breasts may feel
        heavier, tender, or even painful, and the nipples may darken.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Marked fatigue</h3>
      <p>
        Progesterone has a sedative effect. During the first trimester many women experience strong
        daytime sleepiness and a noticeable drop in stamina.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Nausea and morning sickness</h3>
      <p>
        Nausea typically begins around 6 weeks and eases by 12–14 weeks. Despite its name, it can
        occur at any time of day, and certain smells can trigger it.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Frequent urination</h3>
      <p>
        Hormonal changes and, later, pressure from the growing uterus increase the need to urinate
        both day and night.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">7. Changes in taste and smell</h3>
      <p>
        Aversion to previously enjoyed foods, a metallic taste in the mouth, or heightened
        sensitivity to certain odours are common early findings.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">8. Mild abdominal cramping</h3>
      <p>
        Cramps similar to menstrual cramps may occur as the uterus expands and implantation takes
        place. Severe or one-sided pain should be evaluated promptly.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">9. Dizziness</h3>
      <p>
        A mild drop in blood pressure combined with an expanding vascular system can cause
        light-headedness, especially on standing up quickly.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        10. Elevated basal body temperature
      </h3>
      <p>
        A basal body temperature that remains elevated for more than 18 consecutive days after
        ovulation can be an early sign of pregnancy.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">11. Mood changes</h3>
      <p>
        Hormonal fluctuations can lead to sudden sadness, irritability, or the urge to cry without a
        clear cause.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">12. Bloating and constipation</h3>
      <p>
        Progesterone slows intestinal motility. Abdominal bloating and constipation are frequent
        complaints in the first weeks.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">13. Increased vaginal discharge</h3>
      <p>
        A slight increase in clear or milky-white, odourless discharge is normal. Itching, burning,
        or an unpleasant odour should be assessed for infection.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">14. Headache</h3>
      <p>
        Hormonal changes and increased blood volume can trigger headaches. Hydration and adequate
        sleep usually help.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">15. Food cravings</h3>
      <p>
        Sudden, strong desires for specific foods are a classic early sign, typically most
        pronounced during the first trimester.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        When should you take a pregnancy test?
      </h2>
      <p>
        <strong>Home urine tests</strong> are approximately 99% accurate one day after the expected
        period. Testing earlier increases the risk of a false negative. For the most reliable
        result:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Use your first morning urine on the first day of missed menses.</li>
        <li>
          For a definitive answer, a quantitative blood beta-hCG test both confirms the pregnancy
          and gives an estimate of gestational age.
        </li>
        <li>
          If the test is positive, an ultrasound at 6–8 weeks is used to assess the gestational sac
          and cardiac activity.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        When to seek immediate medical attention
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Severe, one-sided pelvic pain</li>
        <li>Heavy, bright-red bleeding similar to a period</li>
        <li>
          Fainting, extreme weakness, or shoulder-tip pain (possible warning of ectopic pregnancy)
        </li>
        <li>Persistent vomiting with inability to keep fluids down</li>
        <li>Fever, chills, or foul-smelling discharge</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">First step in pregnancy care</h2>
      <p>
        A positive pregnancy test is the beginning of a journey. At the first visit we confirm that
        the pregnancy is inside the uterus, whether it is single or multiple, whether there is
        cardiac activity, and the gestational age; folic acid and other supplements are planned. For
        safe, evidence-based, and individualised pregnancy care in Adana, you can contact Op. Dr.
        Sadık Kükrer.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 2: Pregnancy Week Calculation ---------------- */
function PregnancyWeekCalculationPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Gestational age forms the basis of many decisions during pregnancy — the timing of screening
        tests, ultrasound measurements, the estimated due date, and the assessment of fetal growth
        all depend on it. In this article I explain step by step{" "}
        <strong>how pregnancy weeks are calculated</strong>, which methods are most reliable, and
        why the due date is given as a range rather than a single day.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Gestational age is counted from the last menstrual period
      </h2>
      <p>
        Obstetric convention counts pregnancy from the first day of the last menstrual period (LMP),
        not from the date of conception. The reason is practical: the LMP is a date most women can
        remember, whereas fertilisation typically occurs around 2 weeks later and is much harder to
        date. As a result <strong>gestational age is about 2 weeks longer than fetal age</strong>{" "}
        (the time since conception).
      </p>
      <p>
        Example: if the first day of your LMP was 8 weeks ago, you are 8 weeks pregnant. The embryo
        itself is around 6 weeks old.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Calculating the estimated due date (Naegele's rule)
      </h2>
      <p>
        The most widely used formula is Naegele's rule. Apply the following to the first day of your
        LMP:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Add 7 days.</li>
        <li>Subtract 3 months.</li>
        <li>Add 1 year.</li>
      </ul>
      <p>
        The result is the estimated due date (EDD) at 40 weeks (280 days). For example, if your LMP
        was 10 January 2026, your EDD is 17 October 2026.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When cycles are irregular</h2>
      <p>
        Naegele's rule assumes a 28-day cycle with ovulation on day 14. When cycles are irregular or
        considerably longer, the LMP-based calculation may be several days off. In this situation
        ultrasound dating in the first trimester is far more accurate.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Ultrasound dating and when the due date is revised
      </h2>
      <p>
        During the first trimester the fetus grows in a very consistent way. Measuring the{" "}
        <strong>crown–rump length (CRL)</strong> between weeks 7 and 13 provides accurate dating to
        within a few days. Current obstetric guidelines redate the pregnancy from the ultrasound
        when:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>The difference from the LMP is more than 5 days between 6–8 weeks.</li>
        <li>The difference is more than 7 days between 9–13 weeks.</li>
      </ul>
      <p>
        After the first trimester, biparietal diameter, femur length, and abdominal circumference
        are used together for dating and growth assessment; however, dating becomes progressively
        less precise as the pregnancy advances.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">IVF pregnancies</h2>
      <p>
        Because the exact fertilisation and embryo transfer dates are known, IVF pregnancies are
        dated directly from those dates rather than the LMP. For example, for a day-5 embryo
        transfer, gestational age at transfer is 2 weeks and 5 days. IVF dating is the most precise
        form of pregnancy dating available.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">The trimesters</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>First trimester (weeks 1–13):</strong> Embryonic development, formation of the
          major organs. Screening tests and early ultrasound are performed in this window.
        </li>
        <li>
          <strong>Second trimester (weeks 14–27):</strong> Detailed anatomy scan (18–22 weeks); this
          is generally the most comfortable phase of pregnancy, and fetal movements are typically
          first felt around 18–20 weeks.
        </li>
        <li>
          <strong>Third trimester (weeks 28–40):</strong> Rapid fetal growth, gestational diabetes
          and blood pressure screening, and preparation for delivery.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Term and post-term pregnancy</h2>
      <p>
        Only about 5% of babies are born on the calculated due date; delivery typically occurs
        within a two-week window around that date:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Early term: 37 weeks 0 days – 38 weeks 6 days</li>
        <li>Full term: 39 weeks 0 days – 40 weeks 6 days</li>
        <li>Late term: 41 weeks 0 days – 41 weeks 6 days</li>
        <li>Post-term: 42 weeks 0 days and later</li>
      </ul>
      <p>
        Beyond 41 weeks the risks to the fetus increase, and induction of labour is generally
        considered.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Common questions</h2>
      <p>
        <strong>Can I calculate the due date if my cycle is irregular?</strong> The LMP-based
        calculation may be inaccurate. Ultrasound dating in the first trimester gives the most
        reliable answer.
      </p>
      <p>
        <strong>Is the calculation different in twin pregnancies?</strong> The dating principle is
        the same, but delivery usually occurs earlier (around 36–37 weeks for twins).
      </p>
      <p>
        <strong>Does the due date change during the pregnancy?</strong> If a significant discrepancy
        is found in the first trimester, the due date can be revised. After the first trimester the
        initial date is maintained.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Pregnancy care in Adana</h2>
      <p>
        Accurate dating from the first weeks makes it possible to plan screening tests and
        ultrasound at the right time and to spot problems early. For personalised, evidence-based
        pregnancy care in Adana, you can contact Op. Dr. Sadık Kükrer.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 3: Pregnancy Test Guide ---------------- */
function PregnancyTestPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        A pregnancy test is one of the earliest and most reliable ways to confirm pregnancy.
        However, the accuracy of the result depends heavily on the timing and technique of the test.
        This article explains <strong>when a pregnancy test should be taken</strong>, how urine and
        blood tests differ, why false results occur, and what to do after a positive result.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        The hCG hormone: the basis of every pregnancy test
      </h2>
      <p>
        All pregnancy tests detect the hormone <strong>human chorionic gonadotropin (hCG)</strong>.
        hCG is produced by the developing placenta after the embryo implants into the uterine wall,
        and it rises rapidly in early pregnancy. In a healthy early pregnancy hCG typically doubles
        every 48–72 hours.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Home urine pregnancy test</h2>
      <p>
        Home urine tests detect hCG qualitatively — they show a positive or negative result rather
        than a numeric value.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">When to test</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>The safest time is 1 day after the expected period date.</li>
        <li>
          Some highly sensitive tests can detect pregnancy up to 5 days before the expected period,
          but the risk of a false negative is considerable.
        </li>
        <li>The first morning urine is the most concentrated and gives the clearest result.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">How to test</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Read the manufacturer's instructions carefully.</li>
        <li>
          Hold the absorbent tip in the urine stream for 5–10 seconds, or dip it in a collected
          sample.
        </li>
        <li>
          Wait the recommended time (usually 3–5 minutes) and read the result. Do not evaluate lines
          that appear after this window.
        </li>
        <li>
          Any second line — even faint — is considered positive; the intensity of the line does not
          reflect the strength of the pregnancy.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Blood beta-hCG test</h2>
      <p>
        The <strong>blood beta-hCG test</strong> is a quantitative test performed in a laboratory.
        It provides a numeric value and has two important advantages over urine testing:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>It becomes positive earlier:</strong> often 8–10 days after conception, which is
          2–3 days earlier than a urine test.
        </li>
        <li>
          <strong>The numeric value provides information:</strong> monitoring the trend over 48
          hours helps assess the health of the pregnancy and alerts to problems such as ectopic
          pregnancy.
        </li>
      </ul>
      <p>Approximate reference values in early pregnancy:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>3 weeks: 5–50 mIU/mL</li>
        <li>4 weeks: 5–426 mIU/mL</li>
        <li>5 weeks: 18–7,340 mIU/mL</li>
        <li>6 weeks: 1,080–56,500 mIU/mL</li>
        <li>7–8 weeks: 7,650–229,000 mIU/mL</li>
      </ul>
      <p>
        These ranges are wide and individual values must always be interpreted by a physician
        together with the clinical picture and ultrasound.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Causes of false negatives</h2>
      <p>A negative result can be misleading in the following situations:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Testing too early:</strong> the most common cause. If hCG has not yet risen
          enough, the test cannot detect it.
        </li>
        <li>
          <strong>Dilute urine:</strong> a large fluid intake earlier in the day lowers urine hCG
          concentration.
        </li>
        <li>
          <strong>Reading the result too late:</strong> lines that appear after the recommended time
          may not be true positives.
        </li>
        <li>
          <strong>Very high hCG (hook effect):</strong> extremely high hormone levels can, rarely,
          cause the reagent to saturate and produce a false negative.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Causes of false positives</h2>
      <p>These are far less common than false negatives, but they can occur:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Recent miscarriage or termination (residual hCG in circulation)</li>
        <li>Recent hCG-containing fertility medication</li>
        <li>Rare conditions producing hCG (some ovarian tumours, molar pregnancy)</li>
        <li>Evaporation lines mistaken for a second line</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Steps after a positive result</h2>
      <ol className="list-decimal space-y-2 pl-6">
        <li>
          Confirm the positive test with a physician. In cases of uncertainty, the beta-hCG level is
          checked.
        </li>
        <li>Start folic acid (400–800 mcg/day) if not already taken.</li>
        <li>
          An ultrasound is planned around 6–8 weeks to confirm the location of the pregnancy
          (intrauterine vs. ectopic), whether it is single or multiple, and to assess cardiac
          activity.
        </li>
        <li>Routine early pregnancy blood tests are ordered.</li>
        <li>An individualised pregnancy monitoring plan is created.</li>
      </ol>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        When to seek urgent medical attention
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Positive test accompanied by severe abdominal pain</li>
        <li>Positive test with heavy bleeding</li>
        <li>Fainting, dizziness, or shoulder-tip pain (possible warning of ectopic pregnancy)</li>
        <li>Persistent vomiting with inability to keep fluids down</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Pregnancy care in Adana</h2>
      <p>
        The pregnancy journey begins with a positive test. Careful monitoring from the very first
        weeks is one of the most important factors for a healthy pregnancy. For pregnancy testing
        and follow-up in Adana, you can contact Op. Dr. Sadık Kükrer.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 4: Bleeding in Pregnancy ---------------- */
function BleedingInPregnancyPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        Bleeding occurs in roughly <strong>20–30% of pregnancies</strong> during the first trimester
        and, in the majority of cases, does not prevent the pregnancy from progressing to a healthy
        outcome. However, not every bleed carries the same meaning: some represent a normal
        physiological process, while others may be the first sign of an emergency that requires
        prompt intervention. This guide reviews the causes of bleeding at different stages of
        pregnancy, which can be observed, and which are urgent.
      </p>

      <div className="my-8 rounded-2xl border-2 border-[color:var(--rose)] bg-[color:var(--surface)] p-6">
        <p className="font-display text-lg font-semibold text-[color:var(--rose)]">Important</p>
        <p className="mt-2">
          <strong>Every episode of bleeding in pregnancy must be reported to a clinician.</strong>{" "}
          This article is for information only; the cause of bleeding can only be established by
          your doctor through examination and ultrasound.
        </p>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Causes of bleeding in the first trimester (weeks 0–13)
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Implantation bleeding</h3>
      <p>
        Occurs as the fertilised egg implants into the uterine wall, approximately 10–14 days after
        conception. It appears as <strong>light pink or brown spotting</strong>, lasts 1–3 days, is
        lighter than a menstrual period, and does not contain clots. It is painless or associated
        with a mild pulling sensation.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        2. Threatened miscarriage (abortus imminens)
      </h3>
      <p>
        There is bleeding, but the cervix is closed and the pregnancy is still viable. This is one
        of the most frequent causes of first-trimester bleeding. When fetal cardiac activity is seen
        on ultrasound, the likelihood of the pregnancy continuing is high. Rest, avoidance of
        intercourse, and — where indicated — progesterone support may be recommended.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Early miscarriage (spontaneous abortion)
      </h3>
      <p>
        Roughly 10–15% of clinically recognised pregnancies end in miscarriage in the first
        trimester; the majority are caused by chromosomal abnormalities. Signs include progressively
        heavier bleeding, passage of clots or tissue, cramping abdominal pain, and a sudden decrease
        in pregnancy symptoms.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Ectopic pregnancy</h3>
      <p>
        Implantation of the fertilised egg outside the uterus — most often in a fallopian tube. It
        occurs in approximately <strong>1–2% of pregnancies</strong> and{" "}
        <strong>is a life-threatening emergency</strong>. Symptoms include severe one-sided
        abdominal pain, bleeding, shoulder-tip pain, and dizziness. After 6 weeks, a positive
        pregnancy test with no intrauterine gestational sac on ultrasound is a strong warning sign.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Molar pregnancy (hydatidiform mole)
      </h3>
      <p>
        A rare abnormal development of the placenta. Diagnosis is based on bleeding, severe nausea
        and vomiting, markedly elevated hCG levels, and the typical "snowstorm" appearance on
        ultrasound. Treatment is uterine evacuation, followed by hCG monitoring.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Cervical causes</h3>
      <p>
        The vascularity of the cervix increases during pregnancy; contact bleeding may occur after
        intercourse, an examination, or from a cervical polyp. It is usually light, bright-red, and
        painless.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Causes of bleeding in the second and third trimesters (weeks 14–40)
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Placenta praevia</h3>
      <p>
        The placenta partially or completely covers the internal cervical os. The classical
        presentation is <strong>sudden, painless, bright-red bleeding</strong>. It typically
        presents after 20 weeks and is diagnosed by ultrasound. Bleeding may require caesarean
        delivery.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        2. Placental abruption (abruptio placentae)
      </h3>
      <p>
        Premature separation of the placenta from the uterine wall before delivery. It presents with{" "}
        <strong>severe abdominal pain, a board-like uterus, and dark bleeding</strong>. Occasionally
        the bleeding is concealed and shock develops without visible loss.{" "}
        <strong>It is a life-threatening emergency</strong> requiring urgent delivery for both
        mother and baby.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Vasa praevia</h3>
      <p>
        Fetal blood vessels run across the internal cervical os. Bleeding after rupture of the
        membranes is extremely dangerous for the baby and requires emergency caesarean delivery.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Preterm labour</h3>
      <p>
        Regular contractions and cervical dilatation before 37 weeks may be accompanied by a
        pinkish, mucus-tinged discharge known as the "show". Immediate assessment is essential and
        tocolytic therapy may be indicated.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Bloody show</h3>
      <p>
        The mucus, pink or brown discharge that occurs as the cervix begins to soften and dilate
        near term. At 37 weeks or more it is physiological and signals that labour is approaching.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Situations requiring immediate hospital attention
      </h2>
      <div className="rounded-2xl bg-[color:var(--surface)] p-6">
        <ul className="list-disc space-y-2 pl-6">
          <li>Bleeding heavy enough to fully soak a menstrual pad within one hour</li>
          <li>Passage of clots the size of a walnut or larger</li>
          <li>Severe, persistent, or one-sided abdominal or pelvic pain</li>
          <li>A hard, board-like uterus and abdomen</li>
          <li>Dizziness, fainting, visual disturbance, or palpitations</li>
          <li>Shoulder-tip pain (a possible sign of internal bleeding)</li>
          <li>Watery or clot-containing bleeding with regular contractions before 37 weeks</li>
          <li>A marked reduction in fetal movements</li>
          <li>Fever (over 38 °C) accompanied by bleeding</li>
          <li>Any bleeding episode in an Rh-negative mother</li>
        </ul>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        What to do — and not to do — before your visit
      </h2>
      <h3 className="mt-6 font-display text-xl font-semibold">Do</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Use a sanitary pad; note how often you need to change it to estimate the volume.</li>
        <li>
          If you pass tissue or clots, place them in a clean container and bring them to hospital.
        </li>
        <li>
          Record when the bleeding started, its colour (bright red, dark, brown), and any associated
          pain.
        </li>
        <li>If you are alone, inform a relative and arrange transport in advance.</li>
      </ul>
      <h3 className="mt-6 font-display text-xl font-semibold">Do not</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Do not use tampons; use pads only.</li>
        <li>Do not have intercourse.</li>
        <li>Do not perform a vaginal examination on yourself or douche.</li>
        <li>Do not take pain relievers, aspirin, or blood thinners without medical advice.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Rh incompatibility and bleeding</h2>
      <p>
        In Rh-negative mothers, <strong>every episode of bleeding</strong> should be assessed for
        the need for <strong>anti-D (Rh) immunoglobulin</strong> to prevent the development of
        antibodies against the fetus. An injection given within 72 hours protects future
        pregnancies.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Frequently asked questions</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">Is brown spotting dangerous?</h3>
      <p>
        Brown discharge indicates older blood (slowly draining over several days) and is usually
        less concerning than fresh bleeding. Any spotting during pregnancy should still be reported.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        I bled after intercourse — is there a problem?
      </h3>
      <p>
        Because cervical vascularity is increased in pregnancy, contact bleeding is common and
        usually harmless. However, since the amount can vary and other causes may be present, any
        first-time episode should be evaluated.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        I saw bleeding — does this mean I will miscarry?
      </h3>
      <p>
        No. The great majority of women who experience first-trimester bleeding go on to deliver
        healthy babies. When fetal cardiac activity is seen on ultrasound and the cervix is closed,
        the probability that the pregnancy will continue is high.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Does bed rest help during a bleeding episode?
      </h3>
      <p>
        There is no strong evidence that bed rest prevents miscarriage. However, avoiding heavy
        physical activity, travel, and intercourse while active bleeding continues is a reasonable
        precaution.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Assessment of pregnancy bleeding in Adana
      </h2>
      <p>
        Bleeding during pregnancy is understandably a source of anxiety for the mother and family.
        Early evaluation is essential — both to identify the underlying cause and to begin
        appropriate treatment on time. For early pregnancy ultrasound, evaluation of bleeding,
        anti-D administration, and comprehensive pregnancy care in Adana, you can contact Op. Dr.
        Sadık Kükrer.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 5: Nutrition in Pregnancy ---------------- */
function NutritionInPregnancyPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        Nutrition in pregnancy is not about "eating for two"; it is about providing{" "}
        <strong>the variety and quality of nutrients for two</strong>. A balanced diet directly
        influences the baby's healthy development, the mother's energy levels, and the prevention of
        common complaints such as constipation, reflux, and anaemia, as well as postnatal recovery.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        How many extra calories are needed during pregnancy?
      </h2>
      <p>
        Contrary to a widespread belief, calorie requirements do not increase immediately in early
        pregnancy. General guidance:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>First trimester (weeks 0–13):</strong> no additional calories are needed. Weight
          gain is often 0–2 kg because of nausea.
        </li>
        <li>
          <strong>Second trimester (weeks 14–27):</strong> approximately{" "}
          <strong>+340 kcal/day</strong>.
        </li>
        <li>
          <strong>Third trimester (weeks 28–40):</strong> approximately{" "}
          <strong>+450 kcal/day</strong>.
        </li>
      </ul>
      <p>
        Total expected weight gain depends on pre-pregnancy body mass index (BMI):{" "}
        <strong>11–16 kg</strong> for normal BMI, 7–11 kg for overweight, and 5–9 kg for obese
        women.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Food groups to include every day
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Protein</h3>
      <p>
        Daily requirement is roughly <strong>1.1 g/kg</strong> of body weight. Rotate between eggs,
        chicken, turkey, well-cooked red meat, fish, legumes (lentils, chickpeas, beans), yoghurt,
        and cheese.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Complex carbohydrates</h3>
      <p>
        Wholegrain bread, bulgur, oats, wholewheat pasta, brown rice, and quinoa. Choosing complex
        carbohydrates over refined flour and sugar reduces the risk of gestational diabetes and
        stabilises energy.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Vegetables and fruit</h3>
      <p>
        At least <strong>5 servings a day</strong> (approximately 400–500 g). Prioritise dark-green
        leafy vegetables (spinach, purslane, rocket), orange vegetables (carrot, pumpkin), citrus
        fruits, berries, kiwi, and other fresh fruit.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Dairy products</h3>
      <p>
        <strong>3–4 servings per day</strong> (milk, yoghurt, kefir, cheese). Daily calcium
        requirement is 1000 mg and is essential for the baby's bone development. Ensure that cheeses
        are made from <strong>pasteurised milk</strong>.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Healthy fats and omega-3</h3>
      <p>
        Olive oil, walnuts, almonds, avocado, and flaxseed. Two servings a week of low-mercury fish
        (salmon, mackerel, sardines, anchovies) provide important <strong>DHA</strong> for brain and
        eye development.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Key vitamins and minerals</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Folic acid (400–800 mcg/day):</strong> prevents neural tube defects. Ideally
          started <strong>at least one month before conception</strong> and continued through the
          first 12 weeks.
        </li>
        <li>
          <strong>Iron (27 mg/day):</strong> prevents anaemia and preterm birth. Sources include red
          meat, liver, legumes, molasses, and dried fruits. Vitamin C (orange, pepper, parsley)
          enhances absorption; <strong>tea and coffee reduce it</strong>, so take them between
          meals.
        </li>
        <li>
          <strong>Calcium (1000 mg/day):</strong> dairy, sesame, tahini, broccoli.
        </li>
        <li>
          <strong>Vitamin D (600–2000 IU/day):</strong> supplementation is advised when sun exposure
          is inadequate; essential for calcium absorption and bone development.
        </li>
        <li>
          <strong>Iodine (220 mcg/day):</strong> iodised table salt is usually sufficient; critical
          for thyroid function and fetal brain development.
        </li>
        <li>
          <strong>Choline (450 mg/day):</strong> egg yolk, meat, milk.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Foods and drinks to avoid completely
      </h2>
      <div className="rounded-2xl border border-[color:var(--rose)] bg-[color:var(--surface)] p-6">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Raw or undercooked meat, poultry, and fish</strong> (sushi, tartare, rare steak)
            — risk of toxoplasmosis and listeria.
          </li>
          <li>
            <strong>Unpasteurised milk and dairy products</strong>. Check the label for
            "pasteurised".
          </li>
          <li>
            <strong>Products containing raw eggs</strong> (homemade mayonnaise, tiramisu, raw cookie
            dough, ice cream with raw egg).
          </li>
          <li>
            <strong>High-mercury fish:</strong> shark, swordfish, king mackerel, marlin (canned
            light tuna may be consumed in limited amounts).
          </li>
          <li>
            <strong>Deli meats such as ham, salami, sausage, and sujuk</strong> — because of
            listeria risk, they should be heated through before consumption.
          </li>
          <li>
            <strong>Alcohol</strong> — no amount is considered safe.
          </li>
          <li>
            <strong>Raw sprouts</strong> (alfalfa, broccoli sprouts, etc.) — bacterial contamination
            risk.
          </li>
          <li>
            <strong>Herbal teas</strong> in large amounts (sage, rosemary, excessive chamomile);
            mint and linden may be consumed in moderation, but consult your doctor before starting
            any new herbal tea.
          </li>
        </ul>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">To consume in moderation</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Caffeine:</strong> keep below <strong>200 mg/day</strong> (roughly one cup of
          filter coffee or two small cups of Turkish coffee). Account for cola, energy drinks, tea,
          and chocolate.
        </li>
        <li>
          <strong>Sugar and sweets:</strong> restrict because of gestational diabetes risk; prefer
          water, ayran, or milk to sugary drinks.
        </li>
        <li>
          <strong>Salt:</strong> use iodised salt in normal amounts; excess salt increases oedema
          and blood pressure risk. Watch hidden salt in soups, canned foods, and crisps.
        </li>
        <li>
          <strong>Liver (high in vitamin A):</strong> only occasionally and in small amounts; very
          high vitamin A can harm the fetus.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Water intake</h2>
      <p>
        Aim for <strong>2–2.5 litres of water per day</strong>. Adequate hydration helps prevent
        constipation, urinary tract infections, preterm labour, and headaches. Pale-yellow urine is
        a good indicator.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Practical trimester-by-trimester advice
      </h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        First trimester (nausea and low appetite)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Eat small, frequent meals; never let the stomach stay completely empty.</li>
        <li>Keep dry biscuits, unsalted crackers, or almonds by the bed before rising.</li>
        <li>Avoid strong-smelling foods; cold meals are often better tolerated.</li>
        <li>Ginger (fresh or as tea) can help with nausea.</li>
        <li>Discuss vitamin B6 supplementation with your doctor.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Second trimester (energy and appetite return)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Increase protein and iron sources.</li>
        <li>Ensure daily dairy for calcium.</li>
        <li>The 50 g oral glucose challenge test is performed between weeks 24 and 28.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Third trimester (reflux and fullness)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Divide intake into 5–6 small meals per day.</li>
        <li>Do not lie down for at least 1–2 hours after eating.</li>
        <li>Limit spicy, very fatty, and acidic foods.</li>
        <li>Increase fibre and water intake to reduce constipation.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">A sample daily menu</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Breakfast:</strong> boiled egg, white cheese, olives, tomato, cucumber, walnuts,
          wholewheat bread, linden tea.
        </li>
        <li>
          <strong>Mid-morning snack:</strong> a piece of fruit + 5–6 almonds/walnuts.
        </li>
        <li>
          <strong>Lunch:</strong> lentil soup, grilled chicken, bulgur pilaf, seasonal salad with
          lemon and olive oil.
        </li>
        <li>
          <strong>Afternoon snack:</strong> yoghurt + 1 teaspoon of molasses or fresh fruit.
        </li>
        <li>
          <strong>Dinner:</strong> baked salmon, steamed broccoli and carrot, brown rice or quinoa,
          cacık (yoghurt with cucumber).
        </li>
        <li>
          <strong>Before bed:</strong> a glass of milk or a small banana.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Frequently asked questions</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Are honey, molasses, and tahini safe in pregnancy?
      </h3>
      <p>
        Yes. Pasteurised or traditional honey, molasses, and tahini are safe during pregnancy;
        molasses and tahini are especially valuable sources of iron. The warning against honey
        applies only to <strong>infants under one year of age</strong>.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Can I follow a vegetarian or vegan diet?
      </h3>
      <p>
        Yes, but planning with your doctor and a dietitian is essential to cover protein, vitamin
        B12, iron, zinc, omega-3 (DHA), and vitamin D.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Can I eat tuna?</h3>
      <p>
        Canned light tuna is generally considered safe up to one small serving per week. Avoiding
        albacore (white) tuna is safer, as its mercury content is higher.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        My weight gain is too low or too high — what should I do?
      </h3>
      <p>
        Weight gain should not be judged on the scale alone but alongside the baby's growth curve.
        If ultrasound and fetal measurements are reassuring, isolated variation in maternal weight
        is rarely a concern. When gain is markedly high or low, your doctor may refer you to a
        dietitian.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Can I start dieting immediately after birth?
      </h3>
      <p>
        Restrictive diets are not advisable, especially while breastfeeding. Nutritional needs
        during lactation are even higher than during pregnancy (+500 kcal/day). Aim for a gradual,
        balanced return to pre-pregnancy weight over 6–12 months.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Pregnancy care and nutritional guidance in Adana
      </h2>
      <p>
        Every pregnancy has its own nutritional profile: gestational diabetes, anaemia, thyroid
        disorders, and multiple pregnancies all call for an individualised plan. For comprehensive
        pregnancy monitoring, laboratory assessment, and, where needed, referral to a dietitian, you
        can consult Op. Dr. Sadık Kükrer in Adana.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 6: Ovarian Cysts ---------------- */
function OvarianCystsPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p className="text-lg">
        An ovarian cyst is a fluid-filled or mixed-content sac that develops within or on the
        surface of the ovary. Most women of reproductive age experience at least one ovarian cyst
        during their lifetime.
        <strong> The great majority of cysts are benign</strong> and resolve on their own; a smaller
        proportion require follow-up or surgical treatment. This guide reviews the different types
        of cysts, their symptoms, and how treatment decisions are made.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Types of ovarian cyst</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Functional (simple) cysts</h3>
      <p>
        The most common type; these form as <strong>part of the menstrual cycle</strong>. They are
        usually smaller than 5 cm, unilocular, and contain clear fluid. Most resolve spontaneously
        within 1–3 menstrual cycles.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Follicular cyst:</strong> forms when a follicle continues to grow because
          ovulation has not occurred.
        </li>
        <li>
          <strong>Corpus luteum cyst:</strong> develops when fluid accumulates in the corpus luteum
          after ovulation; may also be seen in early pregnancy.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Endometrioma (chocolate cyst)</h3>
      <p>
        Forms when tissue similar to the uterine lining implants in the ovary in the setting of
        endometriosis. Because the cyst contains old blood, its content is dark brown. It is
        associated with chronic pelvic pain, painful periods, painful intercourse, and
        <strong> infertility</strong>.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Dermoid cyst (teratoma)</h3>
      <p>
        Arises from congenital embryonic cells and may contain hair, teeth, fat, and skin tissue. It
        is generally benign, but as it enlarges the risk of{" "}
        <strong>rupture or ovarian torsion</strong> increases; surgical removal is therefore
        recommended.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        4. Cystadenomas (serous / mucinous)
      </h3>
      <p>
        Benign tumours that develop from the outer surface of the ovary and can occasionally reach
        large sizes. Surgical removal is usually indicated.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Polycystic ovary syndrome (PCOS)
      </h3>
      <p>
        Not truly a "cyst" in the classical sense. Ultrasound shows numerous small follicles (2–9
        mm) in both ovaries. PCOS is a hormonal syndrome accompanied by irregular menses, hirsutism,
        acne, weight gain, and insulin resistance, and requires{" "}
        <strong>a distinct management approach</strong>.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Complex or suspicious cysts</h3>
      <p>
        A cyst is classified as "complex" when ultrasound reveals solid components, thick
        septations, irregular walls, increased blood flow, or free peritoneal fluid. These cysts
        require more detailed evaluation for <strong>malignancy</strong> — MRI, tumour markers, and
        gynaecologic oncology consultation.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms</h2>
      <p>
        Most cysts are <strong>asymptomatic</strong> and are discovered incidentally on routine
        ultrasound. When symptomatic, the most common presentations are:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Unilateral, dull or sharp pelvic pain</li>
        <li>Menstrual irregularities, premenstrual spotting</li>
        <li>
          Painful periods (dysmenorrhoea) and painful intercourse (particularly with endometrioma)
        </li>
        <li>Abdominal bloating, sense of fullness</li>
        <li>Urinary frequency or bowel pressure (with large cysts)</li>
        <li>Infertility (especially endometrioma and PCOS)</li>
      </ul>

      <div className="my-8 rounded-2xl border-2 border-[color:var(--rose)] bg-[color:var(--surface)] p-6">
        <p className="font-display text-lg font-semibold text-[color:var(--rose)]">
          Emergency symptoms
        </p>
        <p className="mt-2">
          Sudden <strong>severe one-sided abdominal pain</strong> with nausea and vomiting, fever,
          dizziness, or fainting may indicate <strong>cyst rupture</strong> or{" "}
          <strong>ovarian torsion</strong>. Attend the emergency department immediately — both
          conditions may require urgent surgery.
        </p>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">How is the diagnosis made?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Transvaginal ultrasound:</strong> the gold standard; evaluates size, structure,
          uni- or multilocular architecture, and vascularity.
        </li>
        <li>
          <strong>Tumour markers:</strong> CA-125, HE4, CEA, AFP, hCG, LDH — selected according to
          age and cyst type. CA-125 can be elevated in endometrioma and some benign conditions and
          is not diagnostic on its own.
        </li>
        <li>
          <strong>MRI:</strong> provides a clearer view when ultrasound is inconclusive in complex
          cysts.
        </li>
        <li>
          <strong>Hormone testing:</strong> particularly when PCOS or irregular menses is suspected.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">When is observation sufficient?</h2>
      <p>Cysts with the following characteristics are typically observed:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Simple functional cysts under 5 cm</li>
        <li>
          Cysts in premenopausal women that shrink or resolve on post-menstrual follow-up ultrasound
        </li>
        <li>Normal tumour markers, no pain, no increased vascularity</li>
      </ul>
      <p>
        Follow-up is generally performed with ultrasound every 6–12 weeks. Most functional cysts
        resolve within this period.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment options</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Medical therapy</h3>
      <p>
        <strong>Combined oral contraceptives</strong> can prevent the formation of new functional
        cysts but do not "dissolve" an existing cyst. In endometriosis and PCOS, hormonal therapies
        are used to control pain and regulate the cycle. Simple analgesics (paracetamol; NSAIDs
        outside pregnancy) can help with pain.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Surgical treatment</h3>
      <p>
        The modern standard is <strong>laparoscopy</strong> (keyhole surgery). It is performed
        through small incisions with rapid recovery and an excellent cosmetic result. Robotic
        surgery may be offered as an option. Open surgery (laparotomy) is reserved for very large or
        malignancy-suspicious cysts.
      </p>
      <p>Surgical options:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Cystectomy:</strong> removal of the cyst while preserving ovarian tissue —
          preferred in reproductive-age women.
        </li>
        <li>
          <strong>Oophorectomy:</strong> removal of the ovary — usually after menopause or when
          malignancy is suspected.
        </li>
        <li>
          <strong>Salpingo-oophorectomy:</strong> removal of the ovary and fallopian tube together.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">When is surgery necessary?</h2>
      <div className="rounded-2xl bg-[color:var(--surface)] p-6">
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Cyst 5–8 cm or larger</strong> that is not shrinking on follow-up
          </li>
          <li>Complex cysts (solid components, multiloculated, irregular walls)</li>
          <li>Elevated tumour markers or suspicion of malignancy on MRI/ultrasound</li>
          <li>Cyst rupture, ovarian torsion, or intraperitoneal bleeding</li>
          <li>Any newly detected ovarian cyst in a postmenopausal woman</li>
          <li>Endometrioma contributing to infertility (particularly in patients planning IVF)</li>
          <li>Severe, treatment-resistant chronic pelvic pain</li>
          <li>Enlarging dermoid cysts (torsion risk)</li>
        </ul>
      </div>

      <h2 className="mt-10 font-display text-2xl font-semibold">Are ovarian cysts cancer?</h2>
      <p>
        In women of reproductive age, <strong>more than 90% of ovarian cysts are benign</strong>.
        The risk of ovarian cancer rises with <strong>postmenopausal status</strong>, complex
        morphology, rapid growth, and elevated CA-125. Women with a family history of ovarian or
        breast cancer may require BRCA1/BRCA2 genetic assessment.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Do cysts recur?</h2>
      <p>
        Recurrence of functional cysts is normal and generally not clinically significant.
        Endometrioma has a <strong>20–30%</strong> recurrence rate within five years; for this
        reason, postoperative hormonal therapy and regular follow-up are recommended. Dermoid cysts
        rarely recur in the same location, but occur in the contralateral ovary in about 10% of
        patients.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Frequently asked questions</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Do ovarian cysts prevent pregnancy?
      </h3>
      <p>
        Most small functional cysts do not interfere with conception. However, endometrioma, large
        dermoid cysts, and PCOS can reduce fertility. If infertility is suspected, a detailed
        evaluation should be performed.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Does cystectomy reduce ovarian reserve?
      </h3>
      <p>
        Surgical technique is decisive. In experienced hands, limited use of cautery during
        cystectomy keeps the loss of ovarian reserve to a minimum. A slight decrease in AMH (ovarian
        reserve) may be seen after endometrioma surgery; individualised decision-making is therefore
        essential in patients wishing to conceive.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Does the contraceptive pill dissolve a cyst?
      </h3>
      <p>
        It does not dissolve an existing cyst, but it can prevent the formation of new functional
        cysts. Existing functional cysts usually resolve on their own.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        How long does recovery take after surgery?
      </h3>
      <p>
        After laparoscopic cystectomy, most patients stay one night in hospital and return to daily
        activities within about one week. Heavy physical activity can usually be resumed in 2–4
        weeks. Open surgery may require 4–6 weeks of recovery.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Ovarian cyst evaluation and laparoscopic surgery in Adana
      </h2>
      <p>
        When an ovarian cyst is diagnosed, the priorities are{" "}
        <strong>accurate classification</strong> and <strong>individualised decision-making</strong>
        : the same cyst may require completely different management depending on the patient's age,
        reproductive plans, and symptom profile. For ovarian cyst evaluation, laparoscopic
        gynaecologic surgery, and endometriosis treatment in Adana, you can consult Op. Dr. Sadık
        Kükrer.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 7: Vaginal Discharge ---------------- */
function VaginalDischargePost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Vaginal discharge</strong> is a natural part of the female reproductive system, and
        in the great majority of cases it is entirely normal. The vagina and cervix produce daily
        secretions that cleanse the tract, maintain moisture balance, and form a protective barrier
        against infection. However, when the colour, odour, amount, or consistency of the discharge
        changes — or when itching, burning, or pain is present — an underlying{" "}
        <strong>infection or hormonal disturbance</strong> may be responsible.
      </p>

      <p>
        This article reviews the features of normal vaginal discharge, which findings warrant a
        medical consultation, the most common infectious causes (yeast, bacterial vaginosis,
        trichomoniasis), diagnostic methods, and treatment options.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Characteristics of normal vaginal discharge
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Colour:</strong> clear, milky-white, or pale cream; may appear slightly yellowish
          once dry.
        </li>
        <li>
          <strong>Odour:</strong> odourless or very mild and inoffensive.
        </li>
        <li>
          <strong>Consistency:</strong> varies with the menstrual cycle; clear and stretchy
          (egg-white–like) around ovulation, thicker and creamier at other times.
        </li>
        <li>
          <strong>Amount:</strong> variable between women; approximately 1–4 mL per day is normal.
        </li>
        <li>
          <strong>No accompanying symptoms:</strong> no itching, burning, redness, pain, or
          discomfort during intercourse.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Physiological factors that increase discharge
      </h2>
      <p>An increase in discharge is normal in the following situations:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ovulation:</strong> discharge becomes more abundant, clearer, and takes on the
          egg-white consistency mid-cycle.
        </li>
        <li>
          <strong>Sexual arousal:</strong> increased lubricating secretion.
        </li>
        <li>
          <strong>Pregnancy:</strong> higher oestrogen and blood flow lead to a marked increase in
          discharge (<em>leucorrhoea</em>). White, odourless, and non-itchy discharge is normal.
        </li>
        <li>
          <strong>Hormonal contraceptives:</strong> may influence the amount of discharge.
        </li>
        <li>
          <strong>Stress and fatigue:</strong> can affect hormonal balance and therefore discharge.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Abnormal discharge: when to be concerned
      </h2>
      <p>
        The following findings suggest that discharge is not normal and require medical evaluation:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Yellow, green, grey, or frothy discharge</li>
        <li>Foul, fishy, or yeasty odour</li>
        <li>Cottage-cheese–like, cheesy discharge</li>
        <li>Itching, burning, redness, or swelling</li>
        <li>Pain on urination or during intercourse</li>
        <li>Bloody or pink discharge outside menstruation</li>
        <li>Lower abdominal pain or fever</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Most common causes</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Yeast infection (vulvovaginal candidiasis)
      </h3>
      <p>
        Vaginal yeast infection is most often caused by overgrowth of <em>Candida albicans</em>.
        Approximately 75% of women experience at least one episode during their lifetime.
      </p>
      <p>
        <strong>Typical findings:</strong>
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>White, cottage-cheese–like discharge</li>
        <li>Notably little odour (or a mild yeasty smell)</li>
        <li>Intense itching, burning, and redness</li>
        <li>Burning on urination and during intercourse</li>
      </ul>
      <p>
        <strong>Risk factors:</strong> antibiotic use, pregnancy, diabetes, immunosuppression, tight
        or non-breathable underwear, and prolonged wear of wet swimwear or workout clothing.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Bacterial vaginosis (BV)</h3>
      <p>
        Bacterial vaginosis develops when the natural balance of vaginal bacteria is disrupted —{" "}
        <em>lactobacilli</em> decrease and anaerobic bacteria overgrow. It is not classically a
        sexually transmitted infection, but sexual activity increases its frequency.
      </p>
      <p>
        <strong>Typical findings:</strong>
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Grey-white, thin, watery discharge</li>
        <li>A prominent fishy odour (particularly after intercourse and during menstruation)</li>
        <li>Itching and burning are usually mild or absent</li>
      </ul>
      <p>
        Untreated BV can increase the risk of preterm birth, low birth weight, and pelvic
        inflammatory disease in pregnancy.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Trichomoniasis</h3>
      <p>
        A sexually transmitted infection caused by the parasite <em>Trichomonas vaginalis</em>.
      </p>
      <p>
        <strong>Typical findings:</strong>
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Yellow-green, frothy, profuse discharge</li>
        <li>Foul-smelling discharge</li>
        <li>Intense itching, burning, and redness</li>
        <li>Pain on urination and discomfort during intercourse</li>
        <li>Some women remain asymptomatic</li>
      </ul>
      <p>Concurrent treatment of the partner is mandatory.</p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        4. Cervicitis (inflammation of the cervix)
      </h3>
      <p>
        Sexually transmitted infections such as chlamydia and gonorrhoea can cause cervicitis.
        Findings include yellow-green, foul-smelling discharge — sometimes blood-tinged — postcoital
        spotting, and lower abdominal pain. Untreated cervicitis can spread to the fallopian tubes,
        causing <strong>tubal obstruction and infertility</strong>.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Atrophic vaginitis (postmenopausal)
      </h3>
      <p>
        Postmenopausal oestrogen decline thins the vaginal mucosa and increases dryness and
        sensitivity. Watery, yellow, or blood-tinged discharge, burning, and pain during intercourse
        may occur. Local oestrogen therapy usually provides substantial relief.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Foreign body</h3>
      <p>
        A retained tampon, condom fragment, or other foreign body can cause a foul-smelling
        brown-greenish discharge. It is quickly identified on examination.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Quick guide by colour and odour</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Clear / white, odourless:</strong> generally normal.
        </li>
        <li>
          <strong>Cottage-cheese–like, itchy:</strong> yeast infection.
        </li>
        <li>
          <strong>Grey, fishy odour:</strong> bacterial vaginosis.
        </li>
        <li>
          <strong>Yellow-green, frothy, foul-smelling:</strong> trichomoniasis or cervicitis.
        </li>
        <li>
          <strong>Brown:</strong> usually old blood — normal at the start or end of menstruation;
          otherwise needs evaluation.
        </li>
        <li>
          <strong>Pink / bloody:</strong> outside menstruation, always consult a physician.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">How is the diagnosis made?</h2>
      <p>
        The examination begins with inspection of the external genital area and vagina, followed by
        cervical visualisation with a speculum. A sample of the discharge can be examined with the
        following tests:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Wet mount microscopy:</strong> may show fungal hyphae, <em>clue cells</em> (BV),
          or motile trichomonads.
        </li>
        <li>
          <strong>pH measurement:</strong> normal vaginal pH is 3.8–4.5. BV and trichomoniasis raise
          the pH above 4.5.
        </li>
        <li>
          <strong>Whiff (amine) test:</strong> in BV, adding KOH releases a sharp fishy odour.
        </li>
        <li>
          <strong>Culture or PCR:</strong> when resistant yeast, chlamydia, or gonorrhoea is
          suspected.
        </li>
        <li>
          <strong>Cervical smear / HPV testing:</strong> part of the assessment when chronic
          discharge or spotting is present.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">Yeast infection</h3>
      <p>
        Treated with a single dose or short course of oral antifungals, or with topical vaginal
        pessaries or creams. Recurrent infections may require longer suppressive therapy.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Bacterial vaginosis</h3>
      <p>
        Metronidazole or clindamycin — oral tablets or a vaginal gel or cream. Alcohol should be
        avoided during the course of treatment.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Trichomoniasis</h3>
      <p>
        A single dose or 7-day course of oral metronidazole or tinidazole. Concurrent partner
        treatment and abstinence from unprotected intercourse until treatment is complete are
        essential.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Chlamydia / gonorrhoea</h3>
      <p>
        Appropriate antibiotics (e.g. azithromycin, doxycycline, or ceftriaxone) are prescribed.
        Partner treatment and screening for other sexually transmitted infections (HIV, syphilis,
        hepatitis) are recommended.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Tips for vaginal health</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Do not douche:</strong> it disrupts the natural flora and increases the risk of BV
          and yeast infection.
        </li>
        <li>
          <strong>Avoid perfumed products:</strong> scented soaps, wet wipes, pads, and tampons are
          irritating.
        </li>
        <li>
          Prefer <strong>cotton underwear</strong>; avoid tight, synthetic clothing.
        </li>
        <li>
          Do not remain in <strong>wet swimwear or workout clothing</strong> for long periods.
        </li>
        <li>
          Wipe <strong>from front to back</strong> after using the toilet.
        </li>
        <li>Change pads or tampons every 4–6 hours during menstruation.</li>
        <li>
          Avoid unprotected intercourse; condoms provide the most effective protection against
          sexually transmitted infections.
        </li>
        <li>
          After antibiotic use, a probiotic can be considered on your doctor's advice to help
          restore vaginal balance.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Frequently asked questions</h2>

      <h3 className="mt-6 font-display text-lg font-semibold">
        Does every infection cause itching?
      </h3>
      <p>
        No. Itching is prominent in yeast infection but is often absent in bacterial vaginosis. For
        this reason, self-diagnosis based on symptoms alone and treating with over-the-counter
        products can be misleading.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        Should I worry about increased discharge during pregnancy?
      </h3>
      <p>
        Increased discharge is normal in pregnancy. However, foul-smelling, yellow-green, itchy
        discharge or <strong>watery or bloody discharge</strong> must always be evaluated. A gush of
        watery fluid may indicate rupture of the membranes and preterm labour.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        If it is not sexually transmitted, why should my partner be treated?
      </h3>
      <p>
        Yeast infection and bacterial vaginosis are not classical STIs and partner treatment is
        usually unnecessary. However, in trichomoniasis, chlamydia, and gonorrhoea, concurrent
        partner treatment is mandatory — otherwise the infection will recur.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">
        I have recurrent yeast infections — what should I do?
      </h3>
      <p>
        Four or more episodes per year is defined as <em>recurrent vulvovaginal candidiasis</em>.
        Assessment for underlying diabetes, immunosuppression, and resistant species may be
        required, along with prolonged suppressive therapy.
      </p>

      <h3 className="mt-6 font-display text-lg font-semibold">Isn't douching good for hygiene?</h3>
      <p>
        No. The vagina is self-cleaning. Douching disrupts the natural flora and increases the risk
        of infection; it has also been associated with pelvic inflammatory disease and ectopic
        pregnancy. Warm water and, if desired, pH-balanced, unscented cleansers on the external
        genitalia are sufficient.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Assessment in Adana</h2>
      <p>
        Most women presenting with vaginal discharge are managed with{" "}
        <strong>a simple examination and a short course of treatment</strong>. Recurrent infections,
        atypical discharge, pregnancy-related changes, and suspicion of a sexually transmitted
        infection should always be evaluated by an obstetrician-gynaecologist. For diagnosis and
        treatment of vaginal discharge, recurrent yeast or bacterial vaginosis, and sexually
        transmitted infections in Adana, you can consult Op. Dr. Sadık Kükrer.
      </p>
    </ArticleShell>
  );
}

/* ---------------- Post 8: Endometriosis ---------------- */
function EndometriosisPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Endometriosis is an oestrogen-dependent, chronic condition that affects roughly 1 in 10
        women of reproductive age. Tissue similar to the uterine lining (endometrium) implants
        outside the uterus — on the ovaries, fallopian tubes, peritoneum, bowel, or, rarely, in more
        distant sites. Like the endometrium, this ectopic tissue bleeds with every menstrual cycle;
        because the blood cannot leave the body, chronic inflammation, adhesions, and pain develop.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">What is endometriosis?</h2>
      <p>
        Endometriosis is the presence — outside the uterus — of glandular and stromal tissue that is
        histologically similar to the uterine lining. The ovaries are the most commonly involved
        site; dark-brown cysts that develop within the ovary are called{" "}
        <strong>endometrioma</strong> or, colloquially, "chocolate cysts". Superficial peritoneal
        lesions, deep infiltrating disease between the uterus and bowel, and adhesions are other
        manifestations.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Causes and risk factors</h2>
      <p>
        The exact cause is unknown, but several mechanisms are thought to contribute: retrograde
        menstruation delivering endometrial cells into the peritoneal cavity, failure of the immune
        system to clear these cells, hormonal factors, and genetic predisposition. The main risk
        factors are:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Family history of endometriosis (mother or sister)</li>
        <li>Early menarche (before age 12), late menopause</li>
        <li>Short (fewer than 27 days) or long menstrual cycles</li>
        <li>Nulliparity or first pregnancy at an older age</li>
        <li>Low body mass index</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms of endometriosis</h2>
      <p>
        Symptom severity does not always correlate with disease extent: small lesions can cause
        severe pain, while large cysts may be silent. The most common presentations are:
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Severe menstrual pain (dysmenorrhoea)
      </h3>
      <p>
        Cramping pain that starts 1–2 days before menstruation, is severe enough in the first days
        to interfere with work or school, responds only partially to analgesics, and worsens over
        the years. What sets it apart from ordinary dysmenorrhoea is its progression over time and
        its impact on daily life.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Chronic pelvic pain</h3>
      <p>
        Pelvic pain unrelated to menstruation, lasting at least six months. It may radiate to the
        lower back and hip and can worsen with prolonged standing and physical activity.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Painful intercourse (dyspareunia)
      </h3>
      <p>
        Pain during deep penetration, persisting for several hours after intercourse, is a classic
        sign of endometriosis lesions in the posterior fornix and in the pouch of Douglas
        (cul-de-sac).
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Infertility</h3>
      <p>
        About 30–50% of women with endometriosis experience difficulty conceiving. Reduced ovarian
        reserve, tubal adhesions, chronic pelvic inflammation, and impaired embryo implantation are
        the principal underlying mechanisms.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Bowel and urinary symptoms</h3>
      <p>
        Painful bowel movements during menstruation, diarrhoea or constipation, rectal pressure,
        painful urination, or blood in the urine (particularly during menstruation) may indicate
        bowel or bladder involvement.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Other symptoms</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Chronic fatigue and low energy</li>
        <li>Nausea and headache during menstruation</li>
        <li>Intermenstrual spotting</li>
        <li>Abdominal bloating ("endo belly")</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Stages</h2>
      <p>
        The American Society for Reproductive Medicine (ASRM) classification defines four stages.
        The stage reflects the extent of disease but does not always correlate with pain severity:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Stage 1 (minimal):</strong> isolated superficial lesions.
        </li>
        <li>
          <strong>Stage 2 (mild):</strong> more superficial lesions and mild adhesions.
        </li>
        <li>
          <strong>Stage 3 (moderate):</strong> endometrioma (chocolate cyst) and more prominent
          adhesions.
        </li>
        <li>
          <strong>Stage 4 (severe):</strong> large endometrioma, extensive deep infiltrating
          disease, and adhesions between the uterus and bowel.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">How is the diagnosis made?</h2>
      <p>
        The diagnosis is based on clinical assessment, imaging, and — when required — surgical
        confirmation. The interval from initial suspicion to definitive diagnosis varies between
        women; early evaluation is important.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Detailed history and examination:</strong> pattern of pain, menstrual history,
          family history, and pelvic examination for tenderness and nodularity.
        </li>
        <li>
          <strong>Transvaginal ultrasound:</strong> endometrioma (chocolate cyst), deep infiltrating
          lesions, and uterine–bowel adhesions can be assessed with a high degree of accuracy.
        </li>
        <li>
          <strong>MRI:</strong> provides detailed mapping of bowel, bladder, and deep infiltrating
          disease and guides surgical planning.
        </li>
        <li>
          <strong>CA-125:</strong> may be elevated in some cases; not diagnostic on its own but
          useful for follow-up.
        </li>
        <li>
          <strong>Laparoscopy:</strong> the gold standard for definitive diagnosis. Small abdominal
          ports allow both visualisation and treatment in the same session, with biopsy
          confirmation.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment options</h2>
      <p>
        Treatment is individualised on the basis of age, symptom severity, stage of disease,
        reproductive plans, and ovarian reserve. There is no single "best treatment"; the aims are
        to control pain, halt disease progression, and preserve fertility.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Medical therapy (pain management)
      </h3>
      <p>
        Non-steroidal anti-inflammatory drugs (NSAIDs) are the first-line option for mild to
        moderate pain. Regular use, started 1–2 days before menstruation and continued through the
        period, is more effective than intermittent dosing.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Hormonal therapy</h3>
      <p>
        By suppressing ovulation and menstruation, hormonal therapy prevents bleeding and growth of
        endometriosis lesions. Options include:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Combined oral contraceptives (especially continuous use)</li>
        <li>Progestin-dominant tablets (dienogest, norethisterone acetate)</li>
        <li>The levonorgestrel-releasing intrauterine device</li>
        <li>
          GnRH agonists or antagonists — these induce a temporary menopause-like state; usually used
          for 3–6 months with add-back therapy
        </li>
      </ul>
      <p>
        Hormonal therapy does not cure the disease; symptoms may return after treatment is stopped.
        It is not suitable for patients who wish to conceive.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Laparoscopic surgery</h3>
      <p>
        Surgery is considered when pain is refractory to medical therapy, for endometriomas 4 cm or
        larger, when imaging findings are suspicious, when the fallopian tubes are blocked, or as
        part of infertility evaluation. Laparoscopy (keyhole surgery) is the current gold standard:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>3–4 small incisions (0.5–1 cm) in the abdominal wall; scarring is minimal.</li>
        <li>Endometrioma is removed together with the cyst wall; ovarian tissue is preserved.</li>
        <li>Superficial lesions are treated with cautery or laser, or excised.</li>
        <li>Adhesions are released and tubal patency is assessed.</li>
        <li>Recovery is short (usually about one week); hospital stay is typically one night.</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Fertility treatment</h3>
      <p>
        In women trying to conceive with endometriosis-related infertility, management depends on
        stage and age. In mild disease, spontaneous conception is more likely after laparoscopy; in
        advanced disease or when ovarian reserve is low, IVF may be planned directly. Careful
        preoperative planning is important to preserve ovarian reserve.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Lifestyle and supportive measures
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Regular physical activity and pelvic floor exercises can reduce chronic pain.</li>
        <li>
          An anti-inflammatory diet (rich in omega-3, low in processed foods and trans fats) may
          ease symptoms.
        </li>
        <li>Warm compresses and pelvic relaxation techniques help with menstrual pain.</li>
        <li>
          Chronic pain can have a psychosocial impact; do not hesitate to seek specialist support.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek immediate care</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Sudden severe one-sided pelvic pain (possible endometrioma rupture or torsion)</li>
        <li>High fever with pelvic pain</li>
        <li>Heavy intermenstrual bleeding, fainting</li>
        <li>Pain unresponsive to analgesics and interfering with daily life</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Endometriosis treatment in Adana
      </h2>
      <p>
        With timely diagnosis and experienced laparoscopic surgery, endometriosis is a condition in
        which quality of life can be substantially improved. Op. Dr. Sadık Kükrer offers
        individualised care in Adana for endometriosis — from diagnosis to laparoscopic surgery —
        with the aims of controlling pain and preserving fertility. If you experience severe
        menstrual pain, painful intercourse, or difficulty conceiving, you can contact the clinic
        for an assessment.
      </p>
    </ArticleShell>
  );
}

function UterineFibroidsPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Uterine fibroids — also called myomas or leiomyomas — are benign tumours arising from the
        smooth-muscle layer of the uterus. They affect approximately 70% of women of reproductive
        age and are even more prevalent by age 50. Fibroids range in size from a few millimetres to
        more than 20 centimetres, and may be solitary or multiple. A substantial proportion remain
        entirely asymptomatic and are discovered incidentally during routine examination.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">What are uterine fibroids?</h2>
      <p>
        Fibroids are benign smooth-muscle tumours that develop from the myometrium, the muscular
        wall of the uterus. They are hormone- responsive, growing under the influence of oestrogen
        and progesterone. After menopause, when hormone levels decline, fibroids typically shrink.
        Malignant transformation (leiomyosarcoma) is exceedingly rare, occurring in fewer than 0.1%
        of cases.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Types of fibroids (by location)</h2>
      <p>Symptoms depend more on the location of a fibroid within the uterus than on its size:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Submucosal fibroid:</strong> Projects into the uterine cavity. Even small
          submucosal fibroids can cause heavy bleeding and infertility.
        </li>
        <li>
          <strong>Intramural fibroid:</strong> Located within the muscular wall of the uterus. This
          is the most common type.
        </li>
        <li>
          <strong>Subserosal fibroid:</strong> Grows outward from the outer surface of the uterus
          and can exert pressure on neighbouring organs.
        </li>
        <li>
          <strong>Pedunculated fibroid:</strong> Attached to the uterus by a stalk. Torsion
          (twisting) around the stalk may cause sudden severe pain.
        </li>
        <li>
          <strong>Cervical fibroid:</strong> A rare variant located in the cervix.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Causes and risk factors</h2>
      <p>
        The precise cause of fibroids remains unknown. Hormonal, genetic, and growth-factor
        influences are thought to act together. Principal risk factors include:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Age over 30 (risk increases until menopause)</li>
        <li>Family history of fibroids (mother or sister)</li>
        <li>Early menarche (first period before age 12)</li>
        <li>Nulliparity (never having given birth)</li>
        <li>Obesity and hypertension</li>
        <li>Alcohol consumption and vitamin D deficiency</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms of uterine fibroids</h2>
      <p>
        Approximately half of all fibroids are asymptomatic. When symptoms occur, the most common
        presentations are as follows.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Heavy and prolonged menstrual bleeding
      </h3>
      <p>
        The most common reason for consultation. Menstrual periods lasting longer than seven days,
        passage of clots, the need to change sanitary protection hourly, and bleeding severe enough
        to cause iron-deficiency anaemia are typical. Submucosal fibroids are particularly
        associated with this pattern.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Pelvic pain and pressure</h3>
      <p>
        Large fibroids may cause a sensation of fullness, pressure, and dull pain in the lower
        abdomen. Torsion of a pedunculated fibroid can precipitate sudden severe pain requiring
        urgent evaluation.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Urinary frequency or constipation
      </h3>
      <p>
        Anteriorly located fibroids compressing the bladder can cause urinary frequency and a sense
        of incomplete emptying, while posterior fibroids pressing on the bowel may lead to
        constipation and rectal pressure.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        4. Dyspareunia and lower back pain
      </h3>
      <p>
        Fibroids close to the cervix or in deep locations may cause pain during intercourse, while
        very large fibroids can produce lower back pain that radiates to the hips.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Infertility and recurrent pregnancy loss
      </h3>
      <p>
        Submucosal fibroids distorting the uterine cavity, and large intramural fibroids, may impair
        embryo implantation. Fibroids can also increase the risk of preterm birth, placental
        abnormalities, and caesarean delivery.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        6. Abdominal enlargement and bloating
      </h3>
      <p>
        Very large fibroids may produce visible abdominal distension, a palpable mass, and bloating
        that can resemble pregnancy.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">How is the diagnosis made?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Pelvic examination:</strong> An enlarged, irregularly contoured uterus may be
          palpable.
        </li>
        <li>
          <strong>Transvaginal ultrasound:</strong> The first-line investigation for determining the
          number, size, and location of fibroids, with high diagnostic accuracy.
        </li>
        <li>
          <strong>Saline infusion sonohysterography (SIS):</strong> Ultrasound performed after
          instillation of saline into the uterine cavity; particularly valuable for characterising
          submucosal fibroids.
        </li>
        <li>
          <strong>Hysteroscopy:</strong> Direct visualisation of the uterine cavity with a thin
          camera, offering both diagnostic and therapeutic capability.
        </li>
        <li>
          <strong>MRI:</strong> Used for surgical mapping in cases with numerous or very large
          fibroids and to differentiate from adenomyosis.
        </li>
        <li>
          <strong>Full blood count:</strong> Detects iron-deficiency anaemia secondary to heavy
          bleeding.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment options</h2>
      <p>
        Treatment is individualised according to age, pregnancy plans, severity of symptoms, and the
        number, size, and location of fibroids. Asymptomatic small fibroids are usually managed with
        regular surveillance.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Surveillance</h3>
      <p>
        Asymptomatic fibroids under 4–5 cm are monitored with ultrasound every 6–12 months.
        Treatment is initiated if rapid growth or new symptoms develop.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Medical therapy</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>NSAIDs and tranexamic acid:</strong> Reduce menstrual blood loss and ease pain.
        </li>
        <li>
          <strong>Combined oral contraceptives:</strong> Can regulate bleeding patterns but do not
          shrink fibroids.
        </li>
        <li>
          <strong>Levonorgestrel-releasing IUD (LNG-IUS):</strong> Significantly reduces heavy
          bleeding in patients with a suitable uterine cavity.
        </li>
        <li>
          <strong>GnRH agonists/antagonists:</strong> Produce temporary fibroid shrinkage; used for
          pre-operative preparation or in women close to menopause. Long-term use requires add-back
          therapy.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Hysteroscopic myomectomy</h3>
      <p>
        The gold standard for removing submucosal fibroids. It is performed through the cervix,
        without any abdominal incision. Same-day discharge and rapid recovery are usual, and
        fertility outcomes are favourable.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Laparoscopic myomectomy</h3>
      <p>
        The preferred minimally invasive approach for intramural and subserosal fibroids. Three or
        four small incisions (0.5–1 cm) are made in the abdominal wall and fibroids are removed
        while the uterus is preserved. Advantages include:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Less pain and reduced blood loss</li>
        <li>Short hospital stay (usually one night)</li>
        <li>Rapid recovery and minimal scarring</li>
        <li>Fertility preservation for women planning pregnancy</li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Open (abdominal) myomectomy</h3>
      <p>
        Open surgery may be required for numerous, very large, or deeply located fibroids. The
        uterus is preserved, making this option appropriate for women who wish to conceive.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">6. Hysterectomy</h3>
      <p>
        The definitive solution for women who have completed childbearing and do not respond to
        medication or myomectomy, particularly those with multiple large fibroids. It can be
        performed laparoscopically or vaginally and eliminates the possibility of recurrence.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        7. Uterine artery embolisation (UAE)
      </h3>
      <p>
        A minimally invasive interventional radiology procedure that blocks the blood supply to
        fibroids. It is an alternative for patients unsuitable for or wishing to avoid surgery; it
        is generally not recommended for women planning pregnancy.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Fibroids and pregnancy</h2>
      <p>
        Most women with fibroids conceive and deliver without complications. However, depending on
        fibroid location and size, the following risks may be increased: miscarriage, preterm birth,
        abnormal placentation, obstructed labour, and caesarean delivery. Pre-conception myomectomy
        may be considered for women with submucosal or large intramural fibroids who are planning
        pregnancy.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek urgent care</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Sudden severe pelvic pain (which may indicate torsion of a pedunculated fibroid)</li>
        <li>Heavy bleeding accompanied by faintness or weakness</li>
        <li>Fever, foul-smelling discharge, and pain</li>
        <li>Rapidly enlarging abdomen or urinary retention</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Fibroid care in Adana</h2>
      <p>
        The goal of fibroid treatment is not merely to remove the fibroid, but to select the
        approach that aligns with each patient's age, pregnancy plans, and quality-of-life goals.
        Op. Dr. Sadık Kükrer offers individualised, uterus-sparing care in Adana for uterine
        fibroids, drawing on extensive experience in hysteroscopic and laparoscopic surgery. If you
        are experiencing heavy menstrual bleeding, pelvic pain, or difficulty conceiving, you are
        welcome to arrange an assessment.
      </p>
    </ArticleShell>
  );
}

function HpvVaccinePost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        The Human Papillomavirus (HPV) is the most common sexually transmitted infection worldwide.
        More than 80% of sexually active women and men will encounter HPV at some point in their
        lives. With over 200 subtypes, the virus is the leading cause of cervical cancer and is also
        responsible for a range of other cancers and genital warts. The HPV vaccine is one of the
        most effective preventive tools in modern medicine, making the majority of these diseases{" "}
        <strong>preventable</strong>.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        What is HPV and what does it do?
      </h2>
      <p>
        HPV is a DNA virus that infects skin and mucosal cells. Around 90% of infections are cleared
        by the immune system within two years. However, persistent infection with{" "}
        <strong>high-risk</strong> types — particularly HPV 16 and 18 — can lead to cellular changes
        (dysplasia) and, over time, to cancer. Low-risk types (HPV 6 and 11) cause genital warts.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Which cancers does HPV cause?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Cervical cancer:</strong> Approximately 99% of cases are HPV-related; it is the
          fourth most common cancer in women globally.
        </li>
        <li>
          <strong>Vulvar and vaginal cancers:</strong> Around 70% of cases are HPV-related.
        </li>
        <li>
          <strong>Anal cancer:</strong> Around 90% of cases in both men and women are caused by HPV.
        </li>
        <li>
          <strong>Penile cancer:</strong> Around 60% of cases are linked to HPV.
        </li>
        <li>
          <strong>Oropharyngeal (head and neck) cancers:</strong> HPV is detected in approximately
          70% of tonsil and base-of-tongue cancers, with rising incidence in men.
        </li>
        <li>
          <strong>Genital warts (condylomata):</strong> Over 90% of cases are caused by HPV types 6
          and 11.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Types of HPV vaccine</h2>
      <p>
        The current standard vaccine used worldwide is <strong>Gardasil 9</strong>, which protects
        against nine HPV types: 6, 11, 16, 18, 31, 33, 45, 52 and 58. Together these account for
        approximately 90% of cervical cancer cases and more than 90% of genital warts.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Who should be vaccinated?</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">1. Ages 9–14: the ideal window</h3>
      <p>
        The vaccine is most effective when administered before sexual activity begins. Immune
        response is strongest in this age group and <strong>only 2 doses</strong> (0 and 6–12
        months) are required. Current WHO evidence suggests a single-dose schedule may also provide
        adequate protection in this age band.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        2. Ages 15–26: routine recommendation
      </h3>
      <p>
        The vaccine is recommended even after sexual activity has started. A <strong>3-dose</strong>{" "}
        schedule (0, 2 and 6 months) is used. Prior exposure to one or two HPV types does not
        eliminate the benefit — the vaccine still protects against the remaining types.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Ages 27–45: an individual decision
      </h3>
      <p>
        In 2018, the FDA extended vaccine approval up to age 45. In situations such as a new
        partner, a change in relationship status, or a normal HPV-negative smear, the vaccine may be
        offered after a shared risk–benefit discussion with your clinician.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. HPV vaccination for men</h3>
      <p>
        HPV is not a women's disease alone. Vaccination protects men against anal, penile and
        oropharyngeal cancers, and reduces the risk of transmitting the virus to their partners.
        Vaccination is recommended for boys and young men between the ages of 9 and 26.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Dosing schedule at a glance</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ages 9–14:</strong> 2 doses — 0 and 6–12 months
        </li>
        <li>
          <strong>Age 15 and above:</strong> 3 doses — 0, 2 and 6 months
        </li>
        <li>
          <strong>Immunocompromised individuals (HIV, transplant recipients, etc.):</strong> 3 doses
          regardless of age
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Is the vaccine safe? Side effects
      </h2>
      <p>
        The HPV vaccine has been in use in over 100 countries since 2006 and is endorsed as safe by
        the WHO, FDA and EMA. The most common side effects are:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Pain, redness or swelling at the injection site (approx. 80%)</li>
        <li>Mild fever and headache (10–15%)</li>
        <li>Transient fatigue</li>
        <li>Nausea (rare)</li>
      </ul>
      <p>
        Vasovagal (fainting) reactions can occasionally occur within the first 15 minutes,
        particularly in adolescents, so a 15-minute observation period after vaccination is
        recommended. The vaccine has no established link with infertility, autoimmune disease or
        cancer; such claims have been refuted by extensive scientific studies.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Pregnancy and breastfeeding</h2>
      <p>
        The HPV vaccine is not recommended during pregnancy; the schedule is postponed until after
        delivery. Inadvertent administration during pregnancy is not an indication for termination.
        The vaccine is considered <strong>safe</strong> during breastfeeding.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Do vaccinated women still need cervical screening?
      </h2>
      <p>
        <strong>Yes.</strong> Gardasil 9 protects against nine HPV types but does not cover every
        oncogenic type (approximately 10% of cervical cancers are caused by other types). Regular
        cervical screening with Pap smear and HPV DNA testing should therefore continue from age
        21–25 onwards, even in vaccinated women.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Frequently asked practical questions
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Does the vaccine treat existing HPV?</strong> No. The vaccine is preventive, not
          therapeutic.
        </li>
        <li>
          <strong>Are there sexual-activity restrictions after vaccination?</strong> No restrictions
          are required.
        </li>
        <li>
          <strong>Can postmenopausal women be vaccinated?</strong> Routine vaccination is not
          recommended above 45, but may be considered in selected cases after clinician review.
        </li>
        <li>
          <strong>Is the vaccine free?</strong> Availability and cost vary by country. In many
          nations, the vaccine is offered free of charge to adolescents aged 9–14 as part of
          national immunisation programmes.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        HPV vaccination and counselling in Adana
      </h2>
      <p>
        The HPV vaccine is one of the most valuable investments you can make in the long-term
        gynaecologic health of yourself, your daughter or your son. Op. Dr. Sadık Kükrer offers
        individualised consultation in Adana on HPV vaccination, Pap smear and HPV DNA screening,
        and the treatment of cervical dysplasia (colposcopy, LEEP/conisation). International
        patients are welcome to arrange an appointment for age-appropriate vaccination decisions,
        dosing schedules or interpretation of HPV test results.
      </p>
    </ArticleShell>
  );
}

function PcosPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Polycystic Ovary Syndrome (PCOS) is one of the most common hormonal and metabolic disorders
        in women of reproductive age, affecting approximately 8–13% of this population worldwide.
        Beyond its visible features — irregular periods, hirsutism, acne and weight gain — PCOS also
        increases the long-term risk of insulin resistance, type 2 diabetes and cardiovascular
        disease. With accurate diagnosis and an individualised treatment plan, symptoms can be
        controlled and long-term risks substantially reduced.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        PCOS versus PCOM: an important distinction
      </h2>
      <p>
        Recent guidelines — most notably the 2023 International PCOS Guideline (Teede et al.) — have
        clarified the difference between two often-confused concepts:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>PCOM (Polycystic Ovarian Morphology):</strong> A finding on ultrasound of multiple
          small follicles (≥20 per ovary) or an increased ovarian volume (&gt;10 mL). It is only an
          imaging <strong>sign</strong>. Around 20–30% of healthy women with regular menstrual
          cycles can display PCOM.
        </li>
        <li>
          <strong>PCOS (Polycystic Ovary Syndrome):</strong> A clinical <strong>syndrome</strong>{" "}
          that requires the presence of additional clinical criteria such as ovulatory dysfunction
          or hyperandrogenism.
        </li>
      </ul>
      <p>
        In other words, a "polycystic-appearing ovary" is not the same as "polycystic ovary
        syndrome". PCOM is only one of the possible criteria for PCOS. The 2023 guideline
        additionally allows <strong>anti-Müllerian hormone (AMH)</strong> to replace ultrasound in
        adults, and permits diagnosis without ultrasound in adolescents when regular cycles and
        clear hyperandrogenism are both present.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">What causes PCOS?</h2>
      <p>
        There is no single cause. Genetic predisposition, insulin resistance, low-grade inflammation
        and increased ovarian androgen production interact to produce the syndrome. A family history
        of PCOS, type 2 diabetes or premature cardiovascular disease increases risk. Obesity worsens
        the clinical picture, but PCOS also occurs in lean women (approximately 20% of cases).
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms of PCOS</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Menstrual irregularity and anovulation
      </h3>
      <p>
        The most common presenting complaint. Cycles may be longer than 35 days (oligomenorrhoea),
        fewer than eight cycles per year, or absent altogether (amenorrhoea). Because ovulation is
        impaired, spontaneous conception is more difficult.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Signs of hyperandrogenism</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Hirsutism:</strong> Male-pattern hair growth on the face, chin, chest, abdomen and
          back.
        </li>
        <li>
          <strong>Acne:</strong> Persistent adult-onset or post-adolescent acne, typically along the
          jawline, chin and back.
        </li>
        <li>
          <strong>Androgenic alopecia:</strong> Thinning of the hair along the frontal hairline and
          crown.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        3. Weight gain and features of insulin resistance
      </h3>
      <p>
        Central (abdominal) weight gain, difficulty losing weight, post-meal drowsiness and
        cravings, and dark, velvety skin patches on the neck or axillae (acanthosis nigricans) are
        clinical markers of insulin resistance, present in around 70% of women with PCOS.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Infertility</h3>
      <p>
        PCOS is the most common cause of anovulatory infertility. However, PCOS does not equal
        infertility — with appropriate treatment the majority of women can conceive.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">5. Psychological symptoms</h3>
      <p>
        Depression, anxiety and disordered eating are 2–3 times more frequent in women with PCOS
        than in the general population and should be assessed as part of care.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Diagnosis: the Rotterdam criteria
      </h2>
      <p>
        A diagnosis of PCOS requires at least <strong>two of the following three</strong> criteria,
        after excluding other conditions that produce a similar picture:
      </p>
      <ol className="list-decimal space-y-2 pl-6">
        <li>
          <strong>Ovulatory dysfunction:</strong> Oligo- or anovulation, evidenced by irregular
          cycles or a low mid-luteal progesterone.
        </li>
        <li>
          <strong>Clinical or biochemical hyperandrogenism:</strong> Hirsutism or acne, or elevated
          serum free testosterone.
        </li>
        <li>
          <strong>PCOM or elevated AMH:</strong> Polycystic ovarian morphology on ultrasound, or an
          age-appropriate elevated AMH level (2023 guideline update).
        </li>
      </ol>

      <h3 className="mt-6 font-display text-xl font-semibold">Conditions that must be excluded</h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>Thyroid dysfunction (TSH)</li>
        <li>Hyperprolactinaemia (prolactin)</li>
        <li>Non-classic congenital adrenal hyperplasia (17-OH-progesterone)</li>
        <li>Cushing syndrome and androgen-secreting tumours (rare)</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Which investigations are needed?
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Hormonal profile: LH, FSH, oestradiol, prolactin, TSH, free testosterone, DHEAS,
          17-OH-progesterone
        </li>
        <li>AMH (particularly when ultrasound is inconclusive)</li>
        <li>Fasting glucose, insulin, HbA1c and, when indicated, oral glucose tolerance test</li>
        <li>Lipid profile (total cholesterol, LDL, HDL, triglycerides)</li>
        <li>Vitamin D and liver enzymes</li>
        <li>Transvaginal or abdominal pelvic ultrasound</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment of PCOS</h2>
      <p>
        Treatment is not a single prescription. It is individualised to the patient's{" "}
        <strong>goals</strong>: fertility, cycle regulation, control of skin and hair symptoms, and
        reduction of long-term metabolic risk.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Lifestyle modification: the foundation
      </h3>
      <p>
        In overweight women with PCOS, losing as little as <strong>5–10%</strong> of body weight can
        restore ovulation, reduce insulin resistance and improve hirsutism and acne. A
        Mediterranean-style diet, low-glycaemic carbohydrates, adequate protein and at least 150
        minutes per week of moderate-intensity exercise are the mainstay.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Combined oral contraceptives</h3>
      <p>
        First-line treatment for women not currently seeking pregnancy. They regulate the menstrual
        cycle, reduce androgen levels and markedly improve hirsutism and acne. They also reduce the
        risk of endometrial hyperplasia associated with chronic anovulation.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">3. Metformin</h3>
      <p>
        Used in women with insulin resistance, impaired glucose tolerance or high risk of type 2
        diabetes. It supports weight control and may improve cycle regularity. It is not first-line
        for ovulation induction alone but can be combined with letrozole in selected cases.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">4. Anti-androgen therapy</h3>
      <p>
        Spironolactone, cyproterone acetate or finasteride can be added for severe hirsutism and
        androgenic alopecia. These agents are teratogenic and must always be prescribed alongside
        effective contraception.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        5. Ovulation induction (for women seeking pregnancy)
      </h3>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Letrozole:</strong> Now the <strong>first-line</strong> agent for PCOS-related
          anovulatory infertility, with higher live-birth rates than clomiphene citrate.
        </li>
        <li>
          <strong>Clomiphene citrate:</strong> A long-established alternative.
        </li>
        <li>
          <strong>Gonadotropins:</strong> Low-dose injectable therapy for women who do not respond
          to oral agents; requires close follicular monitoring.
        </li>
        <li>
          <strong>In vitro fertilisation (IVF):</strong> Recommended when other approaches fail or
          when additional infertility factors coexist.
        </li>
        <li>
          <strong>Laparoscopic ovarian drilling:</strong> A surgical option in selected patients.
        </li>
      </ul>

      <h3 className="mt-6 font-display text-xl font-semibold">
        6. Local treatment of skin symptoms
      </h3>
      <p>
        Laser hair removal and electrolysis are effective adjuncts for hirsutism. Topical retinoids,
        benzoyl peroxide, and in resistant cases oral isotretinoin, are used for acne.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Long-term health risks in PCOS</h2>
      <p>
        PCOS is not confined to the reproductive years — it is a metabolic condition that persists
        after menopause. It is associated with:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Type 2 diabetes (3–7 times higher risk than the general population)</li>
        <li>Cardiovascular disease and hypertension</li>
        <li>Metabolic syndrome and non-alcoholic fatty liver disease</li>
        <li>Obstructive sleep apnoea</li>
        <li>Endometrial cancer (due to prolonged unopposed oestrogen exposure)</li>
        <li>Gestational diabetes and pre-eclampsia during pregnancy</li>
        <li>Depression and anxiety</li>
      </ul>
      <p>
        Regular monitoring of glucose, lipid profile, blood pressure and gynaecological health is
        therefore essential in every woman diagnosed with PCOS.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">PCOS and pregnancy</h2>
      <p>
        With the right treatment, most women with PCOS can conceive. Because the risks of
        gestational diabetes, pregnancy-induced hypertension and pre-eclampsia are elevated, closer
        antenatal surveillance is recommended. Pre-conception weight optimisation and folic acid
        supplementation are important.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        PCOS diagnosis and treatment in Adana
      </h2>
      <p>
        PCOS is not a diagnosis that can be made from a single laboratory value — it requires
        careful clinical assessment, ultrasound and integrated interpretation of hormonal results.
        Op. Dr. Sadık Kükrer offers individualised evaluation of menstrual irregularity, hirsutism,
        weight and insulin resistance, and fertility planning for women with PCOS in Adana.
        International patients are welcome to arrange a comprehensive assessment if symptoms persist
        or current treatment is not achieving the desired result.
      </p>
    </ArticleShell>
  );
}

function HysteroscopyPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Hysteroscopy</strong> is a gynaecological procedure that allows the uterine cavity
        to be visualised directly through a thin, illuminated camera called a hysteroscope. Whereas
        ultrasound and hysterosalpingography (HSG) provide only indirect information about the
        endometrial cavity, hysteroscopy offers a real-time, high-resolution view from within. Many
        intrauterine conditions can therefore be both <strong>diagnosed</strong> and{" "}
        <strong>treated</strong> in the same session.
      </p>

      <p>
        Advances in miniaturised optics over the past two decades have transformed hysteroscopy into
        a largely office-based procedure, performed with no anaesthesia or light sedation. It is
        today the gold standard for the evaluation of abnormal uterine bleeding, recurrent pregnancy
        loss, infertility and intrauterine adhesions.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Types of hysteroscopy</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">
        1. Diagnostic (office) hysteroscopy
      </h3>
      <p>
        The aim is to inspect the uterine cavity and, if indicated, to perform a targeted biopsy.
        Thin (2.7–3&nbsp;mm) hysteroscopes are used and cervical dilation is generally not required.
        The procedure is completed in 5–15 minutes without anaesthesia or with light sedation, and
        the patient can usually return to normal activities the same day.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">2. Operative hysteroscopy</h3>
      <p>
        Operative hysteroscopy is performed to remove an intrauterine pathology (polyp, fibroid,
        septum or adhesion) in the same session. Larger (8–10&nbsp;mm) operative hysteroscopes or
        thinner "see-and-treat" systems capable of both diagnosis and treatment may be used. It is
        performed in an operating theatre under general or spinal anaesthesia, and typically lasts
        20–60 minutes depending on the pathology.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When is hysteroscopy indicated?</h2>

      <h3 className="mt-6 font-display text-xl font-semibold">Abnormal uterine bleeding</h3>
      <p>
        Prolonged, irregular or heavy menstrual bleeding, intermenstrual spotting and — most
        importantly — any bleeding after the menopause are the most common indications. Endometrial
        polyps, submucosal fibroids, endometrial hyperplasia and, more rarely, endometrial cancer
        can be distinguished through targeted biopsy.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Endometrial polyps</h3>
      <p>
        Endometrial polyps are benign overgrowths of the uterine lining that may cause bleeding and
        infertility. Hysteroscopic polypectomy is the gold standard for their complete removal and
        for assessing the risk of malignancy. Blind curettage can miss 30–50% of polyps; direct
        visualisation eliminates that risk.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Submucosal fibroids</h3>
      <p>
        Fibroids projecting into the uterine cavity (FIGO types 0, 1 and selected type 2) can
        generally be removed hysteroscopically. Hysteroscopic myomectomy is a uterus-sparing option
        that avoids abdominal incisions and treats heavy bleeding, infertility and recurrent
        pregnancy loss caused by these fibroids.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Uterine septum and congenital anomalies
      </h3>
      <p>
        A uterine septum is a fibrous-muscular partition that divides the uterine cavity and is one
        of the correctable causes of recurrent pregnancy loss and preterm birth. Hysteroscopic
        septum resection (metroplasty) significantly reduces miscarriage rates and improves
        live-birth outcomes.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Intrauterine adhesions (Asherman syndrome)
      </h3>
      <p>
        Adhesions inside the uterine cavity — usually after curettage, infection or surgery — can
        cause reduced or absent menstruation, infertility and recurrent miscarriage. Hysteroscopic
        adhesiolysis is the cornerstone of treatment. Post-procedure oestrogen therapy and, in
        selected cases, a temporary intrauterine balloon help prevent recurrence.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">Infertility assessment</h3>
      <p>
        In couples with suspicious findings on HSG or ultrasound, in recurrent IVF failure and in
        recurrent pregnancy loss, hysteroscopy provides a clear view of the cavity. Silent polyps,
        fibroids, septa and adhesions can be identified and treated in the same session.
      </p>

      <h3 className="mt-6 font-display text-xl font-semibold">
        Displaced or embedded intrauterine device (IUD)
      </h3>
      <p>
        An IUD with missing strings or one that has become embedded in the uterine wall can be
        removed safely and under vision by hysteroscopy, avoiding injury from blind traction
        attempts.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Preparation</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Timing:</strong> In menstruating women the procedure is scheduled in the early
          follicular phase, just after menstruation ends (usually days 6–12 of the cycle), when the
          endometrium is thin and visibility is optimal.
        </li>
        <li>
          <strong>Pregnancy exclusion:</strong> Pregnancy is always excluded before the procedure.
        </li>
        <li>
          <strong>Infection screening:</strong> Any active vaginal or pelvic infection is treated
          beforehand.
        </li>
        <li>
          <strong>Analgesia:</strong> An oral analgesic (naproxen, ibuprofen) may be given before
          office hysteroscopy.
        </li>
        <li>
          <strong>Anaesthesia:</strong> Operative hysteroscopy requires 6–8 hours of fasting;
          anticoagulant medication is reviewed in advance.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">How the procedure is performed</h2>
      <ol className="list-decimal space-y-2 pl-6">
        <li>
          The patient is placed in the lithotomy position and the vagina is cleansed with
          antiseptic.
        </li>
        <li>
          In office hysteroscopy the "vaginoscopic" approach is often used: the hysteroscope is
          introduced through the vagina and cervix without a speculum.
        </li>
        <li>
          Warm saline (isotonic solution) is instilled into the cavity at low pressure to distend it
          and separate the walls for inspection.
        </li>
        <li>
          The endometrial surface, tubal ostia and cervical canal are examined systematically, and
          targeted biopsies are taken from suspicious areas.
        </li>
        <li>
          During operative hysteroscopy, polyps, fibroids or septa are removed with a
          monopolar/bipolar resectoscope, fine scissors or a morcellator. Adhesions are opened with
          sharp dissection.
        </li>
      </ol>

      <h2 className="mt-10 font-display text-2xl font-semibold">Recovery</h2>
      <p>
        Mild cramping and light spotting for 1–3 days are common after diagnostic hysteroscopy.
        After operative hysteroscopy, spotting may last up to a week. The following advice is
        routinely given:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Avoid intercourse, tampons and swimming/hot tubs for the first 1–2 weeks.</li>
        <li>Postpone strenuous exercise and heavy lifting for approximately one week.</li>
        <li>Use paracetamol or a physician-recommended NSAID for mild discomfort.</li>
        <li>
          Seek prompt medical attention for fever, foul-smelling discharge, worsening pain or heavy
          bleeding that soaks through a pad.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Is hysteroscopy safe? What are the risks?
      </h2>
      <p>
        Hysteroscopy is one of the safest gynaecological procedures, with an overall complication
        rate below 1%. As with any surgical intervention, however, the following risks should be
        understood:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Uterine perforation:</strong> Puncture of the uterine wall by the hysteroscope
          (0.1–1%). Observation is usually sufficient; laparoscopic repair is rarely required.
        </li>
        <li>
          <strong>Infection:</strong> Rare (0.1–0.9%); presents with fever and foul-smelling
          discharge and is treated with antibiotics.
        </li>
        <li>
          <strong>Fluid overload:</strong> Absorption of the distension medium into the vascular
          system, mainly during longer operative procedures. Modern monitoring and the use of
          isotonic saline minimise this risk.
        </li>
        <li>
          <strong>Bleeding:</strong> Usually mild and self-limiting.
        </li>
        <li>
          <strong>Cervical injury:</strong> A small tear of the cervix; rare and usually requires no
          treatment.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hysteroscopy versus D&amp;C</h2>
      <p>
        Classical dilation and curettage (D&amp;C) is a blind scraping of the uterine cavity; the
        surgeon cannot see the interior of the uterus, and focal lesions such as polyps or
        submucosal fibroids can be missed. Hysteroscopy allows the cavity to be seen directly and
        pathology to be removed under vision, making it both more accurate and less traumatic. In
        many centres, "see-and-treat" hysteroscopy has replaced diagnostic curettage.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hysteroscopy and fertility</h2>
      <p>
        Numerous studies have shown that hysteroscopic correction of endometrial polyps, submucosal
        fibroids, a uterine septum and intrauterine adhesions improves pregnancy and live-birth
        rates. Office hysteroscopy before embryo transfer is used routinely in many centres
        following recurrent IVF failure. Attempts to conceive can generally resume after 1–2
        menstrual cycles; where operative procedures involve deeper myometrial repair (for example,
        myomectomy), an individualised waiting period is advised.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to see a specialist</h2>
      <p>
        Consultation with a gynaecologist is recommended if any of the following apply — you may
        benefit from hysteroscopy:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Prolonged, irregular or heavy menstrual bleeding.</li>
        <li>Any bleeding or spotting after the menopause.</li>
        <li>Failure to conceive after one year of regular intercourse (6 months over age 35).</li>
        <li>Two or more consecutive pregnancy losses.</li>
        <li>Suspicious intrauterine findings on ultrasound or HSG.</li>
        <li>An IUD with missing strings or embedded in the uterine wall.</li>
      </ul>

      <p className="mt-8">
        Hysteroscopy is today one of the most valuable tools in gynaecology: it both accurately
        diagnoses and — in the same session, without an abdominal incision — treats a wide range of
        intrauterine conditions, allowing patients a rapid return to daily life. Used with the right
        indications, it combines high success rates with a very low complication profile.
      </p>
    </ArticleShell>
  );
}

function MenopausePost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        The <strong>menopause</strong> is the permanent cessation of menstruation that follows the
        natural decline of ovarian hormone production. The medical definition is retrospective:{" "}
        <strong>12 consecutive months without a period</strong>, in the absence of another cause.
        The average age is 48–51 years. It is a physiological transition rather than a disease — but
        its symptoms and long-term health consequences are, in most women, entirely treatable.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">The perimenopause</h2>
      <p>
        The 4–8 year transition preceding the menopause is called the <strong>perimenopause</strong>
        . As ovarian reserve declines, oestrogen levels fluctuate unpredictably. Periods first
        become closer together, then more widely spaced, and bleeding becomes variable in volume.
        Hot flushes, disturbed sleep and mood changes usually begin during this phase. An important
        point: conception remains possible in the perimenopause, so contraception should be
        continued for 12 months after the final period.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms of the menopause</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Vasomotor symptoms:</strong> hot flushes and night sweats, affecting around 75% of
          women and lasting an average of seven years.
        </li>
        <li>
          <strong>Sleep disturbance and fatigue:</strong> sleep fragmented by night sweats leads to
          daytime difficulty with concentration.
        </li>
        <li>
          <strong>Genitourinary syndrome of the menopause:</strong> vaginal dryness, pain during
          intercourse, urinary frequency and recurrent urinary tract infections. Unlike other
          symptoms, this progresses over time if untreated.
        </li>
        <li>
          <strong>Mood symptoms:</strong> mood swings, irritability, anxiety and depressive
          symptoms.
        </li>
        <li>
          <strong>Bone loss:</strong> falling oestrogen accelerates bone resorption; 10–20% of bone
          density may be lost in the first 5–7 years. It is silent until a fracture occurs.
        </li>
        <li>
          <strong>Metabolic change:</strong> central weight gain, an adverse lipid profile and
          rising cardiovascular risk.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">How is it diagnosed?</h2>
      <p>
        In a woman over 45 with typical symptoms and absent periods, the diagnosis is clinical and
        no routine hormone testing is required. FSH and oestradiol measurement is helpful where
        periods stop between 40 and 45, where they stop before 40 (suspected premature ovarian
        insufficiency), after hysterectomy, and in users of hormonal contraception. Thyroid function
        tests are frequently added, since thyroid disease can produce very similar symptoms.
      </p>
      <p>
        <strong>Important:</strong> any vaginal bleeding after the menopause is abnormal and must be
        assessed. Endometrial thickness should be measured by ultrasound, with endometrial biopsy or
        hysteroscopy where indicated.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        What is hormone replacement therapy (HRT)?
      </h2>
      <p>
        HRT replaces the oestrogen the ovaries no longer produce. Women with a uterus must also
        receive a <strong>progestogen</strong> to protect the endometrium; oestrogen alone is
        sufficient after hysterectomy. The main routes are:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Transdermal (patch or gel):</strong> avoids first-pass hepatic metabolism, so the
          thrombotic risk is lower than with oral therapy. Preferred in migraine, hypertension and
          raised triglycerides.
        </li>
        <li>
          <strong>Oral tablets:</strong> convenient and highly effective.
        </li>
        <li>
          <strong>Local vaginal oestrogen:</strong> used where vaginal and urinary symptoms are the
          only complaint. Systemic absorption is negligible, so it carries no systemic risk and
          requires no added progestogen.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Who is suitable for HRT?</h2>
      <p>
        Current guidelines share the <strong>"window of opportunity"</strong> concept: in healthy
        women who begin treatment <strong>under the age of 60</strong> and{" "}
        <strong>within 10 years of the menopause</strong>, the benefit-to-risk balance is
        favourable. In this group HRT reduces hot flushes by up to 75%, improves sleep and quality
        of life, halts bone loss and lowers fracture risk. For women who reach the menopause before
        the age of 40, HRT is not a preference but a requirement, and should normally be continued
        at least until the average age of natural menopause.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risks and contraindications</h2>
      <p>
        The risks of HRT depend closely on the age at initiation and on the regimen used. Combined
        oestrogen–progestogen therapy used beyond five years is associated with a small but real
        increase in breast cancer risk; oestrogen-only therapy is not. Oral oestrogen increases
        venous thromboembolic risk, whereas transdermal preparations do not appreciably do so.{" "}
        <strong>Contraindications</strong> include:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Active or previous breast cancer and other oestrogen-sensitive cancers.</li>
        <li>Unexplained vaginal bleeding.</li>
        <li>Previous deep vein thrombosis or pulmonary embolism.</li>
        <li>Previous stroke or myocardial infarction; uncontrolled hypertension.</li>
        <li>Active liver disease.</li>
      </ul>
      <p>
        Treatment is reviewed at three months and annually thereafter, and breast examination and
        mammographic screening should be maintained.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Non-hormonal options</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>For hot flushes:</strong> low-dose SSRIs/SNRIs, gabapentin, clonidine and the
          newer non-hormonal NK3 receptor antagonists.
        </li>
        <li>
          <strong>For vaginal dryness:</strong> water-based lubricants, long-acting vaginal
          moisturisers and very low-dose local oestrogen.
        </li>
        <li>
          <strong>For bone health:</strong> 1000–1200 mg calcium daily, vitamin D supplementation,
          walking and resistance exercise; bisphosphonates where indicated.
        </li>
        <li>
          <strong>Lifestyle:</strong> stopping smoking, weight management, reducing alcohol and
          caffeine, layered clothing and consistent sleep hygiene.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Routine checks after the menopause
      </h2>
      <p>
        The menopause is the point at which annual health screening becomes particularly valuable:
        breast examination and mammography, cervical screening (cytology/HPV), blood pressure and
        glucose measurement, lipid profile, thyroid function and, at the appropriate age, DEXA bone
        density scanning.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to see a specialist</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Any bleeding or spotting after the menopause.</li>
        <li>Periods stopping before the age of 40.</li>
        <li>Hot flushes, insomnia or mood changes that disrupt daily life.</li>
        <li>Pain during intercourse, vaginal dryness or recurrent urinary tract infections.</li>
        <li>Loss of height, back pain or a fracture after minor trauma.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Menopause care in Adana</h2>
      <p>
        Good menopause management is not a single prescription applied to everyone, but an
        individual plan built from age, time since the menopause, symptom severity and personal and
        family risk factors. Op. Dr. Sadık Kükrer, MD offers comprehensive assessment of the
        menopausal transition, suitability review for hormone replacement therapy, non-hormonal
        alternatives and long-term bone health monitoring in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function InfertilityPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Infertility</strong> is the failure to conceive despite regular unprotected
        intercourse: <strong>12 months</strong> in couples where the woman is under 35, and{" "}
        <strong>6 months</strong> at 35 or older. Approximately one in six or seven couples meets
        this definition. Importantly, it does not imply permanent sterility — in most couples a
        cause can be identified and pregnancy achieved with appropriate treatment.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When should assessment begin?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Under 35: after 12 months of trying.</li>
        <li>35 and over: after 6 months.</li>
        <li>Over 40: at the first consultation, without waiting.</li>
        <li>
          Immediately where there is irregular menstruation, previous pelvic infection,
          endometriosis, previous pelvic surgery, two or more pregnancy losses, or a known male
          factor.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Female causes</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ovulatory disorders:</strong> polycystic ovary syndrome (PCOS) is the most common;
          thyroid disease and hyperprolactinaemia also disrupt ovulation.
        </li>
        <li>
          <strong>Tubal factor:</strong> blockage or adhesions following infection, ectopic
          pregnancy or surgery.
        </li>
        <li>
          <strong>Endometriosis:</strong> impairs both oocyte quality and tubo-ovarian function.
        </li>
        <li>
          <strong>Uterine pathology:</strong> polyps, submucosal fibroids, intrauterine adhesions
          (Asherman syndrome) and uterine septum may prevent implantation.
        </li>
        <li>
          <strong>Diminished ovarian reserve:</strong> related to age, previous ovarian surgery or
          chemotherapy.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Male causes</h2>
      <p>
        A male factor is present in about one third of couples, which is why{" "}
        <strong>semen analysis is a first-line test</strong> and should be requested alongside the
        female investigations. Abnormalities of sperm count, motility or morphology, varicocele,
        previous mumps orchitis, infection, hormonal disorders, certain medications, smoking and
        heat exposure are the principal contributors.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Which investigations are used?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Semen analysis:</strong> after 2–5 days of abstinence; repeated after 2–4 weeks if
          abnormal.
        </li>
        <li>
          <strong>Confirmation of ovulation:</strong> cycle history, day 21 progesterone, or
          follicular tracking.
        </li>
        <li>
          <strong>Ovarian reserve:</strong> day 2–4 FSH and oestradiol, AMH, and antral follicle
          count on ultrasound.
        </li>
        <li>
          <strong>Tubal patency:</strong> hysterosalpingography (HSG) or ultrasound-based HyCoSy.
        </li>
        <li>
          <strong>Hormonal screening:</strong> TSH and prolactin; androgens where clinically
          indicated.
        </li>
        <li>
          <strong>Further investigation:</strong> hysteroscopy for suspected intrauterine pathology,
          laparoscopy where endometriosis or adhesions are suspected.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment options</h2>
      <p>
        Treatment is stepwise and guided by the diagnosis and the woman&apos;s age, aiming to
        achieve pregnancy with the least invasive effective option.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Lifestyle measures:</strong> weight optimisation, smoking cessation, folic acid,
          and regular intercourse around ovulation. In PCOS, a 5–10% weight loss alone can restore
          ovulation.
        </li>
        <li>
          <strong>Ovulation induction:</strong> letrozole or clomifene citrate, with low-dose
          gonadotrophins and follicular tracking where required.
        </li>
        <li>
          <strong>Surgery:</strong> hysteroscopic removal of polyps, submucosal fibroids, septa and
          adhesions; laparoscopic treatment of endometriosis.
        </li>
        <li>
          <strong>Intrauterine insemination (IUI):</strong> 3–4 cycles in mild male factor, cervical
          factor and unexplained infertility, provided the tubes are patent.
        </li>
        <li>
          <strong>IVF/ICSI:</strong> for bilateral tubal blockage, severe male factor, advanced
          maternal age, diminished reserve, or after failed earlier treatment.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Unexplained infertility</h2>
      <p>
        In roughly 10–15% of couples all investigations are normal. This does not mean treatment is
        unavailable: ovulation induction combined with insemination is effective, and IVF gives good
        results where pregnancy is not achieved within a defined period. The woman&apos;s age is the
        most important factor in deciding how long to wait.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek medical advice</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>No pregnancy after 12 months under 35, or 6 months at 35 and over.</li>
        <li>Irregular or absent periods.</li>
        <li>Severe menstrual pain, painful intercourse, or known endometriosis.</li>
        <li>Two or more consecutive pregnancy losses.</li>
        <li>
          Known varicocele, previous testicular surgery or mumps orchitis in the male partner.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Fertility assessment in Adana</h2>
      <p>
        The most valuable step in infertility care is a structured assessment carried out in the
        right order, without unnecessary testing. Op. Dr. Sadık Kükrer, MD offers joint assessment
        of both partners, investigation of ovulatory and uterine factors, and hysteroscopic and
        laparoscopic surgical treatment in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function PrenatalScreeningPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Prenatal screening is one of the most frequently discussed aspects of antenatal care. Its
        purpose is to estimate the <strong>probability</strong> that a baby is affected by a
        chromosomal or structural condition, and to guide further investigation where needed.
        Screening tests do not establish a diagnosis: a high-risk result does not mean the baby is
        affected.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Schedule of screening tests</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>6–10 weeks:</strong> confirmation of pregnancy, fetal heart activity, and baseline
          blood tests (blood group, full blood count, thyroid function, infection screening).
        </li>
        <li>
          <strong>From 10 weeks:</strong> NIPT (cell-free fetal DNA testing) — optional, with the
          highest detection rate of any screening test.
        </li>
        <li>
          <strong>11–14 weeks:</strong> combined test with nuchal translucency (NT) measurement,
          nasal bone assessment and early structural review.
        </li>
        <li>
          <strong>16–18 weeks:</strong> quadruple test where the combined test was not performed,
          and AFP for neural tube defect screening.
        </li>
        <li>
          <strong>20–22 weeks:</strong> the detailed anomaly scan.
        </li>
        <li>
          <strong>24–28 weeks:</strong> oral glucose tolerance test and screening for anaemia.
        </li>
        <li>
          <strong>28 weeks:</strong> anti-D immunoglobulin for rhesus-negative women.
        </li>
        <li>
          <strong>35–37 weeks:</strong> group B streptococcus culture and birth planning.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        The combined test and nuchal translucency
      </h2>
      <p>
        Performed between 11 weeks 0 days and 13 weeks 6 days. Maternal serum{" "}
        <strong>PAPP-A</strong> and <strong>free beta-hCG</strong> are measured, while ultrasound
        assesses nuchal translucency and crown-rump length. Together with maternal age, these
        produce an individual risk for trisomies 21, 18 and 13, with a detection rate of
        approximately 90% at a 5% false-positive rate. Adding nasal bone, ductus venosus and
        tricuspid flow assessment improves accuracy further.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Triple and quadruple tests</h2>
      <p>
        Offered between 16 and 18 weeks where the first-trimester window has been missed. The triple
        test measures AFP, hCG and unconjugated oestriol; the quadruple test adds inhibin A, with a
        detection rate of around 80%. A raised AFP additionally raises the possibility of a{" "}
        <strong>neural tube defect</strong> such as spina bifida.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        NIPT: cell-free fetal DNA testing
      </h2>
      <p>
        Non-invasive prenatal testing analyses placenta-derived cell-free DNA circulating in
        maternal blood and can be carried out from 10 weeks. Detection of Down syndrome exceeds{" "}
        <strong>99%</strong>, with a false-positive rate below 1 in 1000. It also reports on the sex
        chromosomes.
      </p>
      <p>
        NIPT nonetheless remains a <strong>screening</strong> test: a positive result must be
        confirmed by amniocentesis or CVS. It does not detect structural anomalies of the heart,
        spine or kidneys, and therefore does not replace ultrasound screening.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">The anomaly scan</h2>
      <p>
        At 20–22 weeks the fetal brain and spine, cardiac chambers and great vessels, face and lips,
        stomach, kidneys, bladder, hands and feet are examined systematically, together with
        placental position, amniotic fluid volume and cervical length. A substantial proportion of
        structural anomalies are identified at this examination.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        What does a “high-risk” result mean?
      </h2>
      <p>
        Risk is expressed as a ratio. A result of 1 in 150 means that one of 150 comparable
        pregnancies would be affected — in other words, a{" "}
        <strong>99.3% probability that this baby is unaffected</strong>. Where the result exceeds
        the threshold (usually 1 in 250 to 1 in 300), the options are:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>NIPT as a second-line screening test in intermediate-risk results.</li>
        <li>
          Diagnostic testing by <strong>chorionic villus sampling (CVS, 11–14 weeks)</strong> or{" "}
          <strong>amniocentesis (16–20 weeks)</strong>.
        </li>
        <li>Detailed fetal ultrasound and, where indicated, fetal echocardiography.</li>
      </ul>
      <p>
        In experienced centres, the pregnancy loss rate attributable to invasive testing is
        currently reported as approximately 1 in 300 to 1 in 1000.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Common misunderstandings</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Treating a screening result as a diagnosis and acting on it in haste.</li>
        <li>Missing the combined test window at 13 weeks 6 days.</li>
        <li>Omitting the anomaly scan because NIPT was normal.</li>
        <li>Undergoing several screening tests and interpreting each in isolation.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek advice</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>As soon as a pregnancy test is positive, for the first review at 6–8 weeks.</li>
        <li>Before making any decision following a screening result above the threshold.</li>
        <li>
          Where there is a family history of inherited disease or a previous affected pregnancy.
        </li>
        <li>In advanced maternal age (35 and over) or recurrent pregnancy loss.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Antenatal care in Adana</h2>
      <p>
        The value of screening depends on performing each test in the correct window and
        interpreting the results as a whole. Op. Dr. Sadık Kükrer, MD provides antenatal care,
        nuchal translucency measurement, detailed anomaly scanning and high-risk pregnancy
        management in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function BirthControlPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        There is no single best contraceptive; there is only the method best suited to you. The
        choice depends on age, plans for pregnancy, breastfeeding, menstrual pattern, coexisting
        medical conditions and how consistently a method can realistically be used. The options
        below are compared by effectiveness, convenience and return to fertility.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Methods ranked by effectiveness</h2>
      <p>The figures below indicate how many women in 100 conceive during a year of typical use.</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Contraceptive implant:</strong> 0.1% — three years of protection; the most
          effective reversible method.
        </li>
        <li>
          <strong>Hormonal IUD:</strong> 0.1–0.4% — 5–8 years; also reduces menstrual bleeding.
        </li>
        <li>
          <strong>Copper IUD:</strong> 0.8% — 5–10 years; entirely hormone-free.
        </li>
        <li>
          <strong>Tubal ligation / vasectomy:</strong> 0.1–0.5% — permanent methods.
        </li>
        <li>
          <strong>Three-monthly injection:</strong> 4% — oestrogen-free, suitable while
          breastfeeding.
        </li>
        <li>
          <strong>Combined pill, vaginal ring, patch:</strong> 7% — depend on consistent use.
        </li>
        <li>
          <strong>Condoms:</strong> 13% — the only method that also protects against infection.
        </li>
        <li>
          <strong>Fertility awareness and withdrawal:</strong> 20–23% — unreliable.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Hormonal methods</h2>
      <p>
        <strong>Combined preparations</strong> contain oestrogen and a progestogen and suppress
        ovulation. They also improve irregular or painful periods, acne and hirsutism. Migraine with
        aura, uncontrolled hypertension, previous thrombosis, active breast cancer and smoking over
        the age of 35 are contraindications.
      </p>
      <p>
        <strong>Progestogen-only pills</strong>, the <strong>implant</strong> and{" "}
        <strong>injections</strong> contain no oestrogen and are therefore preferred during
        breastfeeding and wherever oestrogen is unsuitable. Irregular spotting in the first months
        is the commonest side effect; it settles with time and many women stop bleeding altogether,
        which is harmless.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Intrauterine devices</h2>
      <p>
        An IUD is fitted in the clinic in a few minutes. The <strong>copper device</strong> contains
        no hormone but may increase menstrual flow and cramping. The{" "}
        <strong>hormonal device</strong> markedly reduces bleeding and is therefore also used to
        treat heavy menstrual bleeding, adenomyosis and fibroid-related bleeding. It is appropriate
        at any age, including for women who have not given birth.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Permanent methods</h2>
      <p>
        Tubal ligation and vasectomy should be regarded as irreversible; reversal surgery has
        limited success. They suit couples certain that their family is complete. Vasectomy is the
        simpler procedure, carries less risk and can be performed under local anaesthesia.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Emergency contraception</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Levonorgestrel: within 72 hours; the earlier it is taken, the more effective.</li>
        <li>Ulipristal acetate: licensed up to 120 hours, with higher effectiveness.</li>
        <li>
          Copper IUD: the most effective option when inserted within five days, and it provides
          continuing contraception thereafter.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Return to fertility</h2>
      <p>
        After stopping the pill, ring, patch, implant or IUD, ovulation usually resumes within one
        to three cycles. Only the three-monthly injection may delay conception by 6–12 months, so it
        is not advised for women planning pregnancy in the near future.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek advice</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Before choosing a method, so that your medical history can be reviewed.</li>
        <li>
          If you develop severe headache, visual disturbance or calf pain and swelling while taking
          the pill.
        </li>
        <li>If irregular or breakthrough bleeding persists beyond three months.</li>
        <li>If you cannot feel the IUD threads, or you develop pelvic pain or fever.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Family planning in Adana</h2>
      <p>
        Op. Dr. Sadık Kükrer, MD provides contraceptive counselling, IUD insertion and removal,
        implant fitting and individualised family planning in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function BirthModePost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        The mode of birth is decided with the health of mother and baby as the first priority. Where
        there is no medical contraindication, vaginal birth is the most appropriate route for most
        pregnancies; caesarean section is a life-saving operation in defined circumstances. The
        decision is reached together, in the light of the pregnancy, the presentation of the baby
        and the mother's history.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Advantages of vaginal birth</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Hospital stay of one to two days and recovery within one to two weeks.</li>
        <li>Lower rates of infection, haemorrhage and venous thrombosis.</li>
        <li>Earlier skin-to-skin contact and easier establishment of breastfeeding.</li>
        <li>Better clearance of fetal lung fluid and favourable microbiome development.</li>
        <li>No increase in the risk of abnormal placentation in future pregnancies.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Indications for caesarean section
      </h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Placenta praevia or placental abruption.</li>
        <li>Transverse lie, certain breech presentations, cord prolapse.</li>
        <li>Fetal distress on cardiotocography.</li>
        <li>Obstructed labour or cephalopelvic disproportion.</li>
        <li>Previous classical (vertical) uterine incision or multiple previous caesareans.</li>
        <li>Active genital herpes or untreated HIV infection.</li>
        <li>Severe pre-eclampsia, some multiple pregnancies and severe growth restriction.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risks of caesarean section</h2>
      <p>
        Caesarean section is major abdominal surgery. It carries risks of haemorrhage, infection,
        bladder or bowel injury, venous thrombosis and anaesthetic complications. Recovery takes two
        to four weeks, and heavy lifting should be avoided for four to six weeks. Repeated
        caesareans increase intra-abdominal adhesions and the risk of serious placental
        abnormalities such as <strong>placenta accreta</strong>. The mode of the first birth is
        therefore particularly important for women planning several children.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Vaginal birth after caesarean (VBAC)
      </h2>
      <p>
        Women with a single <strong>lower-segment (transverse) incision</strong>, a cephalic
        presentation and no additional risk factors may plan a vaginal birth, with success rates of
        60–80%. Because the risk of uterine rupture is approximately 1 in 200, birth should take
        place under continuous monitoring in a unit with immediate access to emergency caesarean
        section. A previous vaginal birth improves the likelihood of success.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Pain relief and the birth plan</h2>
      <p>
        Epidural analgesia is the most effective and safest means of relieving labour pain and does
        not increase the caesarean rate. Water immersion, freedom of movement, breathing techniques
        and massage also help. Discussing a written birth plan with your clinician at 32–34 weeks —
        covering analgesia, birth partner, skin-to-skin contact and early feeding — clarifies
        expectations. The plan should remain flexible so that it can accommodate events in labour.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Common pitfalls</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Choosing caesarean because of fear of childbirth without seeking counselling.</li>
        <li>Neglecting early mobilisation after caesarean, which raises thrombosis risk.</li>
        <li>Planning an elective caesarean before 39 weeks without a medical indication.</li>
        <li>Arriving at the day of birth without ever discussing a birth plan.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek advice</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>When contractions become regular and increasingly frequent.</li>
        <li>Immediately if your waters break, even without contractions.</li>
        <li>With bleeding, severe headache, visual disturbance or sudden swelling.</li>
        <li>If you notice reduced fetal movements.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Birth planning in Adana</h2>
      <p>
        Op. Dr. Sadık Kükrer, MD provides antenatal care, birth planning, assessment for vaginal
        birth after caesarean and high-risk pregnancy management in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function GestationalDiabetesPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Gestational diabetes is a rise in blood glucose caused by the insulin resistance that
        placental hormones induce during pregnancy. It affects approximately{" "}
        <strong>7–14% of pregnancies</strong> and produces no symptoms in most women. Diagnosis
        therefore depends on <strong>screening</strong> rather than on symptoms.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Who is at higher risk?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Body mass index above 30.</li>
        <li>A first-degree relative with type 2 diabetes.</li>
        <li>Previous gestational diabetes or a baby weighing over 4000 g.</li>
        <li>Polycystic ovary syndrome.</li>
        <li>Maternal age over 35, or multiple pregnancy.</li>
        <li>A history of unexplained stillbirth.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Screening and diagnosis</h2>
      <p>
        Screening is offered to all women at <strong>24–28 weeks</strong>, and at the first visit
        for those with risk factors.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>One-step (75 g OGTT):</strong> a single value at or above fasting 92 mg/dL, one
          hour 180 mg/dL or two hours 153 mg/dL confirms the diagnosis.
        </li>
        <li>
          <strong>Two-step:</strong> a non-fasting 50 g test; if the one-hour value exceeds 140
          mg/dL, a 100 g confirmatory test follows.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Target glucose values</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Fasting: below 95 mg/dL</li>
        <li>One hour after meals: below 140 mg/dL</li>
        <li>Two hours after meals: below 120 mg/dL</li>
      </ul>
      <p>
        Readings are usually taken four times daily — fasting and after each main meal — and
        recorded in a diary.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Diet and exercise</h2>
      <p>
        Approximately <strong>70–85%</strong> of women achieve target values with lifestyle measures
        alone.
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Distribute carbohydrate across three meals and two to three snacks; do not skip meals.
        </li>
        <li>Remove sugary drinks, fruit juice, refined flour and confectionery entirely.</li>
        <li>Choose wholegrains, pulses, vegetables and adequate protein.</li>
        <li>Combine carbohydrate with protein or healthy fat to blunt the glucose rise.</li>
        <li>Walk briskly for 15–20 minutes after every meal.</li>
        <li>
          Insulin resistance is highest at breakfast, which should therefore be the
          lowest-carbohydrate meal of the day.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Medical treatment</h2>
      <p>
        Insulin is started if targets are not met within one to two weeks of dietary and exercise
        measures. It does not cross the placenta and is the safest option in pregnancy; doses often
        rise as pregnancy advances and are usually stopped immediately after birth. Metformin is an
        alternative in selected cases.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Monitoring the pregnancy</h2>
      <p>
        Fetal growth is assessed by ultrasound every four weeks, together with amniotic fluid volume
        and fetal movements. Where glucose is well controlled, birth is generally planned at 39–40
        weeks. Caesarean section is discussed when the estimated fetal weight exceeds 4500 g.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">After delivery</h2>
      <p>
        Blood glucose returns to normal in most women. A repeat{" "}
        <strong>75 g OGTT at 6–12 weeks</strong> is nevertheless required, followed by screening
        every one to three years. The long-term risk of type 2 diabetes is substantially increased;
        breastfeeding, weight management and 150 minutes of exercise a week reduce it.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek advice</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>If more than a third of your readings exceed the targets.</li>
        <li>If fasting glucose is persistently above 95 mg/dL.</li>
        <li>
          With severe headache, visual disturbance or sudden swelling, which may indicate
          pre-eclampsia.
        </li>
        <li>If you notice reduced fetal movements.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        High-risk pregnancy care in Adana
      </h2>
      <p>
        Op. Dr. Sadık Kükrer, MD provides screening for gestational diabetes, dietary planning,
        insulin management and comprehensive high-risk pregnancy care in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function LaparoscopyPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Laparoscopy, commonly described as <strong>keyhole surgery</strong>, is performed through
        several 5–10 mm incisions in the abdominal wall using a camera and fine instruments. The
        abdominal cavity is inflated with carbon dioxide so that the pelvic organs can be seen
        clearly, and the operation is carried out under high-definition monitor guidance. The
        majority of gynaecological operations can now be completed in this way.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Which conditions are treated?</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Ovarian cysts:</strong> cystectomy, or removal of the ovary where necessary.
        </li>
        <li>
          <strong>Endometriosis and endometriomas:</strong> excision of deposits, division of
          adhesions and relief of pain.
        </li>
        <li>
          <strong>Fibroids:</strong> uterus-sparing removal (myomectomy).
        </li>
        <li>
          <strong>Ectopic pregnancy and ovarian torsion:</strong> emergency, organ-preserving
          surgery.
        </li>
        <li>
          <strong>Fertility assessment:</strong> confirmation of tubal patency by dye test and
          treatment of pelvic adhesions.
        </li>
        <li>
          <strong>Chronic pelvic pain:</strong> diagnostic laparoscopy where imaging has not
          provided an explanation.
        </li>
        <li>
          <strong>Hysterectomy</strong> and selected gynaecological oncology procedures.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Preparation for surgery</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Full blood count, biochemistry and clotting studies, with an ECG and chest radiograph
          where indicated.
        </li>
        <li>Ultrasound, and MRI where the lesion requires more detailed assessment.</li>
        <li>Fasting for six to eight hours before the operation.</li>
        <li>
          Adjustment of anticoagulant medication under medical supervision; stopping smoking at
          least two weeks beforehand improves healing.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">How the procedure is performed</h2>
      <p>
        Surgery is carried out under general anaesthesia. Access is obtained at the umbilicus, the
        abdomen is insufflated and the camera is introduced. One to three additional incisions in
        the lower abdomen accommodate the instruments. At the end of the procedure the gas is
        released and the incisions are closed with fine sutures. Diagnostic procedures take 20–40
        minutes; advanced surgery takes 1.5 to 3 hours.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Advantages over open surgery</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Less blood loss and a lower rate of wound infection.</li>
        <li>Less postoperative pain and a reduced requirement for analgesia.</li>
        <li>A short hospital stay, with same-day or next-day discharge in most cases.</li>
        <li>Fewer intra-abdominal adhesions and smaller scars.</li>
        <li>A considerably faster return to normal activity and work.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risks and complications</h2>
      <p>
        Serious complications occur in <strong>fewer than 1%</strong> of cases. Injury to blood
        vessels, bowel, bladder or ureter, bleeding, infection, port-site hernia and anaesthetic
        complications are all uncommon. Where there are extensive adhesions, very large masses or a
        high body mass index, the operation may be converted to open surgery. Conversion is not a
        failure but a decision that places patient safety first.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Recovery</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Shoulder and back discomfort in the first 24–48 hours is caused by residual gas and
          settles quickly with walking.
        </li>
        <li>
          The incisions heal within 7–10 days, and showering with a waterproof dressing is usually
          permitted.
        </li>
        <li>
          Return to desk work takes 5–10 days; heavy work and sport are resumed after three to four
          weeks.
        </li>
        <li>
          Fever, increasing abdominal pain, discharge from an incision, leg swelling or
          breathlessness require prompt medical review.
        </li>
      </ul>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD provides gynaecological laparoscopy, endometriosis and fibroid
        surgery in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function LaparoscopicHysterectomyPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Hysterectomy is the surgical removal of the uterus.{" "}
        <strong>Laparoscopic hysterectomy</strong> performs that operation through several 5–10 mm
        abdominal incisions under camera guidance. The uterus is usually delivered through the
        vagina and the vaginal vault is closed laparoscopically, so a large abdominal incision is
        not required.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Indications</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Heavy menstrual bleeding unresponsive to medical treatment or intrauterine procedures.
        </li>
        <li>Large, multiple or symptomatic fibroids.</li>
        <li>Severe pain and bleeding due to adenomyosis.</li>
        <li>Advanced endometriosis.</li>
        <li>Uterine prolapse.</li>
        <li>Pre-cancerous endometrial lesions (atypical hyperplasia) and selected malignancies.</li>
      </ul>
      <p>
        Where fertility is to be preserved, uterus-sparing alternatives such as myomectomy,
        endometrial ablation, a hormonal intrauterine system or uterine artery embolisation are
        considered first.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Surgical techniques</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Total laparoscopic hysterectomy:</strong> the uterus and cervix are removed
          together; this is the commonest approach.
        </li>
        <li>
          <strong>Subtotal (supracervical) hysterectomy:</strong> the cervix is left in place, so
          cervical screening continues.
        </li>
        <li>
          <strong>Laparoscopically assisted vaginal hysterectomy:</strong> part of the operation is
          completed through the vagina.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Are the ovaries removed?</h2>
      <p>
        Removal of the uterus does not imply removal of the ovaries. In pre-menopausal women the
        ovaries are preserved unless there is a medical indication to remove them: hormone
        production continues and <strong>surgical menopause does not occur</strong>. Where the
        ovaries must be removed, menopausal symptoms begin abruptly and hormone therapy is discussed
        in suitable cases. Removal of the fallopian tubes (salpingectomy) is frequently added
        because it reduces the subsequent risk of ovarian cancer.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Surgery and hospital stay</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Performed under general anaesthesia, typically over 1.5 to 3 hours.</li>
        <li>The urinary catheter is usually removed within the first 24 hours.</li>
        <li>
          Mobilising within six hours reduces the risk of thrombosis and helps bowel function
          return.
        </li>
        <li>Discharge is usually within one to two days.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Recovery timeline</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Week 1:</strong> pain is controlled with simple analgesia and short, frequent
          walks are encouraged.
        </li>
        <li>
          <strong>Weeks 2–4:</strong> return to daily activities, avoiding lifting more than five
          kilograms.
        </li>
        <li>
          <strong>Weeks 4–6:</strong> return to heavy work, sport and intercourse once the vaginal
          vault has healed.
        </li>
      </ul>
      <p>
        Light spotting during this period is normal. Fever, offensive discharge, heavy bleeding,
        increasing pain, leg swelling or breathlessness require urgent assessment.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Life after surgery</h2>
      <p>
        Menstruation ceases and pregnancy is no longer possible. When the ovaries are preserved, no
        appreciable hormonal change is expected. Studies show that sexual function after full
        recovery is unchanged in most women and frequently improves, because pain and bleeding have
        resolved. Pelvic floor exercises benefit both bladder and sexual function.
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD performs laparoscopic hysterectomy and advanced minimally invasive
        gynaecological surgery in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function PreeclampsiaPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Pre-eclampsia</strong> is defined as high blood pressure arising for the first time
        after 20 weeks of pregnancy, accompanied by protein in the urine or evidence of kidney,
        liver, clotting, neurological or placental dysfunction. It affects roughly 2–8% of
        pregnancies, and early recognition with close monitoring is decisive for the health of both
        mother and baby.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Warning signs: when to seek urgent care
      </h2>
      <p>
        Pre-eclampsia may initially cause no symptoms, which is why blood pressure is measured at
        every antenatal visit. Attend hospital without delay if you experience any of the following:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>A severe headache that does not respond to simple painkillers</li>
        <li>Blurred vision, flashing lights or double vision</li>
        <li>Pain below the ribs or in the upper abdomen, with nausea or vomiting</li>
        <li>Sudden, marked swelling of the face, hands or feet</li>
        <li>Breathlessness or reduced urine output</li>
        <li>Reduced fetal movements</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risk factors</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Pre-eclampsia in a previous pregnancy</li>
        <li>Chronic hypertension, pre-existing diabetes or kidney disease</li>
        <li>Lupus or antiphospholipid syndrome</li>
        <li>Twin or multiple pregnancy, IVF conception</li>
        <li>First pregnancy, age over 35, body mass index of 30 or above</li>
        <li>Pre-eclampsia in a mother or sister</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Prevention with low-dose aspirin
      </h2>
      <p>
        Risk is assessed in the first trimester. In high-risk women,{" "}
        <strong>low-dose aspirin started between 12 and 16 weeks</strong> and continued until 36
        weeks substantially reduces the risk of pre-eclampsia, particularly severe early-onset
        disease. Calcium supplementation may also be advised where dietary calcium intake is low.
        Aspirin should only be taken on medical advice.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Diagnosis and monitoring</h2>
      <p>
        Diagnosis is based on repeated blood pressure measurements, urine protein assessment
        (protein/creatinine ratio) and blood tests (platelets, liver enzymes and renal function). In
        selected cases the sFlt-1/PlGF ratio helps predict short-term risk. Fetal growth, amniotic
        fluid and blood flow are monitored with ultrasound and Doppler studies.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment and timing of birth</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>From 37 weeks:</strong> delivery is generally recommended.
        </li>
        <li>
          <strong>34–37 weeks:</strong> close surveillance in the absence of severe features;
          delivery if mother or baby deteriorates.
        </li>
        <li>
          <strong>Before 34 weeks:</strong> inpatient monitoring, antihypertensives, corticosteroids
          for fetal lung maturity and magnesium sulphate for seizure prophylaxis where indicated.
        </li>
        <li>
          Severe pre-eclampsia, HELLP syndrome or eclampsia require delivery regardless of
          gestational age.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">After birth</h2>
      <p>
        Pre-eclampsia can develop or worsen during the first six weeks after delivery; headache,
        visual disturbance or breathlessness during this period require urgent assessment. Because
        women who have had pre-eclampsia carry a higher lifetime risk of hypertension and
        cardiovascular disease, annual blood pressure checks and healthy lifestyle measures are
        important. See also our articles on{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "gestational-diabetes" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gestational diabetes
        </Link>{" "}
        and{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "prenatal-screening-tests" }}
          className="text-[color:var(--primary-text)] underline"
        >
          prenatal screening tests
        </Link>
        .
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD provides high-risk pregnancy care and pre-eclampsia management in
        Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function CervicalScreeningPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Cervical cancer is one of the few cancers that regular screening can prevent. Almost all
        cases result from persistent high-risk <strong>human papillomavirus (HPV)</strong>{" "}
        infection, and progression to cancer usually takes years. The <strong>smear test</strong>{" "}
        and <strong>HPV testing</strong> allow pre-cancerous change to be detected early within that
        window.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Smear test versus HPV test</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Smear (cytology):</strong> shows whether cervical cells have undergone abnormal
          change.
        </li>
        <li>
          <strong>HPV test:</strong> detects high-risk HPV types capable of causing cancer; HPV 16
          and 18 may be reported separately.
        </li>
        <li>
          Both can be run on the same sample. HPV testing is more sensitive than cytology alone, and
          a negative result offers long-term reassurance.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Who should be screened, and how often?
      </h2>
      <p>
        Türkiye&apos;s national programme offers{" "}
        <strong>HPV testing with cytology every five years to women aged 30–65</strong>, free of
        charge at KETEM centres and family practices. International guidelines describe screening
        from age 21–25. Women living with HIV, transplant recipients and other immunosuppressed
        women require more frequent screening. Screening may stop after hysterectomy with removal of
        the cervix if there is no history of abnormal results.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Preparing for the test</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Book it outside your period, ideally a few days after bleeding ends.</li>
        <li>
          Avoid intercourse, douching, vaginal medication and lubricants for 48 hours beforehand.
        </li>
        <li>A smear can be taken safely during pregnancy.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Understanding your results</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>HPV negative, normal cytology:</strong> repeat screening in five years.
        </li>
        <li>
          <strong>HPV positive (non-16/18), normal cytology:</strong> usually repeat testing in one
          year; most infections clear in that time.
        </li>
        <li>
          <strong>HPV 16 or 18 positive:</strong> colposcopy is advised even with normal cytology.
        </li>
        <li>
          <strong>ASCUS / LSIL:</strong> surveillance or colposcopy according to HPV status.
        </li>
        <li>
          <strong>ASC-H / HSIL / AGC:</strong> colposcopy and biopsy, with treatment where needed.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Colposcopy and treatment</h2>
      <p>
        At colposcopy the cervix is examined under magnification and small biopsies are taken from
        areas highlighted by acetic acid. If a high-grade pre-cancerous lesion (CIN 2–3) is found,
        the abnormal tissue is removed under local anaesthetic with a fine wire loop (
        <strong>LEEP / conisation</strong>). The uterus is preserved and follow-up continues with
        HPV testing.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Prevention</h2>
      <p>
        Regular screening, the{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "hpv-vaccine" }}
          className="text-[color:var(--primary-text)] underline"
        >
          HPV vaccine
        </Link>
        , condom use and stopping smoking substantially reduce the risk of cervical cancer.
        Vaccinated women should continue screening. Abnormal bleeding, particularly after
        intercourse, should be assessed without waiting for the next screening appointment.
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD provides cervical screening, colposcopy and follow-up in Adana,
        Türkiye.
      </p>
    </ArticleShell>
  );
}

function AbnormalBleedingPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        <strong>Abnormal uterine bleeding</strong> describes bleeding whose frequency, duration,
        regularity or volume falls outside normal limits, or that occurs between periods. Around one
        in three women of reproductive age experiences it at some point. Most causes are benign and
        treatable, but accurate diagnosis requires a systematic assessment.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">The limits of a normal period</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Cycle length: 24–38 days</li>
        <li>Duration of bleeding: fewer than eight days</li>
        <li>Cycle-to-cycle variation: generally less than seven to nine days</li>
        <li>Volume: not interfering with daily life or work</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Causes: the PALM-COEIN classification
      </h2>
      <p>
        The International Federation of Gynecology and Obstetrics (FIGO) divides causes into two
        groups:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Structural (PALM):</strong>{" "}
          <Link
            to="/en/blog/$slug"
            params={{ slug: "hysteroscopy" }}
            className="text-[color:var(--primary-text)] underline"
          >
            endometrial polyp
          </Link>
          , adenomyosis,{" "}
          <Link
            to="/en/blog/$slug"
            params={{ slug: "uterine-fibroids" }}
            className="text-[color:var(--primary-text)] underline"
          >
            fibroids
          </Link>
          , and malignancy or hyperplasia.
        </li>
        <li>
          <strong>Non-structural (COEIN):</strong> coagulopathy, ovulatory dysfunction (
          <Link
            to="/en/blog/$slug"
            params={{ slug: "pcos" }}
            className="text-[color:var(--primary-text)] underline"
          >
            PCOS
          </Link>
          , thyroid disease, raised prolactin, marked weight change, stress), endometrial causes,
          iatrogenic causes such as medication or an intrauterine device, and those not yet
          classified.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Common causes by age</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Adolescence:</strong> immaturity of the ovulatory axis, bleeding disorders.
        </li>
        <li>
          <strong>Reproductive years:</strong> pregnancy and its complications, polyps, fibroids,
          PCOS, hormonal medication.
        </li>
        <li>
          <strong>Menopausal transition:</strong> irregular ovulation, polyps, fibroids, endometrial
          hyperplasia.
        </li>
        <li>
          <strong>After the menopause:</strong> atrophy and polyps; endometrial cancer must always
          be excluded.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Investigations</h2>
      <p>
        Assessment begins with a pregnancy test. A detailed history and examination are followed by
        transvaginal ultrasound, a full blood count and ferritin, with thyroid, prolactin and
        clotting tests as required. Endometrial biopsy is advised for women over 45, those with risk
        factors such as obesity or PCOS, and those who do not respond to treatment. When a polyp or
        submucous fibroid is suspected, <strong>hysteroscopy</strong> provides both diagnosis and
        treatment.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment options</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Non-hormonal medication:</strong> tranexamic acid and non-steroidal
          anti-inflammatory drugs reduce blood loss.
        </li>
        <li>
          <strong>Hormonal treatment:</strong> the combined pill, progestogens and the
          levonorgestrel intrauterine system, which reduces bleeding by up to 90%.
        </li>
        <li>
          <strong>Hysteroscopic surgery:</strong> incision-free removal of polyps and submucous
          fibroids.
        </li>
        <li>
          <strong>Endometrial ablation or hysterectomy:</strong> for selected women who have
          completed their families and in whom other treatments have failed.
        </li>
        <li>Iron replacement is added where there is iron deficiency.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">When to seek urgent care</h2>
      <p>
        Bleeding that soaks a pad every hour or more often, dizziness or feeling faint, bleeding
        when pregnancy is possible and severe pelvic pain require urgent assessment. For bleeding
        during pregnancy, see our article on{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "bleeding-in-pregnancy" }}
          className="text-[color:var(--primary-text)] underline"
        >
          bleeding in pregnancy
        </Link>
        .
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD diagnoses and treats abnormal uterine bleeding, including
        hysteroscopic surgery, in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function EctopicPregnancyPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        An <strong>ectopic pregnancy</strong> is one that implants outside the uterine cavity, most
        often in a fallopian tube. It occurs in about 1–2% of pregnancies and is the leading cause
        of life-threatening bleeding in the first trimester. When diagnosed early it can usually be
        treated safely with medication or keyhole surgery, frequently preserving the tube.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Emergency symptoms</h2>
      <p>
        If your pregnancy test is positive or your period is late, attend the emergency department
        without delay if you experience any of the following:
      </p>
      <ul className="list-disc space-y-2 pl-6">
        <li>Severe or increasing pelvic pain, often on one side</li>
        <li>Dizziness, faintness or cold sweats</li>
        <li>Shoulder-tip pain (caused by internal bleeding)</li>
        <li>A sensation of rectal pressure</li>
        <li>Heavy vaginal bleeding</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Risk factors</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>A previous ectopic pregnancy</li>
        <li>Tubal surgery or sterilisation</li>
        <li>Previous pelvic infection, particularly chlamydia</li>
        <li>Endometriosis</li>
        <li>IVF and other assisted reproduction</li>
        <li>Smoking</li>
        <li>Conceiving with an intrauterine device in place</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Diagnosis</h2>
      <p>
        Diagnosis is based on <strong>serial serum beta-hCG</strong> measurements 48 hours apart and{" "}
        <strong>transvaginal ultrasound</strong>. In a normal pregnancy beta-hCG rises at a
        predictable rate, and above a certain level a gestational sac should be visible in the
        uterus. The absence of an intrauterine sac, an adnexal mass or free pelvic fluid suggests an
        ectopic pregnancy. Where the location cannot be determined, close follow-up continues. See
        also{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "when-to-take-pregnancy-test" }}
          className="text-[color:var(--primary-text)] underline"
        >
          pregnancy testing
        </Link>{" "}
        and{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "bleeding-in-pregnancy" }}
          className="text-[color:var(--primary-text)] underline"
        >
          bleeding in pregnancy
        </Link>
        .
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Treatment options</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Expectant management:</strong> for selected women without symptoms whose beta-hCG
          is low and falling spontaneously.
        </li>
        <li>
          <strong>Methotrexate:</strong> tube-preserving medical treatment for stable women with a
          small mass and no signs of bleeding; follow-up continues until beta-hCG is negative.
        </li>
        <li>
          <strong>Laparoscopic surgery:</strong> for internal bleeding, suspected rupture, high
          beta-hCG or failed medical treatment; the pregnancy tissue or the tube is removed.{" "}
          <Link
            to="/en/blog/$slug"
            params={{ slug: "gynaecological-laparoscopy" }}
            className="text-[color:var(--primary-text)] underline"
          >
            Laparoscopy
          </Link>{" "}
          allows rapid recovery.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">After treatment</h2>
      <p>
        Rh-negative women receive anti-D immunoglobulin. After methotrexate, waiting at least three
        months before trying to conceive is usually advised. In any future pregnancy an early
        ultrasound should confirm intrauterine implantation as soon as the test is positive. The
        emotional impact of pregnancy loss should not be underestimated, and support should be
        sought where needed.
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD diagnoses and treats ectopic pregnancy, including medical
        management and laparoscopic surgery, in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function MiscarriagePost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        Pregnancy loss affects about 10–15% of recognised pregnancies in the first 12 weeks; it is
        common, yet it often has a profound emotional impact. Most early miscarriages result from
        chance chromosomal abnormalities in the embryo and are not related to anything the mother
        did. For couples who have experienced two or more losses, investigation for an underlying
        cause is recommended.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms and when to seek help</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Vaginal bleeding or spotting</li>
        <li>Cramping lower abdominal or back pain, similar to or stronger than period pain</li>
        <li>Passage of tissue</li>
        <li>A sudden reduction in pregnancy symptoms</li>
        <li>
          Bleeding that soaks a pad every hour, fever, offensive discharge or faintness requires
          urgent care.
        </li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Causes</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Chance chromosomal abnormalities in the embryo (the commonest cause)</li>
        <li>Advanced maternal age</li>
        <li>Antiphospholipid syndrome</li>
        <li>Poorly controlled diabetes and thyroid disease</li>
        <li>
          Structural uterine problems: septum,{" "}
          <Link
            to="/en/blog/$slug"
            params={{ slug: "hysteroscopy" }}
            className="text-[color:var(--primary-text)] underline"
          >
            polyps
          </Link>
          , submucous{" "}
          <Link
            to="/en/blog/$slug"
            params={{ slug: "uterine-fibroids" }}
            className="text-[color:var(--primary-text)] underline"
          >
            fibroids
          </Link>
          , adhesions
        </li>
        <li>Smoking, heavy alcohol use and some medications</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">Diagnosis</h2>
      <p>
        Diagnosis is made with transvaginal ultrasound and, where needed, serial beta-hCG. In very
        early pregnancy a repeat scan after 7–14 days may be required for a definitive answer. For
        other causes of bleeding see{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "bleeding-in-pregnancy" }}
          className="text-[color:var(--primary-text)] underline"
        >
          bleeding in pregnancy
        </Link>
        , and for the distinction from an ectopic pregnancy see{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "ectopic-pregnancy" }}
          className="text-[color:var(--primary-text)] underline"
        >
          ectopic pregnancy
        </Link>
        .
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Management options</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Expectant management:</strong> allowing the tissue to pass naturally; usually
          complete within two weeks.
        </li>
        <li>
          <strong>Medical management:</strong> misoprostol, with mifepristone where appropriate,
          hastens the process.
        </li>
        <li>
          <strong>Vacuum aspiration:</strong> for heavy bleeding, infection or incomplete
          miscarriage; a brief and safe procedure.
        </li>
        <li>Rh-negative women receive anti-D immunoglobulin where indicated.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Investigating recurrent pregnancy loss
      </h2>
      <p>After two or more losses, recommended assessment includes:</p>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          Antiphospholipid antibodies (lupus anticoagulant, anticardiolipin, anti-beta2
          glycoprotein)
        </li>
        <li>Thyroid function and thyroid antibodies</li>
        <li>Assessment of uterine anatomy with 3D ultrasound, hysteroscopy or MRI</li>
        <li>
          Chromosome analysis of the couple and genetic testing of pregnancy tissue in selected
          cases
        </li>
        <li>Glucose, body mass index and lifestyle review</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Treatment and the next pregnancy
      </h2>
      <p>
        Antiphospholipid syndrome is treated with low-dose aspirin and heparin, a uterine septum or
        polyp with hysteroscopic surgery, and thyroid dysfunction with medication. Even when no
        cause is found, early pregnancy care and support give a high chance of a live birth next
        time. Pre-conception folic acid, stopping smoking and a healthy weight are advised; do not
        hesitate to seek support for the emotional impact of your loss.
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD manages pregnancy loss and investigates recurrent miscarriage in
        Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}

function PostpartumPost({ post }: { post: Post }) {
  return (
    <ArticleShell post={post}>
      <p>
        The <strong>postpartum period</strong> covers roughly the first six weeks after birth.
        During this time the uterus returns to its pre-pregnancy size, hormones rebalance and the
        body recovers from childbirth. In these weeks with a new baby, the mother&apos;s own health
        matters just as much as her baby&apos;s.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Postpartum bleeding (lochia)</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>
          <strong>Days 1–4:</strong> red and possibly heavier than a period; small clots are normal.
        </li>
        <li>
          <strong>Weeks 1–2:</strong> pinkish-brown and decreasing.
        </li>
        <li>
          <strong>Weeks 2–6:</strong> yellowish-white discharge, usually stopping within four to six
          weeks.
        </li>
        <li>Use pads rather than tampons during this period.</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Caring for stitches and a caesarean wound
      </h2>
      <p>
        After a vaginal birth, wash perineal stitches with warm water from front to back after using
        the toilet and pat dry; cold packs reduce swelling in the first days and healing takes two
        to three weeks. After a caesarean, getting up on the first day reduces the risk of
        thrombosis; the wound heals within one to two weeks and heavy lifting should be avoided for
        six weeks. For differences in recovery by mode of birth, see{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "vaginal-birth-or-c-section" }}
          className="text-[color:var(--primary-text)] underline"
        >
          vaginal birth or caesarean section
        </Link>
        .
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Symptoms that need urgent care</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Bleeding that soaks a pad every hour, or large clots</li>
        <li>Fever above 38°C or offensive discharge</li>
        <li>
          Severe headache or visual disturbance, which may indicate postpartum{" "}
          <Link
            to="/en/blog/$slug"
            params={{ slug: "preeclampsia" }}
            className="text-[color:var(--primary-text)] underline"
          >
            pre-eclampsia
          </Link>
        </li>
        <li>Chest pain, breathlessness or a painful swollen leg</li>
        <li>A red, hard, painful area in the breast with fever</li>
        <li>Thoughts of harming yourself or your baby</li>
      </ul>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Mental health: baby blues or depression?
      </h2>
      <p>
        Most mothers experience mood swings and tearfulness in the first days; these{" "}
        <strong>baby blues</strong> usually pass within two weeks. Low mood lasting longer than two
        weeks, loss of enjoyment, intense anxiety and disturbed sleep or appetite suggest{" "}
        <strong>postnatal depression</strong>. It affects about one in seven mothers, is not the
        mother&apos;s fault and responds well to treatment. Do not hesitate to ask for help.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">
        Breastfeeding, nutrition and activity
      </h2>
      <p>
        Breastfeeding offers many benefits for mother and baby; skin-to-skin contact and frequent
        feeds in the first days support milk supply. Breastfeeding mothers are advised to eat a
        balanced diet, drink plenty of fluids and continue supplements as recommended. Gentle
        walking can begin early, and pelvic floor exercises help prevent urinary incontinence.
        Strenuous exercise is usually resumed after the six-week check.
      </p>

      <h2 className="mt-10 font-display text-2xl font-semibold">Check-ups and contraception</h2>
      <p>
        Blood pressure, bleeding, wound healing and breastfeeding are reviewed in the first weeks,
        with a comprehensive examination at around six weeks. Women who had{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "gestational-diabetes" }}
          className="text-[color:var(--primary-text)] underline"
        >
          gestational diabetes
        </Link>{" "}
        should have a glucose tolerance test four to twelve weeks after birth. Breastfeeding alone
        is not reliable contraception, and an interval of at least 18 months between births is
        recommended. For breastfeeding-compatible options see{" "}
        <Link
          to="/en/blog/$slug"
          params={{ slug: "birth-control-methods" }}
          className="text-[color:var(--primary-text)] underline"
        >
          birth control methods
        </Link>
        .
      </p>

      <MedicalDisclaimer lang="en" />

      <p className="mt-8 text-sm text-[color:var(--muted-foreground)]">
        Op. Dr. Sadık Kükrer, MD provides postnatal follow-up and postpartum care in Adana, Türkiye.
      </p>
    </ArticleShell>
  );
}
